import * as THREE from 'three';
import { ballsFor, ballRadius, BALL_COLOR, DEFAULT_THEME, PHYSICS, tableFor, THEMES, type BallId, type ThemeId, type VariantId, type PhysicsConstants } from '../config';
import type { BallSnapshot, CueParams } from '../types';
import { shotLinkUrl, takeLinkedShot } from '../share/shotLink';
import { createCaromWorld, sampleAt, simulate, World, PHYS_DT, type SimulationResult } from '../physics';
import { applyShotResult, applyTimeout, createMatch, deriveSequence, evaluateShot, inFinalShotStage, resolveLag, shotGoalRule, VARIANTS, type CaromRuleConfig, type MatchConfig, type MatchState, type ShotEvaluation, type ShotResult } from '../rules';
import { findBestShot } from '../ai/ShotSearch';
import { Renderer } from '../render/Renderer';
import { buildLighting } from '../render/Lighting';
import { buildTable } from '../render/Table';
import { buildHall, makeBackdrop } from '../render/Hall';
import { ENVIRONMENTS, DEFAULT_ENV, loadPanorama, loadPanoramaVideo, loadModel, mergeStatic, initKTX2, type EnvId, type EnvDef } from '../render/Environments';
import { BallView } from '../render/Balls';
import { AimVisuals } from '../render/AimVisuals';
import { TutorialHint } from '../render/TutorialHint';
import { TablePointers } from '../render/TablePointers';
import { ChalkMarks } from '../render/ChalkMarks';
import { CushionPops } from '../render/CushionPops';
import { CueStick, STICK_LENGTH } from '../render/CueStick';
import { PlayerFigure } from '../render/PlayerFigure';
import { FLOOR_Z } from '../render/Hall';
import {
  CameraRig,
  FOV_MIN,
  FOV_MAX,
  ZOOM_OUT_MIN,
  ZOOM_OUT_MAX,
  ZOOM_IN_MIN,
  ZOOM_IN_MAX,
  ZOOM_RATE_MIN,
  ZOOM_RATE_MAX,
  type CameraMode,
} from '../camera/CameraRig';
import { PerfOverlay } from '../render/PerfOverlay';
import { PerfSampler, PERF_FLUSH_MS } from '../render/PerfSampler';
import Stats from 'stats.js';
import { SoundManager } from '../audio/SoundManager';
import { HUD, type UiMode } from '../ui/HUD';
import { Lobby } from '../ui/Lobby';
import { AvatarEditor } from '../ui/AvatarEditor';
import { parseAvatar, serializeAvatar, type AvatarConfig } from '../avatar';
import { MainMenu } from '../ui/MainMenu';
import { ChatPanel } from '../ui/ChatPanel';
import { EmoteWheel } from '../ui/EmoteWheel';
import { EMOTES_BY_ID } from '../emotes/catalog';
import { PresencePanel } from '../ui/PresencePanel';
import { ShotReviewPanel } from '../ui/ShotReviewPanel';
import { HistoryPanel } from '../ui/HistoryPanel';
import { ItemStorePanel } from '../ui/ItemStorePanel';
import { FeedbackPanel } from '../ui/FeedbackPanel';
import { AuthModal } from '../ui/AuthModal';
import { DailyPanel, type DailyState } from '../ui/DailyPanel';
import { TutorialPanel } from '../ui/TutorialPanel';
import {
  COURSES,
  MISSIONS,
  actionsSatisfied,
  resolveHint,
  resolveLayout,
  shotSatisfies,
  type CourseId,
  type Mission,
  type ShotContext,
  type TutorialAction,
} from '../tutorial/missions';
import { applyTutorialContent, missionsFor, missionsForCourse } from '../tutorial/runtime';
import { clearedCount, loadProgress, nextMission, nextMissionAfter, saveProgress, tutorialStatus } from '../tutorial/progress';
import { RewardToast } from '../ui/RewardToast';
import { AdminPanel } from '../ui/AdminPanel';
import { MusicPanel } from '../ui/MusicPanel';
import { applyEnvClass, isMac, preventEdgeSwipeNavigation } from './env';
import { VoicePanel } from '../ui/VoicePanel';
import { TuningPanel, type TuneParam } from '../ui/TuningPanel';
import { t, onLang, getLang } from '../ui/i18n';
import { tierLabel } from '../ui/tier';
import { randomDisplayName } from '../ui/names';
import { NetClient, defaultServerUrl } from '../net/NetClient';
import { VoiceChat } from '../net/VoiceChat';
import type { MusicAction, RoomConfig, ServerMessage, SerializedGame, Identity, GameFormat, LobbyState, BreakStyle, PresenceStatus, FinishRule, PlayerStats, ShotRecord, GameRecord, GameSummary, MyProfile, HeadToHead, SharedShot, CoinEvent } from '../net/protocol';
import { FORMAT_INFO, defaultFinish } from '../net/protocol';
import type { AuthProvider } from '../net/protocol';
import { signInWith } from '../net/socialLogin';

type Phase = 'aiming' | 'simulating' | 'finished' | 'review' | 'breaking' | 'watchbreak' | 'assessed' | 'strike';

// Custom table models (designed in Blender, exported game-ready: centred at the
// origin, cloth surface at z=0, z-up). When a variant has one it replaces the
// procedural buildTable() *visual* — physics still uses the variant's TableSpec,
// so the model must keep the same play footprint and cloth height. Drop a new
// export at public/models/<file>.glb to swap a table's look.
const TABLE_MODELS: Partial<Record<VariantId, string>> = {
  'straight-rail': import.meta.env.BASE_URL + 'models/table.glb',
};

// Lighting for 3D-model hall scenes. The room is lit mainly by a warm point light
// at each modelled ceiling lamp; a gentle ambient/IBL fill keeps shadows from going
// pure black without washing the room out. All easily dialled if it reads too
// bright/dim. (The procedural hall + 360° backdrops keep their own dim table rig.)
// Keep the flat fills LOW so the ceiling lamps (HALL_LAMP) actually do the
// lighting — a high envIntensity/hemi washes the whole room out uniformly and the
// lamps stop reading as sources. Just enough fill here to keep shadows from black.
// Lean on cheap ambient fill (IBL + hemisphere — nearly free) and only a FEW point
// lights. Each point light is per-pixel GPU work every frame, so 35 of them tanks
// FPS; we keep only the handful nearest the table (maxLights) and let the ambient
// do the rest for an even, dim "room" look.
// "Dark bar": ambient crushed near zero so the room falls into shadow, low
// exposure, and a few WARM lamps making tight pools (decay 2 = quick falloff).
// The table keeps its own spotlights, so it glows in the dark room.
const HALL_FILL = { hemi: 0.05, dir: 0.0, envIntensity: 0.05, exposure: 0.35 };
// maxLights panels nearest the table cast real light pools; the rest only glow.
// unlitGlow dims those glow-only panels so they read as distant/off fixtures
// instead of "lit but casting nothing" (the fake look).
const HALL_LAMP = { color: 0xffc888, intensity: 45, distance: 0, decay: 2, maxLights: 3, unlitGlow: 0.25 };
// How far from the table a surround model still needs `receiveShadow` (metres).
// The one shadow-casting spot (render/Lighting.ts) hangs 1.7 m up with a 45° cone
// and a 4 m throw, so its shadow reaches ~3.2 m out; beyond that a mesh would pay
// for shadow lookups that can only ever return "lit". See applyShadowPolicy().
const MODEL_SHADOW_RADIUS = 3.2;

// Ctrl-stroke: pull the mouse back to wind up, then swing it FORWARD across the
// start line to strike. Power comes from the SPEED of that forward swing (like a
// real cue stroke) — a fast flick hits hard, a slow push barely taps. The pull-back
// only sets how far the cue is drawn (visual) and arms the shot.
// Pull-back range (px of accumulated, pointer-locked mouse travel) the cue visual
// maps to a full draw.
const STROKE_DRAW_RANGE = 700;
// Average forward-swing speed (px per ms) mapped to FULL power — the fastest swing
// you'd make; half this speed is the middle of the power range. Mouse-DPI
// dependent, so it's a live tunable.
const STROKE_SPEED_FULL = 8;
// S-curve sharpness. Power is an odd-symmetric curve about the mid speed: steep at
// BOTH ends so the weakest (0) and strongest (max) shots need a deliberately
// extreme swing, and shallow in the middle so ordinary swings spread across the
// mid range. 1 = linear; higher = harder extremes / more forgiving middle.
const STROKE_CURVE = 2;
const STROKE_MIN_DRAW = 0.05; // ignore a negligible pull-back before releasing

// Skill assessment: a fixed number of shots; the raw score is caroms made × 10
// (so 10/20 scores = 100), snapped DOWN to the nearest standard handicap rung.
// A handicap high enough that the solo match never "clears".
const ASSESS_TURNS = 20;
const ASSESS_HANDICAP = 9990;
// 당구 교실: how long a cleared mission's "잘하셨어요!" stays up before the course
// moves itself on. Long enough to read the praise and see what the shot did,
// short enough that it never feels like waiting; 'Next step' skips the rest.
const TUTORIAL_ADVANCE_MS = 1800;

// How much chalk one shot scrubs off the tip: a base amount plus more for english
// (the tip brushes across the ball instead of driving through it). Was 0.05/0.20,
// which meant a full-english player was back at the chalk every ~4 shots and even
// centre-ball play every 20 — chalking read as a chore rather than a bit of care.
// At these numbers that's ~9 shots on heavy english, ~14 on moderate, ~40 on
// centre ball. The MISCUE odds are unchanged: the tip just stays good for longer,
// so a worn tip still bites and skids exactly as it did before.
const CHALK_WEAR_BASE = 0.025;
const CHALK_WEAR_SPIN = 0.09;
/** Max cue elevation the player can set (rad). Just past vertical so a steep massé
 *  can raise the stick all the way over 90°; physics caps its effect at 90°. */
const MAX_ELEVATION = 1.75; // ~100°
/** The only levels a player can be assessed at (standard Korean handicap rungs). */
const ASSESS_LEVELS = [30, 50, 80, 100, 120, 150, 200, 250, 300, 400, 500];

/** Floor a raw score (caroms × 10) to the highest standard level at or below it. */
function assessLevel(rawPoints: number): number {
  let level = ASSESS_LEVELS[0];
  for (const l of ASSESS_LEVELS) if (rawPoints >= l) level = l;
  return level; // below the lowest rung -> the entry level (30)
}

/**
 * Is this key event already going to a control that wants Enter itself (a text
 * field, or a focused button/link Enter is meant to activate)? Those must keep
 * it — the chat overlay only claims Enter when nothing else is listening.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A', 'OPTION'].includes(el.tagName);
}

/** Map an assessed level to a rank i18n key. */
function rankKeyForLevel(level: number): string {
  if (level >= 300) return 'rank.master';
  if (level >= 200) return 'rank.expert';
  if (level >= 120) return 'rank.advanced';
  if (level >= 80) return 'rank.intermediate';
  return 'rank.beginner'; // 30, 50
}

// Cue speed (m/s) at zero power; `tune.maxShotSpeed` is the speed at full power.
const MIN_SHOT_SPEED = 0.6;

// Framed-camera zoom used while spectating: pulled back from the shooter's POV so
// the cue ball and the object balls ahead both stay in shot.
const SPECTATE_ZOOM = 2;

// Lowest the tip contact may sit above the cloth (m) — draw english can't push
// the tip into the bed, so the vertical spin offset is clamped to respect it.
const TIP_CLOTH_GAP = 0.006;

// Online PvP shot clock: each turn gets this long to fire (or the timer forces a
// timeout, see applyTimeout); the last stretch ticks audibly/visually.
const TURN_TIME_MS = 45_000;
const TURN_TICK_START_MS = 10_000;

interface SoundCue {
  t: number;
  kind: 'ball' | 'cushion';
  intensity: number;
}

/** Top-level orchestrator: scene, camera, physics, rules, AI, HUD, input, loop. */
export class App {
  private readonly renderer: Renderer;
  private readonly scene = new THREE.Scene();
  private readonly cameraRig: CameraRig;
  private readonly aimVisuals = new AimVisuals();
  private readonly tutorialHint = new TutorialHint(); // animated suggested-shot arrow during a lesson
  private readonly tablePointers = new TablePointers(); // other players' 3D "ping" arrows
  private readonly chalkMarks = new ChalkMarks(); // F+click: chalk dabs on the cloth, cleared on the next shot
  private readonly cushionPops = new CushionPops(); // 마지막 3쿠션: running rail count
  /** Cushion-count pops queued for the shot being played back, ascending by time. */
  private popCues: { t: number; n: number; x: number; y: number; win: boolean }[] = [];
  private popIdx = 0;
  /** True once jackpot() has fired for the shot in progress, so finalizeShot's
   *  bank-score chime doesn't ALSO play over the (louder) 마지막 3쿠션 win fanfare. */
  private jackpotFiredThisShot = false;
  private pointing = false; // holding the point key → broadcasting my pointer
  // Practice-mode ball placement: offline solo/vs-AI, or an online room's
  // waiting lobby (see canDragBalls). Holding F over a ball, or clicking
  // straight onto one (desktop), or touch-dragging it (mobile) picks it up;
  // releasing drops it where it sits.
  private draggingBallId: BallId | null = null;
  private touchDragPointerId: number | null = null;
  private announcedPlaces = new Set<number>(); // teams whose finishing place we've announced (3+ player)
  private lastPointerSentMs = 0;
  private readonly ptrRaycaster = new THREE.Raycaster();
  private readonly ptrPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0); // table cloth (z=0)
  private readonly sound = new SoundManager();
  private readonly hud: HUD;
  private readonly canvas: HTMLCanvasElement;
  /** Static stand-in shown instead of the live 3D scene while parked at the
   *  main menu/lobby (menuOpen) — see applyMenuBackdrop(). Saves the ongoing
   *  render/shadow cost of the idle heartbeat when no game is in progress. */
  private readonly menuBgStatic: HTMLImageElement | null;
  private menuBgShown = false;

  // per-variant scene objects (rebuilt when the variant changes)
  private ballView!: BallView;
  private cueStick!: CueStick;
  private figures: PlayerFigure[] = []; // [seat 0, seat 1] humanoids around the table

  // theme-dependent scenery (rebuilt when the theme changes)
  private theme: ThemeId = DEFAULT_THEME;
  private env: EnvId = DEFAULT_ENV;
  private readonly panoramaCache = new Map<EnvId, THREE.Texture>();
  private readonly videoCache = new Map<EnvId, { texture: THREE.VideoTexture; video: HTMLVideoElement }>();
  private activeVideo: HTMLVideoElement | null = null; // the currently-playing 360 video, if any
  private readonly modelCache = new Map<EnvId, THREE.Object3D>(); // loaded room models
  private activeModel: THREE.Object3D | null = null; // the room model currently in the scene, if any
  private hallFill: THREE.Group | null = null; // extra fill light enabled only for 3D-model scenes
  private envToken = 0; // guards against a slow panorama load overwriting a newer pick
  private neutralEnv: THREE.Texture | null = null; // indoor IBL used by the procedural hall
  private tableGroup: THREE.Object3D | null = null;
  private hallGroup: THREE.Group | null = null;
  private readonly tableModelCache = new Map<VariantId, THREE.Object3D>(); // loaded custom table meshes
  private tableModelToken = 0; // guards a slow table-model load against a newer variant pick

  // game state
  private variant: VariantId = 'straight-rail';
  private base!: World;
  private match!: MatchState;
  private uiMode: UiMode = 'solo';
  private phase: Phase = 'aiming';
  /** Korean handicap (countdown) starting points, per player. */
  private handicaps: [number, number] = [100, 100];

  // ---- skill assessment (calibration) ----
  /** A fixed-length solo session: score as many caroms as you can in N shots;
   *  level = caroms × 10, mapped to a rank. */
  private assessing = false;
  private assessTurns = 0; // shots taken so far
  private assessScores = 0; // caroms made so far
  private assessResult = ''; // HTML banner shown when the session ends
  /** Live-tunable control sensitivities (edited from the Tuning panel). */
  private readonly tune = { aimSensitivity: 0.0045, tiltSensitivity: 0.0012, strokeDrawRange: STROKE_DRAW_RANGE, strokeSpeedFull: STROKE_SPEED_FULL, strokeCurve: STROKE_CURVE, maxShotSpeed: 7.0, playbackSpeed: 0.55, shotJitter: 0.06 };

  /** Tiny FPS meter in the corner (stats.js). */
  private readonly stats = new Stats();
  /** Render profiler (draw calls / shaders / lights). Only built for `?perf=1`. */
  private readonly perf = PerfOverlay.requested() ? new PerfOverlay() : null;
  /** Always-on render telemetry, flushed to the server every PERF_FLUSH_MS. */
  private readonly perfSampler = new PerfSampler();
  private lastPerfFlushMs = 0;

  // start menu
  private mainMenu!: MainMenu;
  /** The start menu is open (a modal): the table behind it is frozen. */
  private menuOpen = false;
  // two-choice prompt overlay (e.g. opponent-left win)
  private promptOverlay!: HTMLDivElement;
  private promptMsg!: HTMLDivElement;
  private promptA!: HTMLButtonElement;
  private promptB!: HTMLButtonElement;

  // online play
  private lobby!: Lobby;
  private chat!: ChatPanel;
  private emoteWheel!: EmoteWheel;
  private music!: MusicPanel;
  private voicePanel!: VoicePanel;
  private voice!: VoiceChat;
  private net: NetClient | null = null;
  /** Our room seat (0..players-1). The 2-side engine team is `seat % teams`. */
  private seat = 0;
  private opponentPresent = false;
  private spectator = false;
  private roomCode = '';
  private intentionalClose = false;
  private reconnectAttempts = 0;
  /** Exhibition spectating: a finished match's automatic rematch is held here
   *  (instead of applied immediately) until the viewer chooses to keep watching. */
  private pendingGameStart: Parameters<App['handleGameStart']>[0] | null = null;
  private pendingState: SerializedGame | null = null;
  private spectatorReadyForNext = false;
  /** Server confirmed the exhibition room is gone while its finish dialog was still
   *  up (see announceSpecMatchEnd) — "watch next" has nothing to wait for, so treat
   *  it the same as "exit spectate" instead of leaving the viewer stuck on the dialog. */
  private specRoomAlreadyClosed = false;
  /** Display name for the room list / ready panel / chat (empty = anonymous → "Guest"). */
  private nickname = '';
  /** Profile avatar (upper-body portrait); persisted as a compact string. */
  private avatar!: AvatarConfig;
  private avatarEditor!: AvatarEditor;
  /** Logged-in nickname + session token (null = playing anonymously/guest). */
  private account: string | null = null; // registered nickname (null for guests)
  private accountId: string | null = null; // stable account id (guest or registered) — the ranking key
  private guest = true; // anonymous session until they sign up / log in
  private token: string | null = null;
  /** Guards the room-list request so a silent/stale server surfaces a clear hint. */
  private listTimer: ReturnType<typeof setTimeout> | null = null;
  /** In a waiting room (pre-game): the shared table is open for synced practice. */
  private waiting = false;
  /** A started online match is in progress (vs. browsing/waiting). */
  private gameActive = false;
  /** Room format + per-seat names for the current room. */
  private format: GameFormat = '1v1';
  // --- global presence (a dedicated always-on socket, separate from the room one) ---
  private presence: NetClient | null = null;
  private presencePanel!: PresencePanel;
  private adminPanel!: AdminPanel;
  private reviewPanel!: ShotReviewPanel;
  private historyPanel!: HistoryPanel;
  private storePanel!: ItemStorePanel;
  private feedbackPanel!: FeedbackPanel;
  private authModal!: AuthModal;
  /** Which UI the last login/signup attempt came from, so a server authError
   *  (or a success) routes back to the form the player is actually looking at. */
  private authSource: 'lobby' | 'modal' = 'lobby';
  private dailyPanel!: DailyPanel;
  private tutorialPanel!: TutorialPanel;
  private rewardToast!: RewardToast;
  private heldCoinUpdates: Array<() => void> = [];
  private features = new Set<string>(); // server feature-gate ids currently enabled
  private coins = 0; // 코인 balance
  private owned = new Set<string>(); // store item ids this account has purchased
  private myCueColor: string | null = null; // equipped custom cue-ball colour (hex, no '#'), null = default
  /** Full ranking card (tier + ladder progress + 하이런 + coins) from the server. */
  private myProfile: MyProfile | null = null;
  private myTier: number | null = null; // this account's ladder tier (점); online games default to it (ranked)
  /** Head-to-head record vs each seat, keyed by account id (shown on the cards). */
  private h2h = new Map<string, HeadToHead>();
  /** Today's 오늘의 배치, as last reported by the server. */
  private daily: DailyState | null = null;
  /** A daily-challenge attempt in progress: strokes/caroms/banks so far, and
   *  when it started (Time Attack's countdown/elapsed-time rules need it). */
  private dailyRun: { strokes: number; caroms: number; banks: number; startTs: number } | null = null;
  /** The attempt just submitted, held until the server's verdict comes back. */
  private lastDailyRun: { strokes: number; caroms: number; banks: number; elapsedMs: number | null } | null = null;
  /** The player asked for the daily panel; the next snapshot should open it. */
  private dailyWanted = false;
  private dailyTimer: ReturnType<typeof setTimeout> | null = null;
  /** 'timedScore' Time Attack: ends the run when the countdown hits zero. */
  private dailyCountdown: ReturnType<typeof setTimeout> | null = null;
  /** Ticks the HUD while a Time Attack run is live, so its live timer chip updates. */
  private dailyTick: ReturnType<typeof setInterval> | null = null;

  // ---- 당구 교실 (tutorial) ----
  /** Mission ids already completed, remembered per browser. */
  private readonly tutorialDone: Set<string> = loadProgress();
  /**
   * Non-null for as long as a tutorial session is running. `mission` is null on
   * the closing "course complete" card, which is still part of the session.
   * `actions` collects the controls used since the current mission began.
   */
  private tutorial: { mission: Mission | null; state: 'doing' | 'passed' | 'retry'; actions: Set<TutorialAction> } | null = null;
  /** Which of the three courses the current session is playing. */
  private tutorialCourse: CourseId | null = null;
  /**
   * The curriculum for the CURRENT course, on this device — touch drops the
   * mouse-only lessons. Reassigned in `startTutorial`; defaults to the full
   * list only until a course is actually chosen.
   */
  private tutorialMissions: readonly Mission[] = MISSIONS;
  /** Pending auto-advance to the next lesson (see completeMission). */
  private tutorialNextTimer: ReturnType<typeof setTimeout> | null = null;
  private presenceStatus: string | null = null; // last "status|room" key sent (dedupe)
  private pendingWatch: string | null = null; // room to spectate once online connects
  private pendingRematch: string | null = null; // player to invite once online connects
  private presenceTimer: ReturnType<typeof setTimeout> | null = null;
  private playerNames: string[] = [];
  private playerLevels: number[] = []; // per-seat handicap, from the room
  private playerIds: string[] = []; // per-seat account id, from the room (for result reporting)
  private playerCueColors: string[] = []; // per-seat equipped cue-ball colour (hex, '' = default)
  private seatStats: (PlayerStats | null)[] = []; // per-seat all-time stats, shown under each card
  private breakStyle: BreakStyle = 'preset'; // room opening style (online)
  /** Variant the waiting-room practice table is currently built for (avoids rebuilds). */
  private waitingVariant: VariantId | null = null;
  private lastCue: CueParams | null = null;
  /** House "마지막 3쿠션" finish rule for online 4-ball rooms (synced via RoomConfig). */
  private finish: FinishRule = { caroms: 1, banks: 0 };
  /** Solo practice: the table + score + aim captured just before the last shot, so
   *  the 'r' key can rewind and let you play that exact shot again. */
  private preShot: {
    match: MatchState;
    cue: CueParams;
    balls: Record<string, { pos: { x: number; y: number; z: number }; orientation: { x: number; y: number; z: number; w: number } }>;
  } | null = null;
  private pendingRemote: { finalState: Record<BallId, BallSnapshot>; result: ShotResult; match?: MatchState } | null = null;
  /** Remote shots (or shot-clock timeouts) that arrived while one was still
   *  animating — played/applied in order so a fast opponent/bot run never
   *  interrupts (and drops) the turn in progress. */
  private remoteQueue: (
    | ({ kind: 'shot' } & { cue: CueParams; finalState: Record<BallId, BallSnapshot>; result: ShotResult; match?: MatchState; from?: number })
    | { kind: 'timeout'; from?: number }
  )[] = [];
  /** A queue drain is waiting for the next frame (see frame()). Incoming shots must
   *  queue behind it rather than playing immediately, or they'd overtake it. */
  private drainPending = false;
  /** Last-seen value of the server's per-room seq counter (shot/practice/sync/newgame).
   *  0 means "no baseline yet" — the room we're in has sent us nothing seq'd, or we
   *  just left/joined a room. See checkGameSeq/adoptGameSeq. */
  private lastGameSeq = 0;
  /** Epoch ms when the current turn's shot clock expires; 0 = no timer running
   *  (not online PvP, not my turn, or not aiming). See updateTurnTimer. */
  private turnDeadlineMs = 0;
  /** Last whole second we played a tick sound for, so each second only ticks once. */
  private turnTickSecond = -1;
  /** The opponent's live cue pose (online), shown so their stick moves in real time. */
  private remoteAim: { azimuth: number; elevation: number; a: number; b: number; pullback: number } | null = null;
  // an opponent's (human or bot) cue stroke being telegraphed before the balls roll
  private oppStrike: {
    shot: SimulationResult;
    cuePos: THREE.Vector3;
    az: number;
    el: number;
    spin: { a: number; b: number };
    maxPull: number;
    t0: number;
    backDur: number;
    fwdDur: number;
    /** True for a review replay: hand off to a non-scoring playback, not finalize. */
    replay?: boolean;
  } | null = null;
  private lastAimSentMs = 0;
  /** The breaker's live swing (online): streamed ball positions + cue angle. */
  private remoteBreak: { balls: Record<BallId, { x: number; y: number; z: number }>; lever: number } | null = null;
  private lastBreakSentMs = 0;

  private get online(): boolean {
    return this.uiMode === 'online';
  }

  /** The player's own framed-camera pull-back — persisted, and restored whenever
   *  the view comes back to their shot (see adjustZoom). */
  private aimZoom = 0;

  /** Watching the opponent (or spectating): free-orbit viewing mode, no aiming. */
  private get viewing(): boolean {
    return this.online && this.opponentPresent && !this.myTurn;
  }

  /** Pick the camera for the current turn: spectators ride the shooter's POV,
   *  a player waiting on the opponent free-orbits, otherwise your own view. */
  private applyTurnCamera(): void {
    if (this.spectator) {
      // watching a match: follow the active player's POV (aim framing behind their
      // cue ball), pulled back so the whole shot reads. The render loop keeps the
      // aim target on the current shooter's cue ball each frame.
      this.setAimActive(false);
      this.cameraRig.resetView();
      this.cameraRig.setZoom(SPECTATE_ZOOM);
      this.cameraRig.setMode('aim');
    } else if (this.viewing) {
      // opponent's turn: ride their POV (framed behind their cue ball, pulled back),
      // like spectating — the render loop keeps the target on the active shooter's
      // ball every frame, so we stay on their ball even between their hits.
      this.setAimActive(false);
      this.cameraRig.resetView();
      this.cameraRig.setZoom(SPECTATE_ZOOM);
      this.cameraRig.setMode('aim');
    } else {
      // back on my own shot: restore MY pull-back. Without this, one turn spent
      // watching the opponent leaves the camera parked at SPECTATE_ZOOM for the
      // rest of the game. (Zero until the constructor has read the saved value —
      // don't clamp the camera to its closest zoom before then.)
      if (this.aimZoom > 0) this.cameraRig.setZoom(this.aimZoom);
      this.cameraRig.setMode(this.cameraMode === 'action' ? 'aim' : this.cameraMode);
    }
    this.refreshHud();
  }

  // aim state
  private azimuth = Math.PI / 2;
  // touch-only: held while the HUD's aim-boost button is down (see HUD's
  // onAimBoost) — doubles the touch aim-drag gain for sweeping across the
  // table in fewer strokes; released as soon as the finger lifts.
  private aimBoost = false;
  private power01 = 0.55;
  private spin = { a: 0, b: 0 };
  private elevation = 0;
  private guideOn = true;
  private chalkLevel = 1; // 1 = freshly chalked tip, drops with use; low = miscue risk
  private cameraMode: CameraMode = 'aim';
  private message = '';

  // touch device (phone/tablet): auto-detected once at startup; swaps the
  // pointer-lock desktop control scheme for finger gestures (see bindTouchInput).
  private readonly touch = applyEnvClass();
  // On macOS, ⌘ works as the stroke modifier too — Mac muscle memory reaches for
  // ⌘ as "the" modifier key, and Ctrl there is also the trackpad right-click
  // chord, an awkward key to hold through an entire stroke.
  private readonly macMod = isMac();

  // input: aim mode (toggled by a left-click) + the Ctrl "stroke"
  private aimActive = false; // left-click on → mouse moves the cue; right-click/key off
  private enteringAim = false; // the entering left-press shouldn't also drag spin
  private pointerY = 0;
  private stroking = false;
  private strokeDraw = 0; // current cue pull-back (0..1) shown during a stroke
  private strokeAnchorY = 0; // mouse Y when Ctrl was pressed (the release line)
  private maxDraw = 0; // peak pull-back reached this stroke (arms the shot)
  private peakDrawPx = 0; // deepest wind-up point (px past the start line)
  private tPeak = 0; // time (ms) of that deepest point — start of the forward swing

  // on-demand rendering: only redraw when something actually changes (balls/cue
  // moving, camera easing, aim input, scene rebuilt). When idle (menu, waiting for
  // the opponent/AI, between shots) we fall back to a slow heartbeat instead of
  // pinning the GPU at the monitor's full refresh rate. See frame().
  private needsRender = true;
  /** Worker-timer fallback that drives frame() while the tab is hidden (rAF stops). */
  private hiddenTicker: Worker | null = null;
  private lastRenderMs = 0;
  /** Last frame the shadow map was rebuilt (throttled while aiming). */
  private lastShadowMs = 0;

  // shot recorder: the ordered shot log for the game/session in progress, plus its
  // header (physics + mode), so every shot can be reviewed and replayed later. Saved
  // per account at game end / session teardown. See recordShot / persistGameLog.
  private gameLog: ShotRecord[] = [];
  private gameLogMeta: {
    id: string;
    variant: VariantId;
    mode: GameRecord['mode'];
    format: GameRecord['format'];
    physics: PhysicsConstants;
    ts: number;
  } | null = null;
  /** The most recent recorded game (kept after 'finished' so the end-game panel can list it). */
  private lastGameRecord: GameRecord | null = null;
  /** Post-game shared "try this layout" practice: any player may shoot, nothing scores. */
  private shotPractice = false;
  /** While practising a recorded shot, the layout to reload on "re-practice". */
  private practiceCtx: {
    positions: Record<BallId, { x: number; y: number }>;
    cue?: CueParams;
    variant: VariantId;
    physics: PhysicsConstants;
  } | null = null;

  // shot playback
  private currentShot: SimulationResult | null = null;
  private lastShot: SimulationResult | null = null;
  private replaying = false;
  /** One-shot callback fired when the current replay finishes (see endReplay). */
  private afterReplay: (() => void) | null = null;
  private reviewFrac = 1;
  private shotStartMs = 0;
  private soundCues: SoundCue[] = [];
  private soundIdx = 0;

  // input: previous mouse position for spin deltas
  private pointerX = 0;
  private pointerInit = false;

  // ai
  private aiToken = 0;
  private aiThinking = false;

  private lastNow = 0;
  private readonly scratch = new THREE.Vector3();

  // ---- opening "lever" break (4-ball, offline): the cue butt is pinned at a
  // corner and the mouse swings the shaft around it, sweeping a jittered line of
  // balls out into the table. See startCornerBreak().
  private breakArmed = false; // a key/click has enabled swing mode (no button hold needed)
  private breakLocked = false; // pointer lock is currently held for the swing
  private readonly breakPivot = new THREE.Vector3(); // pinned butt position (a corner)
  private leverAngle = 0; // current shaft heading from the pivot (rad)
  private leverPrevAngle = 0; // heading last frame, for angular velocity
  private leverHasAngle = false; // the mouse has driven the heading at least once
  private leverOmega = 0; // signed angular velocity of the swing (rad/s)
  private breakDisturbed = false; // at least one ball has been struck
  private breakStrokeSpent = false; // swing has passed the balls — cue can no longer hit them

  constructor(canvas: HTMLCanvasElement, hudRoot: HTMLElement) {
    this.canvas = canvas;
    this.menuBgStatic = document.getElementById('menu-bg-static') as HTMLImageElement | null;
    this.renderer = new Renderer(canvas);
    initKTX2(this.renderer.three); // lets loadModel() decode the hall's KTX2/Basis textures
    this.neutralEnv = this.renderer.makeEnvironment(); // indoor IBL for the procedural hall
    this.scene.environment = this.neutralEnv;
    // Our world is z-up, but three.js samples equirectangular 360 backgrounds as
    // y-up — so without this the panorama lands on its side (horizon vertical).
    // Rotating the background/environment 90° about X stands the 360 photo upright.
    this.scene.backgroundRotation.set(Math.PI / 2, 0, 0);
    this.scene.environmentRotation.set(Math.PI / 2, 0, 0);
    this.scene.add(buildLighting());
    this.scene.add(this.aimVisuals.group);
    this.scene.add(this.tutorialHint.group);
    this.scene.add(this.tablePointers.group);
    this.scene.add(this.chalkMarks.group);
    this.scene.add(this.cushionPops.group);
    this.rebuildScenery();

    this.cameraRig = new CameraRig(canvas);
    this.aimVisuals.setVisible(this.guideOn);

    this.hud = new HUD(hudRoot, {
      onSpinChange: (a, b) => {
        this.spin = { a, b };
        this.noteTutorialAction('spin:drag');
      },
      onAimDrag: (dx, dy) => {
        if (this.phase !== 'aiming' || !this.myTurn || this.cameraMode === 'orbit') return;
        this.cameraRig.resetView(); // snap back to the shot framing, same as dragging the table
        this.azimuth -= dx * this.tune.aimSensitivity * 0.8 * (this.aimBoost ? 2 : 1);
        // vertical drag on the same pad also reaches cue elevation — a second
        // way to get to the tilt dial's control, not a replacement for it.
        this.adjustTilt(dy);
        this.noteTutorialAction('touch:aim');
        this.needsRender = true;
      },
      onCamera: (m) => this.setCamera(m),
      onVariant: (id) => {
        // Changing the game by hand is leaving the lesson: its layout and its
        // variant are the mission, so the session ends rather than carrying on
        // against a table it no longer describes.
        this.endTutorialSession();
        // online: variant is fixed by the room creator; just remember it for the next room
        if (this.online) this.variant = id;
        else this.buildForVariant(id);
      },
      onHandicap: (value) => {
        this.endTutorialSession(); // same as onVariant: this restarts a real game
        this.handicaps = [value, value];
        localStorage.setItem('carom.level', String(value)); // remember for next launch
        if (!this.online) this.buildForVariant(this.variant); // restart at the new target
      },
      onTheme: (id) => {
        this.theme = id;
        this.rebuildScenery();
        this.broadcastConfig(); // keep the opponent's table colour in step
      },
      onEnv: (id) => this.applyEnv(id), // background: admin-set room config when online
      onMode: (mode) => this.setMode(mode),
      onMainMenu: () => this.requestExitToMenu(),
      onReplay: () => this.replayLast(),
      onReviewShots: () => {
        this.hud.dismissBanner(); // close the end-game banner when the review opens
        this.openEndReview();
      },
      onRePractice: () => this.rePractice(),
      onBannerClick: () => {
        if (this.phase === 'breaking' && !this.breakArmed) this.armBreak();
      },
      onScrub: (frac) => this.onScrub(frac),
      onAssess: () => this.startAssessment(),
      onNewGame: () => {
        this.endTutorialSession(); // a fresh game is not a lesson
        this.assessing = false; // leaving any assessment session
        this.assessResult = '';
        // online: rematch in the same room (notify the opponent); otherwise restart locally
        if (this.online && this.opponentPresent) {
          this.net?.send({ type: 'newgame', variant: this.variant });
          this.startOnlineMatch(this.variant);
        } else {
          localStorage.setItem('carom.finished', '0'); // restarted single play → in progress again
          this.reviewPanel.dismiss(); // a fresh game → close any lingering review
          this.buildForVariant(this.variant);
        }
      },
      onToggleGuide: () => {
        this.toggleGuide();
      },
      onEditAvatar: () => this.avatarEditor.open(this.avatar),
      // touch "pull back & release" stroke pad (mobile): draw loads power, the
      // release fires the shot at the current aim/spin — the finger analogue of
      // the desktop Ctrl stroke.
      onStrokeDraw: (power01) => {
        if (this.phase !== 'aiming' || !this.myTurn) return;
        if (this.cameraMode !== 'aim' && this.cameraMode !== 'top') this.setCamera('aim');
        this.power01 = power01;
        this.strokeDraw = power01; // telegraph the cue pull-back on the stick
        this.hud.setPower(power01);
        this.needsRender = true;
      },
      onStrokeRelease: (fire, power01) => {
        this.strokeDraw = 0;
        this.needsRender = true;
        if (!fire || this.phase !== 'aiming' || !this.myTurn) return;
        this.power01 = power01;
        this.shootHuman();
      },
      onChalk: () => {
        if (this.canChalk()) this.chalkTip();
      },
      onAimBoost: (active) => {
        this.aimBoost = active;
      },
      onTilt: (deg) => {
        // Absolute, from the touch dial. Only while it's actually your shot —
        // the dial stays visible between turns but must not move a live cue.
        if (this.phase !== 'aiming' || !this.myTurn) return;
        this.setElevation((deg * Math.PI) / 180);
        this.needsRender = true;
      },
    });

    // anonymous handle: a stored one, or a fresh random "Adjective Noun" (en/ko)
    this.nickname = (localStorage.getItem('carom.nick') ?? '').slice(0, 24);
    if (!this.nickname) this.setNickname(randomDisplayName(getLang()));
    // profile avatar (upper-body portrait); persisted as a compact index string
    this.avatar = parseAvatar(localStorage.getItem('carom.avatar'));
    this.hud.setAvatar(this.avatar);
    this.token = localStorage.getItem('carom.token');
    // remembered single-play level (Korean handicap); default 100
    const savedLevel = Number(localStorage.getItem('carom.level')) || 100;
    this.handicaps = [savedLevel, savedLevel];
    // remembered table theme
    const savedTheme = localStorage.getItem('carom.theme') as ThemeId | null;
    if (savedTheme && THEMES[savedTheme]) this.theme = savedTheme;
    // remembered background scene (so the chosen 3D hall / 360° backdrop sticks)
    const savedEnv = localStorage.getItem('carom.env') as EnvId | null;
    if (savedEnv && ENVIRONMENTS[savedEnv]) this.env = savedEnv;
    this.lobby = new Lobby(hudRoot, {
      onNickname: (name) => this.setNickname(name),
      onReroll: () => {
        this.setNickname(randomDisplayName(getLang()));
        this.lobby.setNickname(this.nickname);
      },
      onRefresh: () => this.sendList(),
      onCreate: (name, format, variant, finish, breakStyle, stake) => {
        this.finish = finish ?? defaultFinish(format);
        this.netCreateRoom(name, format, variant, breakStyle, stake);
      },
      onJoin: (code) => this.net?.send({ type: 'joinRoom', code, identity: this.identity() }),
      onLeave: () => this.netLeaveRoom(),
      onReady: (ready) => this.net?.send({ type: 'setReady', ready }),
      onStart: () => this.net?.send({ type: 'startGame' }),
      onOpenItems: () => this.openStore(),
      onKick: (id) => this.net?.send({ type: 'kick', targetId: id }),
      onTheme: (id) => this.applyTheme(id), // table colour: chosen at creation, also live-previews the practice table
      onEnv: (id) => this.applyEnv(id), // background scene: chosen at creation, also live-previews the practice table
      onClose: () => this.openMainMenu(), // close the room browser -> back to main menu
      onEditAvatar: () => this.avatarEditor.open(this.avatar),
      onLogin: (u, p) => {
        this.authSource = 'lobby';
        this.net?.send({ type: 'login', name: u, password: p });
      },
      onSignup: (u, p, email) => {
        this.authSource = 'lobby';
        this.net?.send({ type: 'signup', name: u, password: p, email, token: this.token ?? undefined });
      },
      onSocialLogin: (provider) => void this.socialLogin(provider),
      onLogout: () => this.netLogout(),
    });
    this.lobby.setNickname(this.nickname);
    this.authModal = new AuthModal(hudRoot, {
      onLogin: (u, p) => {
        this.authSource = 'modal';
        (this.net ?? this.presence)?.send({ type: 'login', name: u, password: p });
      },
      onSignup: (u, p, email) => {
        this.authSource = 'modal';
        (this.net ?? this.presence)?.send({ type: 'signup', name: u, password: p, email, token: this.token ?? undefined });
      },
      onClose: () => {},
    });
    this.avatarEditor = new AvatarEditor(hudRoot, { onSave: (cfg) => this.setAvatar(cfg) }, this.avatar);
    this.lobby.setAvatar(this.avatar);
    this.chat = new ChatPanel(hudRoot, {
      onSend: (text) => this.net?.send({ type: 'chat', text }),
      // whispers ride the always-on presence socket, so they work from the menus
      // and the room browser too — not just inside a room
      onWhisper: (to, text) => this.sendWhisper(to, text),
      onReplayShot: (shot) => this.replaySharedShot(shot),
    });
    this.emoteWheel = new EmoteWheel(hudRoot, { onEmote: (id) => this.fireEmote(id) });
    // Optional real audio for sound emotes: drop CC0 files in public/sfx/ (clap.mp3,
    // nice.mp3, …). Missing files fall back to the synthesized sounds automatically.
    this.sound.setEmoteSamples({
      clap: '/sfx/clap.mp3',
      nice: '/sfx/nice.mp3',
      cheer: '/sfx/cheer.mp3',
      whistle: '/sfx/whistle.wav',
      boo: '/sfx/boo.wav',
      drumroll: '/sfx/drumroll.mp3', // no sample yet → synthesized fallback
      // Korean 당구 voice lines (recorded); ids match the v_* emotes in the catalog.
      v_kabi: '/sfx/voice/kabi.mp3',
      v_niceshot: '/sfx/voice/niceshot.mp3',
      v_goodshot: '/sfx/voice/goodshot.mp3',
      v_taeyang: '/sfx/voice/taeyang.mp3',
      v_jjalne: '/sfx/voice/jjalne.mp3',
      v_jjalne2: '/sfx/voice/jjalne2.mp3',
      v_angaju: '/sfx/voice/angaju.mp3',
      v_jayu: '/sfx/voice/jayu.mp3',
      v_mwonya: '/sfx/voice/mwonya.mp3',
      v_gensei: '/sfx/voice/gensei.mp3',
      v_coffee: '/sfx/voice/coffee.mp3',
    });
    this.presencePanel = new PresencePanel(hudRoot, {
      onWatch: (room) => this.watchRoom(room),
      onWhisper: (name) => this.chat.openDm(name), // 💬 in the online list → a DM tab
      onRematch: (name) => this.sendRematch(name),
    });
    this.reviewPanel = new ShotReviewPanel(hudRoot);
    this.historyPanel = new HistoryPanel(hudRoot, {
      onOpen: (id) => this.openHistoryGame(id),
      // closing the list → back to the menu (only if we're still in the menu, not
      // if we've since dropped into practice)
      onClose: () => this.menuOpen && this.mainMenu.show(),
    });
    this.storePanel = new ItemStorePanel(hudRoot, {
      onBuy: (itemId) => (this.presence ?? this.net)?.send({ type: 'purchase', itemId }),
      onEquip: (itemId) => (this.presence ?? this.net)?.send({ type: 'equip', itemId }),
      onPlayMusic: (videoId, title) => this.music.addOwnedTrack(videoId, title),
      onRename: (name) => (this.presence ?? this.net)?.send({ type: 'rename', name }),
      onPreviewSound: (sound) => this.sound.emote(sound),
      onClose: () => this.menuOpen && this.mainMenu.show(),
    });
    this.feedbackPanel = new FeedbackPanel(hudRoot, {
      onSend: (text) => (this.presence ?? this.net)?.send({ type: 'feedbackCreate', text }),
      onClose: () => this.menuOpen && this.mainMenu.show(),
    });
    this.tutorialPanel = new TutorialPanel(hudRoot, {
      onNext: () => this.advanceMission(),
      onSkip: () => this.skipMission(),
      onReset: () => this.resetMission(),
      onExit: () => this.exitTutorial(),
    });
    this.dailyPanel = new DailyPanel(hudRoot, {
      onPlay: () => this.startDailyRun(),
      // closing after a finished attempt leaves a dead table behind, so head back
      // to the menu rather than stranding the player on it
      onClose: () => {
        this.dailyWanted = false;
        if (this.menuOpen) this.mainMenu.show();
        else if (!this.online && !this.dailyRun) this.openMainMenu();
      },
    });
    this.rewardToast = new RewardToast(hudRoot);
    this.presencePanel.setMyName(this.myDisplayName());
    this.adminPanel = new AdminPanel(hudRoot, {
      onSubscribe: () => this.net?.send({ type: 'adminSubscribe' }),
      onUnsubscribe: () => this.net?.send({ type: 'adminUnsubscribe' }),
      onHistory: (id) => this.net?.send({ type: 'playerHistory', id }),
      onSetTier: (targetId, tier) => this.net?.send({ type: 'setTier', targetId, tier }),
    });
    this.connectPresence(); // always-on socket announcing this player to the online list

    this.buildPrompt(hudRoot);
    this.music = new MusicPanel(hudRoot, (a) => this.onMusicAction(a));
    this.voice = new VoiceChat({
      send: (signal) => this.net?.send({ type: 'voice', ...signal }),
      seat: () => (this.seat % 2) as 0 | 1,
      onStatus: (status, muted) => this.voicePanel.setStatus(status, muted),
    });
    this.voicePanel = new VoicePanel(hudRoot, {
      onToggle: () => void this.voice.toggle(),
      onMute: (muted) => this.voice.setMuted(muted),
    });
    // Restore a previously chosen field of view BEFORE the panel is built, so its
    // slider opens on the value actually in use (the panel reads each param once).
    const savedFov = Number(localStorage.getItem('carom.fov'));
    if (Number.isFinite(savedFov) && savedFov > 0) this.cameraRig.setFov(savedFov);
    const savedZoomOut = Number(localStorage.getItem('carom.zoomOutMax'));
    if (Number.isFinite(savedZoomOut) && savedZoomOut > 0) this.cameraRig.setZoomOutMax(savedZoomOut);
    const savedZoomIn = Number(localStorage.getItem('carom.zoomInMax'));
    if (Number.isFinite(savedZoomIn) && savedZoomIn > 0) this.cameraRig.setZoomInMax(savedZoomIn);
    const savedZoomRate = Number(localStorage.getItem('carom.zoomRate'));
    if (Number.isFinite(savedZoomRate) && savedZoomRate > 0) this.cameraRig.setZoomStepRate(savedZoomRate);
    // ...and the pull-back they last chose (applied after the limits above, which
    // clamp it). Read back from the rig so aimZoom always holds a legal value.
    const savedZoom = Number(localStorage.getItem('carom.zoom'));
    // Touch has no saved preference yet: default it pulled back further than desktop's
    // framing so the whole table (and the touch HUD panels around it) fit a small screen.
    const TOUCH_DEFAULT_ZOOM = 3.3;
    this.aimZoom =
      Number.isFinite(savedZoom) && savedZoom > 0
        ? this.cameraRig.setZoom(savedZoom)
        : this.touch
          ? this.cameraRig.setZoom(TOUCH_DEFAULT_ZOOM)
          : this.cameraRig.zoomLevel;
    // Physics/config tuning panel (collapsed by default; + in its header opens
    // it) — a developer tool with no touch-friendly affordance of its own, and
    // one more thing competing for the same corner as the real touch controls.
    if (!this.touch) new TuningPanel(hudRoot, this.tuningParams());

    this.mainMenu = new MainMenu(
      hudRoot,
      {
        onPractice: (level) => this.startSingle('solo', level),
        onVsAI: (level) => this.startSingle('ai', level),
        // fired by the "+ 방 만들기" button inside the main menu's rooms screen
        // (behind 온라인 대전) — straight to the create form, not the browser.
        onOnline: () => this.openCreateRoom(),
        // a bare login/signup form, no room list attached — the Kakao button
        // next to it is the one-click path; this is for an existing password account
        onGeneralAuth: () => this.authModal.open(),
        onKakaoLogin: () => void this.socialLogin('kakao'),
        onStore: () => this.openStore(),
        onFeedback: () => this.openFeedback(),
        onDaily: () => this.openDaily(),
        onTutorial: (course) => this.startTutorial(course),
        onJoinRoom: (code) => this.watchRoom(code),
      },
      this.handicaps[0]
    );
    this.refreshTutorialProgress();

    // reflect the remembered single-play settings in the in-game controls
    this.hud.setHandicap(this.handicaps[0]);
    this.hud.setEnv(this.env);
    // Resume the last single-play session straight away — UNLESS that game had
    // already finished, in which case ask again (show the mode menu). First-time
    // players (or those who last played online) also get the menu. Level + background
    // are changed in-game from the HUD; the start menu only asks the mode.
    // Always ask for the game mode on entry (a previously-left game is never
    // mid-play once reloaded — single play rebuilds from the opening — so there's
    // nothing to silently resume into). Build a quiet table behind the menu.
    this.buildForVariant(this.variant);
    // A shot permalink wins over both the menu and a resumable room: the visitor
    // followed a link to see ONE shot, so that's what the page must open on.
    const linked = takeLinkedShot();
    if (linked) {
      this.menuOpen = false;
      this.openLinkedShot(linked);
    } else if (this.tryResumeRoom()) {
      this.menuOpen = false;
    } else {
      this.menuOpen = true;
      this.mainMenu.show(); // row-3 data arrives once the presence socket opens (see connectPresence)
    }
    this.applyMenuBackdrop(this.menuOpen); // start on the static image if opening straight to the menu
    // re-render the dynamic HUD text (title / sub / stats) when the language changes
    onLang(() => this.match && this.refreshHud());
    this.bindInput();
    this.resize();
    new ResizeObserver(() => this.resize()).observe(canvas);
    // FPS graph (stats.js), top-right. It overlaps the in-game exit button, which
    // is why it was pulled before — `pointerEvents: none` is what makes that fine
    // now: clicks fall straight through the meter to the button underneath.
    Object.assign(this.stats.dom.style, {
      position: 'fixed', top: '8px', right: '8px', left: 'auto',
      zIndex: '21', pointerEvents: 'none', opacity: '0.85',
    });
    document.body.appendChild(this.stats.dom);

    this.lastNow = performance.now();
    this.renderer.three.setAnimationLoop((t) => this.frame(t));
    // Browsers stop requestAnimationFrame in hidden tabs, which would freeze the
    // frame loop (shot playback, sounds, bot turns) until the tab regains focus.
    // Main-thread timers are throttled to >=1s in background tabs, but worker
    // timers are not — so while hidden, a throwaway worker ticks frame() at 20fps.
    //
    // Both handlers also flush render telemetry: 'pagehide' fires on mobile
    // tab-switch/close where 'unload' does not, and a backgrounded tab often
    // never comes back — that's frequently the session that was performing badly.
    window.addEventListener('pagehide', () => this.flushPerf(performance.now(), true));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flushPerf(performance.now(), true);
        const src = 'setInterval(() => postMessage(0), 50);';
        const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
        this.hiddenTicker = new Worker(url);
        URL.revokeObjectURL(url);
        this.hiddenTicker.onmessage = () => this.frame(performance.now());
      } else {
        this.hiddenTicker?.terminate();
        this.hiddenTicker = null;
      }
    });
  }

  // ---- setup / variant switching ----

  /** Rebuild the table + hall + backdrop for the current colour theme & variant. */
  private rebuildScenery(): void {
    this.needsRender = true; // new geometry/lighting → redraw + refresh shadows
    const theme = THEMES[this.theme];
    const table = tableFor(this.variant);
    if (this.tableGroup) this.scene.remove(this.tableGroup);
    if (this.hallGroup) this.scene.remove(this.hallGroup);
    this.tableGroup = buildTable(theme, table);
    this.hallGroup = buildHall(theme, table);
    this.scene.add(this.tableGroup, this.hallGroup);
    this.applyCustomTable();
    this.applyEnvironment();
  }

  /**
   * If the current variant has a designed table model (see {@link TABLE_MODELS}),
   * swap it in for the procedural table's *visual*. Physics is untouched — it
   * runs on the variant's TableSpec — so the model only changes how the table
   * looks. The procedural table shows immediately and stays as the fallback while
   * the model streams in (or if it fails to load). The model brings its own
   * legs/base, so the procedural hall's legs are hidden when one is active.
   */
  private applyCustomTable(): void {
    const url = TABLE_MODELS[this.variant];
    const token = ++this.tableModelToken;
    if (!url) return; // no designed model for this variant: keep the procedural table
    const swap = (obj: THREE.Object3D) => {
      if (token !== this.tableModelToken) return; // variant changed since the request
      if (this.tableGroup) this.scene.remove(this.tableGroup);
      this.tintTableFelt(obj); // re-colour the felt to the current theme before showing it
      this.tableGroup = obj; // the designed mesh becomes the table
      this.scene.add(obj);
      if (this.hallGroup) this.hideHallLegs(this.hallGroup); // table has its own base
    };
    const cached = this.tableModelCache.get(this.variant);
    if (cached) {
      swap(cached);
      return;
    }
    // No shadowRadius here (unlike the hall surround): this model IS the table,
    // so it must keep casting — it's what blocks the overhead spotlight from
    // reaching the floor. Dropping cast (tried once, reverted) let light shine
    // straight through the felt/slate and put the balls' and cue's shadows on
    // the floor instead. It's cheap anyway: only 17 meshes / ~660 triangles.
    void loadModel(url, { up: 'y' })
      .then((obj) => {
        this.optimizeStaticModel(obj); // merge by material + freeze matrices, as for the hall
        this.tableModelCache.set(this.variant, obj);
        swap(obj);
      })
      .catch(() => {
        /* model missing/blocked: keep the procedural table */
      });
  }

  /**
   * Re-tint every billiard table's felt in a model to the active colour theme —
   * both the player's table model and the decorative tables baked into the hall
   * scene. Those GLBs use flat `cloth`/`cushion`/`rail_wood` materials (named to
   * match the procedural table), so without this they ignore the admin's
   * table-colour pick and stay the colour they were exported in. Mirrors the
   * palette in Table.ts: cushions are the cloth darkened 25% toward black; rails
   * use the wood colour. Called on every swap (fresh load AND cache hit) since the
   * theme may have changed since the model was cached.
   */
  private tintTableFelt(obj: THREE.Object3D): void {
    const theme = THEMES[this.theme];
    const cushion = new THREE.Color(theme.cloth).lerp(new THREE.Color(0x000000), 0.25).getHex();
    obj.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        const m = mat as THREE.MeshStandardMaterial;
        const name = m?.name ?? '';
        if (name.startsWith('cloth')) m.color.setHex(theme.cloth);
        else if (name.startsWith('cushion')) m.color.setHex(cushion);
        else if (name.startsWith('rail_wood')) m.color.setHex(theme.railWood);
      }
    });
  }

  /** Hide the procedural hall's table legs (the designed table supplies its own),
   *  keeping the floor. Legs are the BoxGeometry meshes; the floor is the plane. */
  private hideHallLegs(hall: THREE.Group): void {
    for (const child of hall.children) {
      const m = child as THREE.Mesh;
      if (m.isMesh && (m.geometry as THREE.BufferGeometry).type === 'BoxGeometry') m.visible = false;
    }
  }

  /**
   * Apply the selected environment as a full 360° VR scene, sourced from a real
   * equirectangular 360 VR image OR video (or the procedural hall):
   *  - hall  → gradient backdrop + neutral indoor IBL
   *  - photo → equirectangular still wrapped around AND used as image-based
   *            lighting, so the table sits inside and reflects the scene
   *  - video → a looping equirectangular 360 clip as a live wrap-around backdrop
   * Sources stream in and are cached; the gradient shows as a placeholder.
   */
  /** Toggle the extra hall lighting used for 3D-model scenes. The procedural hall
   *  and 360° backdrops keep the dim, table-focused rig (and exposure tuned for the
   *  felt); a loaded room would otherwise sit in darkness beyond the table lamps. */
  private setModelFill(on: boolean): void {
    if (!this.hallFill) {
      const g = new THREE.Group();
      const hemi = new THREE.HemisphereLight(0xffffff, 0x556070, HALL_FILL.hemi);
      hemi.position.set(0, 0, 4); // z-up world
      const dir = new THREE.DirectionalLight(0xfff6e8, HALL_FILL.dir);
      dir.position.set(6, 4, 8); // high and to the side
      g.add(hemi, dir);
      this.hallFill = g;
      this.scene.add(g);
    }
    this.hallFill.visible = on;
    this.scene.environmentIntensity = on ? HALL_FILL.envIntensity : 1.0;
    this.renderer.three.toneMappingExposure = on ? HALL_FILL.exposure : 0.82;
  }

  /** Turn modelled ceiling-lamp panels into real light sources: drop a warm point
   *  light onto each mesh whose name starts with the env's `lampPrefix`. Called once
   *  per model (the lights ride along in the cached object on re-selection). */
  private addLampLights(root: THREE.Object3D, prefix: string): void {
    root.updateMatrixWorld(true);
    const v = new THREE.Vector3();
    const lamps: { mesh: THREE.Object3D; d: number }[] = [];
    root.traverse((o) => {
      if ((o as THREE.Mesh).isMesh && o.name.startsWith(prefix)) {
        o.getWorldPosition(v);
        lamps.push({ mesh: o, d: Math.hypot(v.x, v.y) }); // horizontal distance to the table (origin)
      }
    });
    // Only light the lamps nearest the table — the camera lives there, and each
    // point light is real per-pixel GPU cost, so distant lamps aren't worth it.
    lamps.sort((a, b) => a.d - b.d);
    // One dim clone per source material, SHARED by every glow-only panel. Cloning
    // per mesh would give the ~110 unlit panels ~110 distinct materials, which
    // blocks mergeStatic() from collapsing them into a single draw call.
    const dimmed = new Map<THREE.Material, THREE.MeshStandardMaterial>();
    lamps.forEach(({ mesh }, i) => {
      if (i < HALL_LAMP.maxLights) {
        mesh.add(new THREE.PointLight(HALL_LAMP.color, HALL_LAMP.intensity, HALL_LAMP.distance, HALL_LAMP.decay));
      } else {
        // glow-only panel (no light, for FPS): dim its emissive so it doesn't look
        // "lit but casting nothing". Clone the shared material so we don't dim the lit ones.
        const m = (mesh as THREE.Mesh).material as THREE.MeshStandardMaterial;
        if (m && 'emissiveIntensity' in m) {
          let c = dimmed.get(m);
          if (!c) {
            c = m.clone();
            c.emissiveIntensity = (m.emissiveIntensity ?? 1) * HALL_LAMP.unlitGlow;
            dimmed.set(m, c);
          }
          (mesh as THREE.Mesh).material = c;
        }
      }
    });
  }

  private applyEnvironment(): void {
    this.needsRender = true; // lighting/background changed → redraw + refresh shadows
    const def = ENVIRONMENTS[this.env];
    const token = ++this.envToken;
    // A full 3D-model hall needs general lighting; the dim table-focused rig leaves
    // it in darkness. Add a bright fill + lift the IBL/exposure for model scenes only,
    // so the felt look of the other backdrops is untouched.
    this.setModelFill(!!def.model);
    // A real 360 photo/video or a 3D room model replaces the surround, so hide
    // the procedural floor + legs; the bare procedural hall keeps them.
    if (this.hallGroup) this.hallGroup.visible = !(def.panorama || def.video || def.model);
    // pause any 360 video that was playing before this switch
    if (this.activeVideo) {
      this.activeVideo.pause();
      this.activeVideo = null;
    }
    // remove any room model left over from a previous selection
    if (this.activeModel) {
      this.scene.remove(this.activeModel);
      this.activeModel = null;
    }
    if (def.video) {
      this.applyVideoEnvironment(def.id, def.video);
      return;
    }
    if (def.model) {
      this.applyModelEnvironment(def, token);
      return;
    }
    if (!def.panorama) {
      this.scene.background = makeBackdrop(THEMES[this.theme]);
      this.scene.environment = this.neutralEnv;
      return;
    }
    const wrap = (tex: THREE.Texture) => {
      this.scene.background = tex; // 360° surround
      this.scene.environment = tex; // image-based lighting / reflections from the scene
    };
    const cached = this.panoramaCache.get(this.env);
    if (cached) {
      wrap(cached);
      return;
    }
    this.scene.background = makeBackdrop(THEMES[this.theme]); // placeholder while streaming
    void loadPanorama(def.panorama)
      .then((tex) => {
        this.panoramaCache.set(this.env, tex);
        if (token === this.envToken) wrap(tex); // unless superseded
      })
      .catch(() => {
        /* offline / blocked: keep the procedural gradient + neutral IBL */
      });
  }

  /** Play a looping equirectangular 360 VR video as the wrap-around background. */
  private applyVideoEnvironment(id: EnvId, url: string): void {
    let entry = this.videoCache.get(id);
    if (!entry) {
      entry = loadPanoramaVideo(url);
      this.videoCache.set(id, entry);
      // if the host blocks CORS (or the clip fails), fall back to the gradient
      entry.video.addEventListener(
        'error',
        () => {
          if (this.env === id) this.scene.background = makeBackdrop(THEMES[this.theme]);
        },
        { once: true }
      );
    }
    this.activeVideo = entry.video;
    void entry.video.play().catch(() => {
      /* autoplay blocked until a gesture; the dropdown change is one, so this is rare */
    });
    this.scene.background = entry.texture; // live 360 video surround
    this.scene.environment = this.neutralEnv; // per-frame video IBL is too costly; keep neutral
  }

  /**
   * Place a 3D room model (e.g. exported from Blender) around the table as the
   * surround. The model provides the visible scenery; we keep the neutral indoor
   * IBL lighting the table and a dim gradient behind anything the model doesn't
   * cover. Loaded models are cached and re-added on reselection. If the model is
   * missing/unloadable, fall back to the procedural hall.
   */
  private applyModelEnvironment(def: EnvDef, token: number): void {
    this.scene.background = makeBackdrop(THEMES[this.theme]); // dim surround behind the model
    this.scene.environment = this.neutralEnv; // keep the indoor IBL lighting the table
    const cached = this.modelCache.get(def.id);
    if (cached) {
      this.tintTableFelt(cached); // the scene's decorative tables follow the colour theme too
      this.scene.add(cached);
      this.activeModel = cached;
      return;
    }
    void loadModel(def.model!, {
      up: def.modelUp,
      scale: def.modelScale,
      offset: def.modelOffset,
      hide: def.hideNodes,
      materialTweaks: def.materialTweaks,
      shadowRadius: MODEL_SHADOW_RADIUS,
    })
      .then((obj) => {
        if (def.lampPrefix) this.addLampLights(obj, def.lampPrefix); // lamp panels -> real lights
        this.tintTableFelt(obj); // the scene's decorative tables follow the colour theme too
        this.optimizeStaticModel(obj);
        this.modelCache.set(def.id, obj);
        if (token === this.envToken) {
          this.scene.add(obj);
          this.activeModel = obj;
        }
      })
      .catch(() => {
        // missing/blocked model: restore the procedural hall so the table isn't bare
        if (token === this.envToken && this.hallGroup) this.hallGroup.visible = true;
      });
  }

  /**
   * Flatten a loaded surround model for drawing: merge its meshes per material,
   * then stop re-deriving world matrices for a scene that never moves.
   *
   * Run once per model, before it is cached — the cached object carries the
   * optimisation into every later re-selection of the environment.
   */
  private optimizeStaticModel(obj: THREE.Object3D): void {
    const [before, after] = mergeStatic(obj, (mesh) =>
      // a lamp panel carrying a real PointLight child: merging would take the
      // light with it. The (many) glow-only panels have no child and do merge.
      mesh.children.some((c) => (c as THREE.Light).isLight)
    );
    // The hall is scenery: nothing in it animates, so recomputing ~2.4k node
    // matrices every frame is pure CPU. One update, then freeze the subtree.
    obj.updateMatrixWorld(true);
    obj.matrixWorldAutoUpdate = false;
    if (import.meta.env.DEV) console.info(`[env] merged model draws: ${before} → ${after}`);
  }

  /** Snapshot the shared room settings (table theme + physics) for the wire. */
  private roomConfig(): RoomConfig {
    const P = PHYSICS;
    return {
      theme: this.theme,
      env: this.env,
      physics: {
        slidingFriction: P.slidingFriction,
        rollingFriction: P.rollingFriction,
        spinningFriction: P.spinningFriction,
        ballBallRestitution: P.ballBallRestitution,
        cushionRestitution: P.cushionRestitution,
        cushionRestitutionSpeedFalloff: P.cushionRestitutionSpeedFalloff,
        cushionRestitutionMin: P.cushionRestitutionMin,
        cushionFriction: P.cushionFriction,
        cushionRollGrip: P.cushionRollGrip,
        followDrawScale: P.followDrawScale,
        masseScale: P.masseScale,
        jumpFactor: P.jumpFactor,
        gravity: P.gravity,
      },
      maxShotSpeed: this.tune.maxShotSpeed,
      playbackSpeed: this.tune.playbackSpeed,
      finish: { ...this.finish },
    };
  }

  /** Push our table/physics config to the room (host on start, or after an edit). */
  private broadcastConfig(): void {
    // Host shares table/physics whenever in a room (waiting or playing); the
    // server only stores it from the host and relays it to everyone else.
    if (!this.online || !this.net || this.spectator) return;
    this.net.send({ type: 'config', config: this.roomConfig() });
  }

  /** Adopt a peer's shared table/physics config (never re-broadcasts). */
  private applyRoomConfig(c: RoomConfig): void {
    const P = PHYSICS;
    P.slidingFriction = c.physics.slidingFriction;
    P.rollingFriction = c.physics.rollingFriction;
    P.spinningFriction = c.physics.spinningFriction;
    P.ballBallRestitution = c.physics.ballBallRestitution;
    P.cushionRestitution = c.physics.cushionRestitution;
    P.cushionRestitutionSpeedFalloff = c.physics.cushionRestitutionSpeedFalloff;
    P.cushionRestitutionMin = c.physics.cushionRestitutionMin;
    P.cushionFriction = c.physics.cushionFriction;
    P.cushionRollGrip = c.physics.cushionRollGrip ?? P.cushionRollGrip;
    P.followDrawScale = c.physics.followDrawScale ?? P.followDrawScale;
    P.masseScale = c.physics.masseScale ?? P.masseScale;
    P.jumpFactor = c.physics.jumpFactor ?? P.jumpFactor;
    P.gravity = c.physics.gravity;
    this.tune.maxShotSpeed = c.maxShotSpeed;
    this.tune.playbackSpeed = c.playbackSpeed;
    // adopt the host's finish rule (older hosts omit it → keep our default)
    if (c.finish) this.finish = { caroms: c.finish.caroms, banks: c.finish.banks };
    // table colour is admin-set room config: adopt the host's choice + reflect it
    if (this.theme !== c.theme) {
      this.theme = c.theme;
      this.hud.setTheme(c.theme);
      this.rebuildScenery();
    }
    this.lobby.setTheme(c.theme);
    // background scene is admin-set room config too (older hosts may omit `env`)
    const env = c.env ?? this.env;
    if (this.env !== env) {
      this.env = env;
      this.hud.setEnv(env);
      this.applyEnvironment();
    }
    this.lobby.setEnv(env);
  }

  /** Live-tunable parameters exposed in the Tuning panel (physics + control feel). */
  private tuningParams(): TuneParam[] {
    const P = PHYSICS;
    const tn = this.tune;
    // wrap each setter so tweaking a slider also syncs it to the online opponent
    const p = (labelKey: string, min: number, max: number, step: number, get: () => number, set: (v: number) => void): TuneParam => ({
      labelKey, min, max, step, get,
      set: (v) => {
        set(v);
        this.broadcastConfig();
      },
    });
    return [
      p('tune.slidingFriction', 0, 0.6, 0.005, () => P.slidingFriction, (v) => (P.slidingFriction = v)),
      p('tune.rollingFriction', 0, 0.05, 0.001, () => P.rollingFriction, (v) => (P.rollingFriction = v)),
      p('tune.spinFriction', 0, 0.1, 0.001, () => P.spinningFriction, (v) => (P.spinningFriction = v)),
      p('tune.ballRestitution', 0.5, 1, 0.01, () => P.ballBallRestitution, (v) => (P.ballBallRestitution = v)),
      p('tune.cushionRestitution', 0.5, 1, 0.01, () => P.cushionRestitution, (v) => (P.cushionRestitution = v)),
      p('tune.cushionFalloff', 0, 0.1, 0.005, () => P.cushionRestitutionSpeedFalloff, (v) => (P.cushionRestitutionSpeedFalloff = v)),
      p('tune.cushionMin', 0.3, 1, 0.01, () => P.cushionRestitutionMin, (v) => (P.cushionRestitutionMin = v)),
      p('tune.cushionFriction', 0, 0.4, 0.005, () => P.cushionFriction, (v) => (P.cushionFriction = v)),
      p('tune.cushionRollGrip', 0, 1, 0.05, () => P.cushionRollGrip, (v) => (P.cushionRollGrip = v)),
      p('tune.followDraw', 0, 1.2, 0.05, () => P.followDrawScale, (v) => (P.followDrawScale = v)),
      p('tune.masse', 0, 1.5, 0.05, () => P.masseScale, (v) => (P.masseScale = v)),
      p('tune.jump', 0, 0.4, 0.01, () => P.jumpFactor, (v) => (P.jumpFactor = v)),
      p('tune.gravity', 5, 15, 0.1, () => P.gravity, (v) => (P.gravity = v)),
      p('tune.maxSpeed', 2, 8, 0.1, () => tn.maxShotSpeed, (v) => (tn.maxShotSpeed = v)),
      p('tune.animSpeed', 0.25, 1.5, 0.05, () => tn.playbackSpeed, (v) => (tn.playbackSpeed = v)),
      p('tune.aimSens', 0.001, 0.02, 0.0005, () => tn.aimSensitivity, (v) => (tn.aimSensitivity = v)),
      p('tune.tiltSens', 0.0003, 0.006, 0.0003, () => tn.tiltSensitivity, (v) => (tn.tiltSensitivity = v)),
      p('tune.strokeRange', 100, 1200, 10, () => tn.strokeDrawRange, (v) => (tn.strokeDrawRange = v)),
      p('tune.strokeSpeed', 0.5, 20, 0.5, () => tn.strokeSpeedFull, (v) => (tn.strokeSpeedFull = v)),
      p('tune.strokeCurve', 1, 5, 0.25, () => tn.strokeCurve, (v) => (tn.strokeCurve = v)),
      p('tune.shotJitter', 0, 0.5, 0.01, () => tn.shotJitter, (v) => (tn.shotJitter = v)),
      // Camera lens, NOT wrapped in `p()`: field of view is a personal viewing
      // preference, so it must not be broadcast to the room like the shared
      // physics are. Persisted locally instead, and it survives a reload.
      {
        labelKey: 'tune.fov',
        min: FOV_MIN,
        max: FOV_MAX,
        step: 1,
        get: () => this.cameraRig.fov,
        set: (v) => this.setFov(v),
      },
      // Also local-only: how far the camera may pull back...
      {
        labelKey: 'tune.zoomOut',
        min: ZOOM_OUT_MIN,
        max: ZOOM_OUT_MAX,
        step: 0.5,
        get: () => this.cameraRig.zoomOutMax,
        set: (v) => this.setZoomOutMax(v),
      },
      // ...how close it may push in (lower = closer)...
      {
        labelKey: 'tune.zoomIn',
        min: ZOOM_IN_MIN,
        max: ZOOM_IN_MAX,
        step: 0.01,
        get: () => this.cameraRig.zoomInMax,
        set: (v) => this.setZoomInMax(v),
      },
      // ...and how big each wheel/key zoom step is, which wants re-tuning
      // whenever the field of view changes.
      {
        labelKey: 'tune.zoomRate',
        min: ZOOM_RATE_MIN,
        max: ZOOM_RATE_MAX,
        step: 0.05,
        get: () => this.cameraRig.zoomStepRate,
        set: (v) => this.setZoomStepRate(v),
      },
    ];
  }

  /** Apply a zoom-in limit and remember it for next time. */
  private setZoomInMax(v: number): void {
    this.cameraRig.setZoomInMax(v);
    localStorage.setItem('carom.zoomInMax', String(this.cameraRig.zoomInMax));
    this.needsRender = true;
  }

  /** Apply a zoom step rate and remember it for next time. */
  private setZoomStepRate(v: number): void {
    this.cameraRig.setZoomStepRate(v);
    localStorage.setItem('carom.zoomRate', String(this.cameraRig.zoomStepRate));
  }

  /** Apply a zoom-out limit and remember it for next time. */
  private setZoomOutMax(v: number): void {
    this.cameraRig.setZoomOutMax(v);
    localStorage.setItem('carom.zoomOutMax', String(this.cameraRig.zoomOutMax));
    this.needsRender = true;
  }

  /** Apply a camera field of view and remember it for next time. */
  private setFov(deg: number): void {
    this.cameraRig.setFov(deg);
    localStorage.setItem('carom.fov', String(this.cameraRig.fov));
    this.needsRender = true; // on-demand renderer: the projection changed
  }

  private matchConfig(variant: VariantId): MatchConfig {
    // Solo = practice on your own (the second seat never shoots). AI = the second
    // seat is a computer opponent that takes its turn when you miss.
    const ai = this.uiMode === 'ai';
    // Assessment is a solo session whose handicap is high enough never to "clear"
    // (it ends after a fixed number of shots, not by clearing the countdown).
    const start = this.assessing ? ASSESS_HANDICAP : this.handicaps[0];
    return {
      rule: this.ruleFor(variant),
      mode: 'handicap',
      solo: this.assessing || !ai,
      equalizingInning: false,
      players: [
        { id: 'p1', name: 'You', cueBall: 'white', isAI: false, startScore: start },
        { id: 'p2', name: ai ? 'AI' : '—', cueBall: 'yellow', isAI: ai && !this.assessing, startScore: this.handicaps[1] },
      ],
    };
  }

  /** The variant rule with the room's house finish applied (4-ball only). */
  private ruleFor(variant: VariantId): CaromRuleConfig {
    const rule = VARIANTS[variant];
    if (variant !== 'straight-rail') return rule;
    return { ...rule, finalShotCount: this.finish.caroms, finalBankCount: this.finish.banks };
  }

  /** Tell the room (locally) what it takes to finish a 4-ball game. */
  private announceFinishRule(variant: VariantId): void {
    if (variant !== 'straight-rail') return;
    this.chat.system(t('chat.finishRule', { c: this.finish.caroms, b: this.finish.banks }));
  }

  private buildForVariant(variant: VariantId): void {
    this.persistGameLog(); // flush any prior solo/AI session (uses the still-current match)
    this.practiceCtx = null; // a fresh table is not a recorded-shot practice
    this.variant = variant;
    this.base = createCaromWorld(variant);
    this.match = createMatch({ ...this.matchConfig(variant), startingPlayerIndex: 0 });

    this.rebuildBalls(variant);
    this.rebuildScenery(); // table geometry is per-variant
    this.cameraRig.setTable(tableFor(variant));

    this.phase = 'aiming';
    this.currentShot = null;
    this.lastShot = null;
    this.replaying = false;
    this.shotPractice = false;
    this.aiToken++; // cancel any pending AI shot
    this.aiThinking = false;
    this.message = t(this.uiMode === 'ai' ? 'msg.aiStart' : 'msg.soloStart', { h: this.handicaps[0] });
    this.syncBalls();
    this.faceObjectBall();
    this.resetTouchCameraForNewGame();
    this.applyTurnCamera();
    // record this single-player session (vs-AI or solo practice); flushed on the
    // next buildForVariant / mode change / game end
    this.beginGameLog(this.uiMode === 'ai' ? 'ai' : 'solo', variant, 'solo');
    // 4-ball (offline) opens with a hands-on break: cluster the balls in a corner
    // and let the player sweep the cue across them to scatter the opening layout.
    // (a 오늘의 배치 attempt places an exact shared layout right after this, so it
    // must not be scattered by the break either)
    if (variant === 'straight-rail' && !this.online && !this.assessing && !this.dailyRun && !this.tutorial && !this.menuOpen) {
      this.startCornerBreak();
    } else {
      this.scheduleAIIfNeeded();
    }
    this.refreshHud();
  }

  /** Place the cue pivot + the corner line of balls (shared by the breaker and
   *  any online watchers, so everyone starts from the identical layout). */
  private placeBreakCluster(): void {
    const R = this.ballView.radius;
    const halfW = this.base.halfW;
    const halfL = this.base.halfL;
    const corner = { x: -halfW, y: -halfL }; // the head-left corner the line sits in
    const offTable = 0.45; // ~1.5 ft off the table edge
    this.breakPivot.set(corner.x - offTable, corner.y, 2 * R + 0.006);
    const ids = ballsFor(this.variant);
    const lineY = corner.y + R + 0.03;
    const start = 0.05;
    const step = 2.3 * R;
    ids.forEach((id, i) => {
      const x = Math.min(halfW - R - 0.01, corner.x + start + i * step);
      const b = this.base.get(id);
      b.pos = { x, y: lineY, z: R };
      b.vel = { x: 0, y: 0, z: 0 };
      b.angVel = { x: 0, y: 0, z: 0 };
      b.state = 'stationary';
    });
  }

  /** Online: a non-breaker watches the breaker's swing, rendered from streamed frames. */
  private watchCornerBreak(): void {
    this.placeBreakCluster();
    this.phase = 'watchbreak';
    this.remoteBreak = null;
    this.aiToken++;
    this.aiThinking = false;
    this.message = t('msg.breakWatch');
    this.setCamera('break');
    this.syncBalls();
    this.refreshHud();
  }

  /** Pin the cue in line with the short edge, line the balls up in the corner. */
  private startCornerBreak(): void {
    this.placeBreakCluster();
    this.phase = 'breaking';
    this.breakArmed = false;
    this.breakLocked = false;
    this.leverHasAngle = false;
    this.leverOmega = 0;
    this.breakDisturbed = false;
    this.breakStrokeSpent = false;
    // Rest the shaft along the short edge (heading +X); swinging up into the table
    // (+Y) sweeps the line of balls off the rail.
    this.leverAngle = 0;
    this.leverPrevAngle = this.leverAngle;
    this.aiToken++; // never run an AI shot during the break
    this.aiThinking = false;
    this.message = t('msg.breakSweep');
    this.setCamera('break'); // a sideways plan view, framing the off-table cue
    this.syncBalls();
  }

  /**
   * Treat the shaft as a SOLID swept rod in the bed plane: for each ball, find the
   * nearest point on the cue segment and, if it is penetrating, push it back out
   * and cancel any velocity heading into the shaft (relative to how fast the shaft
   * surface is moving there, ω·r). This both scatters the balls on a swing and
   * stops them ever passing through the cue — call it every physics sub-step.
   */
  private collideStick(dirX: number, dirY: number): void {
    const R = this.ballView.radius;
    const stickR = 0.012; // shaft half-thickness
    const minDist = R + stickR;
    const cap = this.tune.maxShotSpeed;
    const px = this.breakPivot.x, py = this.breakPivot.y;
    const perpX = -dirY, perpY = dirX; // +θ tangential (swing) direction
    for (const id of ballsFor(this.variant)) {
      const b = this.base.get(id);
      const rx = b.pos.x - px, ry = b.pos.y - py;
      const along = Math.max(0, Math.min(STICK_LENGTH, rx * dirX + ry * dirY)); // nearest pt on cue
      const qx = px + dirX * along, qy = py + dirY * along;
      let nx = b.pos.x - qx, ny = b.pos.y - qy;
      let dist = Math.hypot(nx, ny);
      if (dist >= minDist) continue; // not touching the shaft
      if (dist < 1e-6) { nx = perpX; ny = perpY; dist = 1; } // on the axis: pick the swing normal
      nx /= dist; ny /= dist;
      b.pos.x = qx + nx * minDist; // de-penetrate to the shaft surface
      b.pos.y = qy + ny * minDist;
      // velocity of the shaft surface at this point (tangential, ω·r)
      const vsx = -this.leverOmega * along * dirY; // = ω·along·perp
      const vsy = this.leverOmega * along * dirX;
      const vn = (b.vel.x - vsx) * nx + (b.vel.y - vsy) * ny; // closing speed into the shaft
      if (vn < 0) { b.vel = { x: b.vel.x - vn * nx, y: b.vel.y - vn * ny, z: 0 }; } // no pass-through
      const sp = Math.hypot(b.vel.x, b.vel.y);
      if (sp > cap) { b.vel.x *= cap / sp; b.vel.y *= cap / sp; }
      if (b.state === 'stationary') b.state = 'sliding';
      if (sp > 0.15) this.breakDisturbed = true; // a real shove (not an idle touch)
    }
  }

  /** Enable swing mode for the break (any key or click). Mouse motion then swings. */
  private armBreak(): void {
    if (this.phase !== 'breaking' || this.breakArmed) return;
    this.sound.resume();
    this.breakArmed = true;
    this.breakLocked = false;
    this.leverHasAngle = false;
    this.canvas.style.cursor = 'none'; // hide the pointer while swinging the cue
    // Trap the pointer in the window so it can't slip outside while you swing
    // (pointer lock also hides the cursor and feeds raw movement deltas).
    if (this.canvas.requestPointerLock) void this.canvas.requestPointerLock();
    this.message = t('msg.breakSweep');
    this.refreshHud(); // drop the "press to start" banner
  }

  /** Disable swing mode (Esc / any key) but stay in the break — re-arm to resume. */
  private disarmBreak(): void {
    if (!this.breakArmed) return;
    this.breakArmed = false;
    this.breakLocked = false;
    this.leverHasAngle = false;
    this.canvas.style.cursor = '';
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.refreshHud(); // the "press to start" banner returns
  }

  /** The scatter has settled: lock the layout in and hand off to normal aiming. */
  private finishCornerBreak(): void {
    this.phase = 'aiming';
    this.breakArmed = false;
    this.breakLocked = false;
    this.canvas.style.cursor = ''; // restore the pointer for normal aiming
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.cueStick.setVisible(false);
    // The breaker opened the table with the swing — they shoot LAST, so the first
    // scoring turn goes to the next team. (Solo has only one player: a no-op.)
    // The breaker is authoritative here and streams this layout to the room below,
    // so watchers adopt the same active player from the broadcast game state.
    const n = this.match.players.length;
    if (n > 1 && !this.match.config.solo) {
      this.match = { ...this.match, activePlayerIndex: (this.match.activePlayerIndex + 1) % n };
    }
    this.message = t(this.uiMode === 'ai' ? 'msg.aiStart' : 'msg.soloStart', { h: this.handicaps[0] });
    this.syncBalls();
    this.faceObjectBall();
    this.setCamera('aim'); // leave the plan view for the normal shot framing
    this.applyTurnCamera();
    this.scheduleAIIfNeeded();
    // online swing break: send the scattered opening to the rest of the room
    if (this.online && this.net && !this.spectator) {
      this.net.send({ type: 'sync', state: this.serializeGame() });
    }
    this.refreshHud();
  }

  /** Per-frame update during the lever break: swing speed, push, physics, render. */
  private stepCornerBreak(dt: number): void {
    const dirX = Math.cos(this.leverAngle);
    const dirY = Math.sin(this.leverAngle);

    // angular velocity of the swing since last frame (shortest signed delta)
    if (this.breakArmed && this.leverHasAngle && dt > 1e-4) {
      let dTheta = this.leverAngle - this.leverPrevAngle;
      while (dTheta > Math.PI) dTheta -= 2 * Math.PI;
      while (dTheta < -Math.PI) dTheta += 2 * Math.PI;
      this.leverOmega = dTheta / dt;
      this.leverPrevAngle = this.leverAngle;
    } else {
      this.leverOmega = 0;
    }
    // Once the cue has rotated more than 50° up off the rail, the stroke is spent
    // and the cue goes inert (no more hits); up to there it stays live.
    if (this.leverAngle > (50 * Math.PI) / 180) this.breakStrokeSpent = true;

    // advance the live physics in small fixed sub-steps, enforcing the solid cue
    // after each one so a fast ball can never tunnel through the shaft (until the
    // stroke is spent, after which the cue is inert)
    let remaining = dt;
    while (remaining > 1e-6) {
      const h = Math.min(PHYS_DT, remaining);
      const evs = this.base.step(h, 0);
      if (!this.breakStrokeSpent) this.collideStick(dirX, dirY);
      for (const ev of evs) {
        if (ev.kind === 'ball-ball') this.sound.ballClick(this.impactIntensity(ev.speed));
        else if (ev.kind === 'ball-cushion') this.sound.cushion(this.impactIntensity(ev.speed));
      }
      remaining -= h;
    }
    this.syncBalls();

    // show the cue pinned at the corner, shaft swung out along the current heading
    this.aimVisuals.setVisible(false);
    this.cueStick.setVisible(true);
    this.cueStick.poseLever(this.breakPivot, this.scratch.set(dirX, dirY, 0));

    // online: stream the live swing (ball positions + cue angle) to the room (~30 Hz)
    if (this.online && this.net && !this.spectator) {
      const now = performance.now();
      if (now - this.lastBreakSentMs > 33) {
        this.lastBreakSentMs = now;
        const balls = {} as Record<BallId, { x: number; y: number; z: number }>;
        for (const id of ballsFor(this.variant)) {
          const p = this.base.get(id).pos;
          balls[id] = { x: p.x, y: p.y, z: p.z };
        }
        this.net.send({ type: 'breakstream', balls, lever: this.leverAngle });
      }
    }

    // once the line has been scattered and the balls coast to rest, play begins
    if (this.breakDisturbed && this.base.isSettled()) {
      this.finishCornerBreak();
    }
  }

  private syncBalls(): void {
    this.ballView.apply(this.base.frame(0).balls);
    this.needsRender = true; // ball positions changed → draw it
  }

  /** Rebuild the ball meshes + cue stick for a variant (ball size is per-variant). */
  private rebuildBalls(variant: VariantId, colorOverride?: Partial<Record<BallId, number>>): void {
    this.needsRender = true; // new ball/cue meshes → redraw + refresh shadows
    if (this.ballView) {
      this.scene.remove(this.ballView.group);
      this.ballView.dispose();
    }
    if (this.cueStick) this.scene.remove(this.cueStick.group);
    this.hud.setColorOverride(colorOverride ?? {}); // reset for solo/practice; set for an online match
    this.ballView = new BallView(variant, colorOverride);
    this.cueStick = new CueStick(ballRadius(variant));
    const tbl = tableFor(variant);
    this.cueStick.setBounds(tbl.playWidth / 2, tbl.playLength / 2, tbl.cushionHeight + 0.004);
    this.scene.add(this.ballView.group, this.cueStick.group);

    // humanoid figures disabled for now (updateFigures() no-ops on an empty list)
    for (const f of this.figures) this.scene.remove(f.group);
    this.figures = [];
  }

  /** Stand the two figures on the floor at the table edge: the shooter leaning in
   * along the current aim line, the other watching from beside the rail. */
  private updateFigures(): void {
    if (!this.figures.length) return;
    const active = this.match.activePlayerIndex;
    const other = active === 0 ? 1 : 0;
    const tbl = tableFor(this.variant);
    // outer table footprint (cushion + rail + wooden frame) the player stands past
    const outerW = tbl.playWidth / 2 + tbl.railWidth + 0.07;
    const outerL = tbl.playLength / 2 + tbl.railWidth + 0.07;
    const idleX = outerW + 0.2; // just off the long rail

    // solo practice has no opponent — don't stand a phantom figure at the rail
    if (this.match.config.solo) this.figures[other].setVisible(false);
    else this.figures[other].poseIdle(idleX, outerL * 0.45, -Math.PI / 2, FLOOR_Z);

    if (this.phase === 'aiming') {
      let az = this.azimuth;
      let el = this.elevation;
      if (!this.myTurn && this.online && this.remoteAim) {
        az = this.remoteAim.azimuth;
        el = this.remoteAim.elevation;
      }
      // march back from the cue ball along the aim line to the table edge, then a
      // little further, and stand the shooter on the floor just LEFT of the cue
      // (the cue runs along their right side, like a right-handed stance)
      const cue = this.cueBallPos();
      const cax = Math.cos(az);
      const say = Math.sin(az);
      const t = edgeDistance(cue.x, cue.y, -cax, -say, outerW, outerL);
      const sideOff = 0.16; // step to the left of the cue line
      const fx = cue.x - cax * (t + 0.18) + -say * sideOff;
      const fy = cue.y - say * (t + 0.18) + cax * sideOff;
      // while chalking the shooter steps back and stands upright (cue is up in hand)
      if (this.cueStick.isChalking()) this.figures[active].poseIdle(fx, fy, az, FLOOR_Z);
      else this.figures[active].poseShooting(fx, fy, az, el, FLOOR_Z);
    } else {
      // between shots: stand the shooter up beside the rail too
      this.figures[active].poseIdle(idleX, -outerL * 0.45, -Math.PI / 2, FLOOR_Z);
    }

    // You never see your own body — only the opponent does. Hide the figure that
    // represents the local player (your online seat, or seat 0 when offline).
    // Spectators aren't a player, so they keep both figures visible.
    const mySeat = this.spectator ? -1 : this.online ? this.seat % 2 : 0;
    if (mySeat >= 0) this.figures[mySeat].setVisible(false);
  }

  // ---- online play ----

  private get myTurn(): boolean {
    if (!this.online) return !this.activeIsAI;
    if (this.spectator) return false;
    // Waiting-room practice + post-game "try this layout": anyone may shoot the
    // shared table (the phase lock while a shot simulates keeps it to one shooter).
    if (this.waiting || this.shotPractice) return true;
    if (!this.gameActive || !this.opponentPresent) return false;
    // The active team is the engine's active index; within it the shooting partner
    // is tracked by the engine (partnerTurn) so every client agrees. Team count
    // comes from the match itself (robust if `format` is stale after a reconnect).
    const teams = this.match.players.length;
    const myTeam = this.seat % teams;
    return myTeam === this.match.activePlayerIndex && this.seat === this.activePartnerSeat();
  }

  /** Seat of the partner whose turn it is on the active team (2v2 alternation). */
  private activePartnerSeat(): number {
    const teams = this.match.players.length;
    const activeTeam = this.match.activePlayerIndex;
    const partnerTurn = this.match.players[activeTeam]?.partnerTurn ?? 0;
    // seats on a team, in order, are activeTeam, activeTeam+teams, activeTeam+2*teams…
    return activeTeam + partnerTurn * teams;
  }

  private setNickname(name: string): void {
    this.nickname = name.slice(0, 24);
    localStorage.setItem('carom.nick', this.nickname);
    this.presencePanel?.setMyName(this.myDisplayName());
    this.presenceStatus = null; // name changed → re-announce
    this.refreshPresence();
  }

  /** Name we show/compare as "you" elsewhere in the UI: the account name once
   *  logged in (mirrors identity()/the server's authoritative name), else the
   *  local nickname. */
  private myDisplayName(): string {
    return (this.account ?? this.nickname) || 'Guest';
  }

  /** Save the profile avatar and refresh its thumbnail everywhere it shows. */
  private setAvatar(cfg: AvatarConfig): void {
    this.avatar = cfg;
    localStorage.setItem('carom.avatar', serializeAvatar(cfg));
    this.lobby.setAvatar(cfg);
    this.hud.setAvatar(cfg);
  }

  /** Who we are on the wire. A logged-in nickname overrides the local nickname and
   *  is verified server-side; anonymous = local nickname (empty → server shows "Guest"). */
  private identity(): Identity {
    // Online games always play at your ladder tier (there's no free-handicap mode).
    const level = this.myTier ?? this.handicaps[0];
    const avatar = serializeAvatar(this.avatar);
    const id = this.accountId ?? undefined;
    return { name: this.account ?? this.nickname, id, level, avatar };
  }

  /** Log out: drop the session locally and on the server, back to anonymous. */
  private netLogout(): void {
    this.net?.send({ type: 'logout', token: this.token ?? undefined });
    this.account = null;
    this.accountId = null;
    this.guest = true;
    this.token = null;
    localStorage.removeItem('carom.token');
    // A NEW anonymous identity, not the one from before signing in: keeping the
    // old handle would leave the room watching the same person walk back in, and
    // the next social sign-in would claim a guest still wearing that name.
    this.setNickname(randomDisplayName(getLang()));
    this.lobby.setAuth(null, true);
    this.lobby.setNickname(this.nickname);
    this.adminPanel.setAdmin(false);
    // back to anonymous → mint a fresh guest session on both sockets
    this.establishSession(this.presence);
    this.establishSession(this.net);
  }

  /**
   * Online match finished: feed the result to the ranking ladder AND settle any
   * 코인 stake on the room. Only logged-in accounts rank, and only the WINNER
   * reports (the server also guards this) so a match counts exactly once.
   * Ratings + innings-based 코인 are 1v1 only, but a stake can be riding on any
   * format (2v2, 1v1v1, …) — so this must still fire for those, just without
   * `perf` (the server skips the 1v1-only reward/rating math when it's absent).
   */
  private reportMatchResult(): void {
    if (!this.accountId) return;
    const w = this.match.winnerIndex;
    if (w == null) return;
    const players = this.match.players;
    if (players.length < 2) return;
    const winnerId = this.playerIds[w];
    if (!winnerId || winnerId !== this.accountId) return; // only the winner reports → no double count
    // Any other seated player stands in as "the loser" for the report — the
    // server only uses loserId for its anti-abuse guard and (1v1 only) Elo;
    // stake settlement pays every seat NOT on the winner's team regardless.
    const loserIdx = players.findIndex((_, i) => i !== w && this.playerIds[i]);
    if (loserIdx < 0) return;
    const loserId = this.playerIds[loserIdx];
    if (!loserId || loserId === winnerId) return;
    // caroms + innings drive the 애버리지 tier engine — only meaningful 1v1.
    // `innings` only bumps on a miss, so add the inning the game ended in (the
    // winner's run-out) — 애버리지 counts it.
    const stat = (i: number) => ({
      caroms: Math.max(0, players[i].score),
      innings: players[i].innings + (players[i].currentRun > 0 ? 1 : 0),
    });
    this.net?.send({
      type: 'reportResult',
      winnerId,
      loserId,
      winner: players[w].name,
      loser: players[loserIdx].name,
      perf: players.length === 2 ? { winner: stat(w), loser: stat(loserIdx) } : undefined,
    });
  }

  /**
   * Walking away from a 오늘의 배치 attempt. Anything already struck is REPORTED
   * rather than discarded — otherwise bailing on a bad start and re-entering
   * would be a free reroll that never costs an attempt.
   */
  private abandonDailyRun(): void {
    if (!this.dailyRun) return;
    if (this.dailyRun.strokes > 0) this.finishDailyRun(false);
    if (this.dailyCountdown) {
      clearTimeout(this.dailyCountdown);
      this.dailyCountdown = null;
    }
    if (this.dailyTick) {
      clearInterval(this.dailyTick);
      this.dailyTick = null;
    }
    this.dailyRun = null;
  }

  /** Start a single-player game at the chosen level; remember the choice. */
  private startSingle(mode: 'solo' | 'ai', level: number): void {
    this.abandonDailyRun();
    this.endTutorialSession();
    this.handicaps = [level, level];
    localStorage.setItem('carom.level', String(level));
    localStorage.setItem('carom.mode', mode);
    localStorage.setItem('carom.finished', '0'); // a fresh game is now in progress
    this.assessing = false;
    this.menuOpen = false;
    this.mainMenu.hide();
    this.reviewPanel.dismiss(); // starting a fresh single game → close any review panel
    this.setMode(mode); // sets uiMode + (re)builds the table for the chosen mode
  }

  /** A small two-choice modal (used for the opponent-left win prompt). */
  private buildPrompt(root: HTMLElement): void {
    const overlay = document.createElement('div');
    overlay.className = 'app-prompt-overlay';
    const panel = document.createElement('div');
    panel.className = 'app-prompt interactive';
    this.promptMsg = document.createElement('div');
    this.promptMsg.className = 'app-prompt-msg';
    const btns = document.createElement('div');
    btns.className = 'app-prompt-btns';
    this.promptA = document.createElement('button');
    this.promptB = document.createElement('button');
    btns.append(this.promptA, this.promptB);
    panel.append(this.promptMsg, btns);
    overlay.append(panel);
    overlay.style.display = 'none';
    root.appendChild(overlay);
    this.promptOverlay = overlay;
  }

  private showPrompt(message: string, aLabel: string, onA: () => void, bLabel: string, onB: () => void): void {
    this.promptMsg.innerHTML = message;
    this.promptA.textContent = aLabel;
    this.promptB.textContent = bLabel;
    this.promptA.onclick = () => { this.hidePrompt(); onA(); };
    this.promptB.onclick = () => { this.hidePrompt(); onB(); };
    this.promptOverlay.style.display = 'flex';
  }

  private hidePrompt(): void {
    this.promptOverlay.style.display = 'none';
  }

  /**
   * Forget everything that ties this client to a room. Every path out of online
   * play funnels through here.
   *
   * This exists because each exit used to clear its own ad-hoc subset, and what
   * they missed — the seat, the room code, the player-id list, queued remote
   * shots — survived into the next session. Stale seat + stale room is exactly
   * how a player who had left could be treated as still seated, and how a shot
   * meant for someone else got applied locally. If you add room state, clear it
   * HERE rather than in one caller.
   *
   * `pendingWatch` is deliberately NOT cleared: it's set immediately before a
   * mode switch to say which room to enter next, so wiping it here would cancel
   * the very join that's in flight.
   */
  /**
   * Tell the server we're leaving, then drop the socket. The leave must actually
   * reach the server: without it the close reads as a mid-game DROP, so the seat
   * is held for the grace window and the room still counts us as a player.
   */
  private leaveAndClose(): void {
    this.net?.close({ type: 'leaveRoom' });
    this.net = null;
  }

  private resetRoomState(): void {
    this.roomCode = '';
    this.seat = 0;
    this.spectator = false;
    this.gameActive = false;
    this.waiting = false;
    this.waitingVariant = null;
    this.opponentPresent = false;
    this.shotPractice = false;
    this.format = '1v1';
    // pending/queued traffic from the room we just left
    this.pendingGameStart = null;
    this.pendingState = null;
    this.pendingRemote = null;
    this.remoteQueue = [];
    this.lastGameSeq = 0;
    this.turnDeadlineMs = 0;
    this.turnTickSecond = -1;
    this.remoteAim = null;
    this.oppStrike = null;
    this.spectatorReadyForNext = false;
    this.specRoomAlreadyClosed = false;
    // per-room identity of the other players
    this.playerIds = [];
    this.playerNames = [];
    this.playerLevels = [];
    this.seatStats = [];
    this.h2h.clear();
    // shared table furniture
    this.pointing = false;
    this.tablePointers.clearAll();
  }

  /** Opponent left mid-game: keep the board and play on solo (disconnect the room). */
  private continueSolo(): void {
    this.intentionalClose = true;
    this.clearRoomResume(); // explicit disconnect — nothing to resume next load
    this.leaveAndClose();
    this.uiMode = 'solo'; // online getter now false → myTurn uses the solo path
    this.resetRoomState();
    this.voice.dispose();
    this.voicePanel.hide();
    this.chat.setRoomChat(false);
    this.lobby.hide();
    this.music.setOnline(false);
    if (this.phase !== 'aiming') this.phase = 'aiming';
    this.message = '';
    this.faceObjectBall();
    this.applyTurnCamera();
    this.refreshHud();
  }

  /** Open online play. Your handicap online is your ladder tier (from the session,
   *  see identity()), so there's no level to pick here. */
  private openOnline(): void {
    localStorage.setItem('carom.mode', 'online');
    this.menuOpen = false;
    this.mainMenu.hide();
    this.setMode('online');
  }

  /** The main menu's "+ 방 만들기" button (inside the rooms screen behind 온라인
   *  대전): open online play straight onto the create-room form. */
  private openCreateRoom(): void {
    this.openOnline();
    this.lobby.showCreate();
  }

  /**
   * Spectate a live match from the online-players list (the row's Watch button).
   * If already online we can join straight away; otherwise switch to online mode
   * first and join once the socket is up (see the online onOpen in setMode).
   */
  private watchRoom(code: string): void {
    if (!code) return;
    // defensive: the Watch buttons are hidden while you're in your own room, but
    // never spectate another match from inside one (it would abandon/break it).
    if (this.roomCode && !this.spectator) return;
    if (this.online && this.net?.connected) {
      this.net.send({ type: 'joinRoom', code, identity: this.identity() });
      return;
    }
    this.pendingWatch = code;
    this.menuOpen = false;
    this.mainMenu.hide();
    this.setMode('online');
  }

  /** Apply + remember a table theme. In a room it's admin-only room config, so the
   *  host's pick syncs to everyone (the server ignores non-host config). */
  private applyTheme(id: ThemeId): void {
    this.theme = id;
    localStorage.setItem('carom.theme', id);
    this.hud.setTheme(id);
    this.rebuildScenery();
    this.broadcastConfig();
  }

  /** Set the background scene + reflect it in both pickers, and (when hosting an
   *  online room) sync it to everyone. The server ignores config from non-hosts,
   *  so this is admin-only online and a free local preference offline. */
  private applyEnv(id: EnvId): void {
    this.env = id;
    localStorage.setItem('carom.env', id);
    this.hud.setEnv(id);
    this.applyEnvironment();
    this.broadcastConfig();
  }

  /**
   * Exit-to-menu button: confirm before abandoning an in-progress game. Leaving a
   * live online match forfeits it, so warn that it costs rank points; single play
   * and the menus just confirm (or skip the prompt when nothing is at stake).
   */
  private requestExitToMenu(): void {
    const inProgress = !this.menuOpen && this.phase !== 'finished' && this.phase !== 'assessed';
    if (!inProgress || this.spectator) {
      this.openMainMenu(); // nothing at stake (or just watching) → leave straight away
      return;
    }
    const online = this.online && this.gameActive && !this.spectator;
    const msg = online ? t('exit.onlineWarn') : t('exit.confirm');
    this.showPrompt(msg, t('exit.leave'), () => this.openMainMenu(), t('exit.stay'), () => {});
  }

  /** Reopen the start menu (from the HUD), leaving any online room first. */
  private openMainMenu(): void {
    this.abandonDailyRun(); // a 이번주의 매치 attempt must not leak into the next game
    this.endTutorialSession(); // ditto a lesson — the menu is always a clean exit
    this.menuOpen = true;
    this.reviewPanel.dismiss(); // close the shot-review / practice panels on the way out
    this.historyPanel.hide();
    if (this.online) this.setMode('solo'); // disconnect cleanly; builds a quiet table
    this.mainMenu.setProfile(this.account ?? this.nickname, this.myProfile, !this.account);
    this.mainMenu.show();
    (this.presence ?? this.net)?.send({ type: 'daily' }); // row 3's mission card + its own board
    this.refreshHud();
  }

  private setMode(mode: UiMode): void {
    this.persistGameLog(); // flush any in-progress recorded session before switching modes
    if (this.online && mode !== 'online') {
      this.intentionalClose = true;
      this.clearRoomResume(); // explicit exit — nothing to resume next load
      this.leaveAndClose();
      this.resetRoomState();
      this.lobby.hide();
      this.chat.setRoomChat(false);
      this.voice.dispose();
      this.voicePanel.hide();
    }
    this.uiMode = mode;
    this.music.setOnline(mode === 'online');
    this.voicePanel.hide(); // voice is shown once a game starts, not in the browser
    if (mode === 'online') {
      this.lobby.setStatus(t('lobby.intro'));
      this.lobby.showBrowser();
      this.message = t('msg.onlineMode');
      this.refreshHud();
      this.reconnectAttempts = 0;
      this.connectNet(() => {
        this.establishSession(this.net); // auth this socket with our session (guest or registered)
        this.sendList();
        if (this.pendingWatch) {
          this.net?.send({ type: 'joinRoom', code: this.pendingWatch, identity: this.identity() });
          this.pendingWatch = null;
        }
        if (this.pendingRematch) {
          this.net?.send({ type: 'rematch', to: this.pendingRematch, variant: this.variant, identity: this.identity() });
          this.pendingRematch = null;
        }
      });
    } else {
      this.buildForVariant(this.variant);
    }
    this.refreshPresence(); // mode changed → update the online-players status
  }

  /** Remember which seat we're playing so a page refresh can rejoin it (see
   *  tryResumeRoom). Cleared wherever the player explicitly leaves/exits. */
  private saveRoomResume(code: string, seat: number): void {
    localStorage.setItem('carom.room', JSON.stringify({ code, seat }));
  }

  private clearRoomResume(): void {
    localStorage.removeItem('carom.room');
  }

  /** Boot-time: if we were mid-game in a room when the page last unloaded (and
   *  didn't explicitly leave), reconnect straight into it instead of asking for
   *  a mode. Returns true if a resume attempt was started. */
  private tryResumeRoom(): boolean {
    const raw = localStorage.getItem('carom.room');
    if (!raw) return false;
    let saved: { code: string; seat: number } | null = null;
    try { saved = JSON.parse(raw); } catch { /* ignore malformed */ }
    if (!saved || !saved.code || saved.seat == null) { this.clearRoomResume(); return false; }
    this.uiMode = 'online';
    this.roomCode = saved.code;
    this.seat = saved.seat;
    this.reconnectAttempts = 0;
    this.message = t('lobby.connecting');
    this.lobby.setStatus(t('lobby.connecting'));
    this.music.setOnline(true);
    this.connectNet(() => {
      this.establishSession(this.net);
      this.net?.send({ type: 'rejoin', code: saved!.code, seat: saved!.seat });
    });
    return true;
  }

  /** Request the open-room list and arm a watchdog: if the server doesn't answer
   *  (e.g. a stale dev server left running from before the lobby existed), say so. */
  private sendList(): void {
    this.net?.send({ type: 'list' });
    if (this.listTimer) clearTimeout(this.listTimer);
    this.listTimer = setTimeout(() => {
      this.listTimer = null;
      this.lobby.setStatus(t('lobby.noResponse'));
    }, 3500);
  }

  /** Create a room with the host's chosen name/format/variant/opening/stake. */
  private netCreateRoom(name: string, format: GameFormat, variant: VariantId, breakStyle: BreakStyle, stake: number): void {
    const send = () => {
      this.net?.send({ type: 'createRoom', name, format, variant, identity: this.identity() });
      // immediately share our room config (table/physics + finish rule) so the
      // server stores it and every joiner adopts it from the lobby snapshot.
      this.net?.send({ type: 'config', config: this.roomConfig() });
      // opening style and stake aren't part of RoomConfig — they're room fields
      // the (now newly-hosted) room accepts from us as its creator.
      this.net?.send({ type: 'setBreak', style: breakStyle });
      this.net?.send({ type: 'setStake', amount: stake });
    };
    if (this.net?.connected) send();
    else this.connectNet(send);
  }

  /** Leave the current room and return to the room browser. */
  private netLeaveRoom(): void {
    this.clearRoomResume(); // explicit exit — nothing to resume next load
    this.hidePrompt();
    this.pendingGameStart = null;
    this.pendingState = null;
    this.spectatorReadyForNext = false;
    this.specRoomAlreadyClosed = false;
    this.net?.send({ type: 'leaveRoom' });
    this.opponentPresent = false;
    this.spectator = false;
    this.waiting = false;
    this.gameActive = false;
    this.waitingVariant = null;
    this.pointing = false;
    this.tablePointers.clearAll();
    this.voice.dispose();
    this.voicePanel.hide();
    this.chat.setRoomChat(false);
    this.lobby.showBrowser();
    this.refreshHud();
  }

  /** An exhibition match just ended while spectating: announce the winner and let
   *  the viewer choose to keep watching (the room's next bot pairing arrives a few
   *  seconds later, held in `pendingGameStart`) or exit rather than being swapped
   *  into a new match automatically. */
  private announceSpecMatchEnd(): void {
    const w = this.match.winnerIndex;
    const name = w != null ? this.match.players[w]?.name : null;
    const msg = name ? t('prompt.specMatchEnded', { name }) : t('prompt.specMatchEndedDraw');
    this.showPrompt(msg, t('prompt.watchNext'), () => this.watchNextMatch(), t('prompt.exitSpectate'), () => this.netLeaveRoom());
  }

  /** The spectator chose to keep watching after an exhibition match ended: adopt
   *  the next pairing now if it already arrived (both 'gameStart' and the 'state'
   *  snapshot that follows it — see sendSpectatorStart — are held together), else
   *  just mark us ready so the next arrival applies immediately. */
  private watchNextMatch(): void {
    // The server already told us this exhibition room is gone (it opens a brand-new
    // room, not a rematch in this one) — there's no next pairing coming to wait for,
    // so "watch next" can only mean "back to the room list" too.
    if (this.specRoomAlreadyClosed && !this.pendingGameStart) {
      this.netLeaveRoom();
      return;
    }
    this.spectatorReadyForNext = true;
    if (this.pendingGameStart) {
      const nextStart = this.pendingGameStart;
      const nextState = this.pendingState;
      this.pendingGameStart = null;
      this.pendingState = null;
      this.spectatorReadyForNext = false;
      this.handleGameStart(nextStart);
      if (nextState) this.loadGame(nextState);
    }
  }

  /** A local music control — broadcast it to the room so everyone stays in sync. */
  private onMusicAction(a: MusicAction): void {
    if (this.online) this.net?.send({ type: 'music', ...a });
  }

  /** Open (and keep open) the dedicated presence socket. Independent of the room
   *  connection: it just announces what this player is doing and renders the list. */
  private connectPresence(): void {
    const net = new NetClient(defaultServerUrl(), {
      onMessage: (m) => {
        if (m.type === 'presence') {
          this.presencePanel.setUsers(m.users);
          // main-menu row 5's headcount: no joinable session exists for solo/vs-AI
          // practice or a 당구 교실 lesson, so a live count is all there is to show
          const practicing = m.users.filter((u) => u.status === 'solo' || u.status === 'ai').length;
          const tutorial = m.users.filter((u) => u.status === 'tutorial').length;
          this.mainMenu.setActivity(practicing, tutorial);
        }
        else if (m.type === 'dm') this.chat.addDm(m);
        else if (m.type === 'dmError') this.onDmError(m.to, m.reason);
        else if (m.type === 'myProfile') this.onMyProfile(m.profile);
        else if (m.type === 'authed') this.applyAuthed(m);
        else if (m.type === 'gameHistory') this.onGameHistory(m.games);
        else if (m.type === 'gameDetail') this.onGameDetail(m.game);
        else if (m.type === 'store') this.onStoreState(m.coins, m.owned);
        else if (m.type === 'purchase') this.onPurchaseResult(m);
        else if (m.type === 'equip') this.onEquipResult(m);
        else if (m.type === 'rename') this.onRenameResult(m);
        else if (m.type === 'earned') this.onEarned(m.events, m.coins);
        else if (m.type === 'daily' || m.type === 'dailyResult') this.onDaily(m);
        else if (m.type === 'tutorialContent') this.onTutorialContent(m);
        else if (m.type === 'feedbackCreated') this.feedbackPanel.onSent();
        else if (m.type === 'feedbackError') this.feedbackPanel.onError();
        else if (m.type === 'leaderboard') this.lobby.setLeaderboard(m.entries);
        else if (m.type === 'rematchInvite') this.onRematchInvite(m.from, m.code);
        else if (m.type === 'rooms') this.mainMenu.setRooms(m.rooms); // main-menu row 5
        else if (m.type === 'authError') {
          // expired/invalid saved token → drop it and mint a fresh guest
          this.token = null;
          localStorage.removeItem('carom.token');
          this.establishSession(this.presence);
        }
      },
      onOpen: () => {
        // every visit gets a persistent session: resume the saved token, else mint
        // a guest account. The always-on presence socket is the session anchor.
        this.establishSession(this.presence);
        this.presenceStatus = null; // force a (re)send of the current status
        this.refreshPresence();
        // Main-menu row 3 (이번주의 매치 + its own board) needs data even on first
        // paint. `this.net` doesn't exist yet at boot (it's only created once the
        // online lobby connects), so this goes over the always-on presence socket,
        // and only from here — sending it right after `new NetClient()` in the
        // constructor drops silently because the handshake hasn't finished.
        this.presence?.send({ type: 'daily' });
        this.presence?.send({ type: 'tutorialContent' });
        // Main-menu row 5's room list, same reasoning: needs data even on first
        // paint, and this socket then keeps getting fresh 'rooms' pushes for free
        // (the server re-broadcasts to every socket that has ever asked for the list).
        this.presence?.send({ type: 'list' });
      },
      onClose: () => {
        this.presence = null;
        if (this.presenceTimer) return; // already scheduled
        this.presenceTimer = setTimeout(() => {
          this.presenceTimer = null;
          this.connectPresence();
        }, 3000);
      },
    });
    this.presence = net;
    net.connect();
  }

  /**
   * Send a private message (귓속말). It goes over the presence socket, which is
   * open for the whole session regardless of where the player is, so DMs work in
   * the menus, the room browser and mid-game alike. The line itself is rendered
   * when the server echoes it back — so nothing appears unless it really went.
   */
  private sendWhisper(to: string, text: string): void {
    const socket = this.presence?.connected ? this.presence : this.net?.connected ? this.net : null;
    if (!socket) {
      this.chat.systemDm(to, t('chat.disconnected'));
      return;
    }
    socket.send({ type: 'whisper', to, text });
  }

  /** The server couldn't deliver a whisper — say so inside that conversation. */
  private onDmError(to: string, reason: 'offline' | 'flood'): void {
    this.chat.systemDm(to, reason === 'flood' ? t('chat.flood') : t('chat.offline', { name: to }));
  }

  /** Resume the saved session token, or mint a guest account if there's none. */
  private establishSession(socket: NetClient | null): void {
    if (!socket) return;
    if (this.token) socket.send({ type: 'auth', token: this.token });
    else socket.send({ type: 'guest', name: this.nickname || undefined });
  }

  /** What this player is currently doing, for the online-players list. */
  private computePresence(): PresenceStatus {
    if (this.online) {
      if (this.spectator) return 'spectating';
      if (this.gameActive) return `playing-${this.format}` as PresenceStatus;
      if (this.roomCode) return 'waiting'; // in a waiting room
      return 'standby'; // browsing the room list
    }
    if (this.menuOpen) return 'standby';
    if (this.tutorial) return 'tutorial'; // a lesson runs inside 'solo' mode locally
    if (this.uiMode === 'ai') return 'ai';
    if (this.uiMode === 'solo') return 'solo';
    return 'standby';
  }

  /** Recompute + push our status (deduped: only sends when it actually changes). */
  private refreshPresence(): void {
    const status = this.computePresence();
    // hide the Watch buttons while we're in a game of our own (only browsing or
    // already spectating may switch to watch another match)
    this.presencePanel?.setInGame(status !== 'standby' && status !== 'spectating');
    // include our room code while in a live match so others can Watch us
    const room = this.online && this.gameActive && this.roomCode ? this.roomCode : '';
    const key = `${status}|${room}`;
    if (key === this.presenceStatus && this.presence?.connected) return;
    this.presenceStatus = key;
    this.presence?.send({ type: 'presence', name: this.myDisplayName(), status, room: room || undefined });
  }

  private connectNet(onOpen: () => void): void {
    this.intentionalClose = false;
    this.net?.close();
    this.lobby.setStatus(t('lobby.connecting'));
    this.net = new NetClient(defaultServerUrl(), {
      onMessage: (m) => this.onNetMessage(m),
      onOpen,
      onClose: () => this.onNetClose(),
      onError: () => this.lobby.setStatus(t('lobby.connectFail')),
    });
    this.net.connect();
  }

  /** Unexpected socket close: a player tries to reconnect into the held seat. */
  private onNetClose(): void {
    if (this.intentionalClose || !this.online) return;
    if (this.spectator || !this.roomCode || this.reconnectAttempts >= 5) {
      this.handleRoomClosed(t('msg.disconnected'));
      return;
    }
    this.reconnectAttempts++;
    this.opponentPresent = false;
    this.voice.peerGone(); // signaling channel is gone — drop the stale audio link
    this.message = t('msg.reconnecting', { n: this.reconnectAttempts });
    this.refreshHud();
    const code = this.roomCode;
    const seat = this.seat;
    setTimeout(() => {
      if (this.intentionalClose || !this.online) return;
      this.connectNet(() => this.net?.send({ type: 'rejoin', code, seat }));
    }, 600 * this.reconnectAttempts);
  }

  private handleRoomClosed(reason: string): void {
    this.clearRoomResume(); // terminal (kicked / closed / gave up reconnecting) — nothing left to resume
    this.hidePrompt();
    this.resetRoomState();
    this.voice.dispose();
    this.voicePanel.hide();
    this.chat.setRoomChat(false);
    this.message = reason;
    this.lobby.setStatus(reason);
    this.lobby.showBrowser();
    // back to the browser: refresh the room list (reconnect if the socket dropped)
    if (this.net?.connected) this.sendList();
    else if (this.online) this.connectNet(() => this.sendList());
    this.refreshHud();
  }

  private serializeGame(): SerializedGame {
    const positions = {} as SerializedGame['positions'];
    for (const id of ballsFor(this.variant)) {
      const p = this.base.get(id).pos;
      positions[id] = { x: p.x, y: p.y };
    }
    return { variant: this.variant, match: this.match, positions };
  }

  private loadGame(state: SerializedGame): void {
    this.variant = state.variant;
    this.base = createCaromWorld(state.variant);
    this.rebuildBalls(state.variant);
    this.rebuildScenery();
    this.cameraRig.setTable(tableFor(state.variant));
    for (const id of ballsFor(state.variant)) {
      const b = this.base.get(id);
      b.pos = { x: state.positions[id].x, y: state.positions[id].y, z: b.radius };
      b.vel = { x: 0, y: 0, z: 0 };
      b.angVel = { x: 0, y: 0, z: 0 };
      b.state = 'stationary';
    }
    this.match = state.match;
    this.opponentPresent = true;
    // A spectator can join right as (or just after) a match ends — the state
    // snapshot arrives already 'finished', which finalizeShot's phase-flip never
    // sees since no shot plays out locally. Announce it here too, or the winner
    // dialog silently never appears and a later room-closed has nothing to defer to.
    if (state.match.phase === 'finished' && this.spectator) {
      this.phase = 'finished';
      this.cameraRig.setMode('orbit');
      this.announceSpecMatchEnd();
    } else {
      this.phase = 'aiming';
    }
    this.currentShot = null;
    this.oppStrike = null; // cancel any in-flight opponent stroke telegraph
    this.tablePointers.clearAll(); // clear stale ping arrows across a resync
    this.cushionPops.clear(); // and any rail-count pops from the interrupted shot
    // re-derive which places have been announced from the synced match (avoid
    // re-announcing on reconnect, but still announce any that happen after)
    this.announcedPlaces = new Set(state.match.players.map((p, i) => (p.place != null ? i : -1)).filter((i) => i >= 0));
    this.lastShot = null;
    this.replaying = false;
    this.pendingRemote = null;
    this.remoteQueue = []; // drop any queued shots from before the resync
    this.lobby.hide();
    this.message = this.spectator ? t('msg.spectating') : t('msg.reconnected');
    this.syncBalls();
    this.faceObjectBall();
    this.applyTurnCamera();
    this.refreshHud();
  }

  /** Adopt a session (guest/auth/signup/login) from any socket: store the token +
   *  identity and reflect it in the UI. */
  private applyAuthed(m: {
    token: string; id: string; guest: boolean; name: string; admin?: boolean;
    coins: number; features: string[]; providers?: AuthProvider[]; cueColor?: string | null;
  }): void {
    this.account = m.guest ? null : m.name; // registered nickname; null for guests
    this.accountId = m.id;
    this.guest = m.guest;
    this.token = m.token;
    localStorage.setItem('carom.token', m.token);
    this.lobby.setAuth(this.account, m.guest);
    this.lobby.setMyId(m.id);
    if (!m.guest && this.authModal.isOpen) this.authModal.onSuccess();
    this.presencePanel?.setMyName(this.myDisplayName()); // reflect account name once signed in
    this.presenceStatus = null; // identity changed → re-announce so others see the account name
    this.refreshPresence();
    this.adminPanel.setAdmin(!!m.admin);
    this.features = new Set(m.features);
    this.coins = m.coins;
    this.syncCoins();
    this.myCueColor = m.cueColor ?? null;
    this.mainMenu.setStoreEnabled(this.features.has('itemStore'));
    this.lobby.setStoreEnabled(this.features.has('itemStore'));
    // Only offer the social buttons the server actually has credentials for.
    if (m.providers) {
      this.lobby.setProviders(m.providers);
      this.mainMenu.setProviders(m.providers);
    }
    this.presence?.send({ type: 'myProfile' }); // refresh the scoreboard profile card
    this.presence?.send({ type: 'store' }); // refresh balance + owned items
  }

  /**
   * Google / Kakao sign-in. The provider flow runs in a popup so the game and
   * its socket survive it; the popup returns a session token the SERVER minted
   * after verifying the provider's id_token, which we then resume like any
   * other saved session.
   */
  private async socialLogin(provider: string): Promise<void> {
    const res = await signInWith(provider, this.token);
    if (res.error) {
      this.lobby.setAuthMessage(t('lobby.socialFailed', { msg: res.error }));
      return;
    }
    if (!res.token) return; // cancelled — the user closed the popup, say nothing
    this.token = res.token;
    localStorage.setItem('carom.token', res.token);
    // Re-auth on BOTH sockets: the room socket drives the lobby UI, and the
    // presence socket carries the identity everyone else sees.
    (this.net ?? this.presence)?.send({ type: 'auth', token: res.token });
    if (this.presence && this.presence !== this.net) this.presence.send({ type: 'auth', token: res.token });
    this.lobby.setAuthMessage(
      res.linked === 'merged' ? t('lobby.socialLinked') : res.linked === 'claimed' ? t('lobby.socialClaimed') : ''
    );
  }

  /** Pull a fresh profile card (rank/level/elo/win-rate) for the scoreboard. */
  private requestMyProfile(): void {
    (this.net ?? this.presence)?.send({ type: 'myProfile' });
  }

  private onNetMessage(m: ServerMessage): void {
    // Defense in depth against a message that was already in flight from the
    // server when we tore down the room socket (e.g. leaving for solo mode
    // right as an exhibition room's next pairing started): once we're no
    // longer online, nothing from the old socket should be able to mutate
    // state and yank us back into a match.
    if (!this.online) return;
    switch (m.type) {
      case 'authed':
        this.applyAuthed(m);
        break;
      case 'admin':
        this.adminPanel.setSnapshot(m.snapshot);
        break;
      case 'playerHistory':
        this.adminPanel.setProfile(m.profile);
        break;
      case 'authError':
        // a failed login/signup, or an expired token. Drop the bad token; the
        // presence socket re-establishes a guest session (see connectPresence).
        this.token = null;
        localStorage.removeItem('carom.token');
        if (this.authSource === 'modal' && this.authModal.isOpen) this.authModal.setError(m.message);
        else this.lobby.setAuthError(m.message);
        if (!this.accountId) this.establishSession(this.net); // expired token on connect → get a guest
        break;
      case 'leaderboard':
        this.lobby.setLeaderboard(m.entries);
        break;
      case 'myProfile':
        this.onMyProfile(m.profile);
        break;
      case 'playersStats':
        this.seatStats = m.stats;
        this.requestHeadToHead(); // rivalry record for the same seats
        this.refreshHud();
        break;
      case 'headToHead':
        this.h2h.clear();
        m.ids.forEach((id, i) => {
          const rec = m.records[i];
          if (id && rec) this.h2h.set(id, rec);
        });
        this.refreshHud();
        break;
      case 'earned':
        this.onEarned(m.events, m.coins);
        break;
      case 'stakeResult':
        this.message = t(m.won ? 'msg.stakeWin' : 'msg.stakeLoss', { n: String(m.amount) });
        this.refreshHud();
        break;
      case 'daily':
      case 'dailyResult':
        this.onDaily(m);
        break;
      case 'rematchInvite':
        this.onRematchInvite(m.from, m.code);
        break;
      case 'rooms':
        if (this.listTimer) {
          clearTimeout(this.listTimer); // server answered → all good
          this.listTimer = null;
        }
        this.lobby.setStatus('');
        this.lobby.setRooms(m.rooms);
        break;
      case 'lobby':
        this.handleLobby(m.state);
        break;
      case 'chat':
        this.chat.addLine(m.line, !m.line.system && m.line.name === this.myDisplayName());
        break;
      // whispers normally ride the presence socket; they can land here when that
      // socket was down and the room one carried the message instead
      case 'dm':
        this.chat.addDm(m);
        break;
      case 'dmError':
        this.onDmError(m.to, m.reason);
        break;
      case 'emote':
        this.playEmote(m.emote, m.seat, m.name);
        break;
      case 'gameStart':
        // An exhibition room auto-rematches with a new bot pair some time after the
        // current one ends. Gating on `this.phase === 'finished'` raced the client's
        // own animation of the final shot (which can take longer to play out than the
        // server's fixed rematch pause) — the swap could land before we'd even shown
        // the result. Gate on "are we actively spectating a match" instead, which is
        // true for the whole time we're watching, independent of animation timing:
        // any 'gameStart' arriving while that's still the case is necessarily a new
        // pairing replacing the current one, so hold it until the viewer chooses to
        // keep watching (see announceSpecMatchEnd / the prompt's "watch next" callback).
        // The very first join has gameActive===false yet, so it's never held.
        if (this.spectator && this.gameActive && !this.spectatorReadyForNext) {
          this.pendingGameStart = m;
          break;
        }
        this.spectatorReadyForNext = false;
        this.handleGameStart(m);
        break;
      case 'joined':
        // reconnect into a held seat mid-game
        this.seat = m.seat;
        this.roomCode = m.code;
        this.spectator = false;
        this.gameActive = true;
        this.reconnectAttempts = 0;
        this.saveRoomResume(this.roomCode, this.seat);
        this.lobby.hide();
        this.chat.setRoomChat(true);
        break;
      case 'shot':
        this.checkGameSeq(m.seq);
        this.applyRemoteShot(m);
        break;
      case 'timeout':
        this.checkGameSeq(m.seq);
        this.applyRemoteTimeout(m);
        break;
      case 'practice':
        this.checkGameSeq(m.seq);
        this.applyRemotePractice(m);
        break;
      case 'aim':
        this.remoteAim = { azimuth: m.azimuth, elevation: m.elevation, a: m.a, b: m.b, pullback: m.pullback };
        break;
      case 'chalk':
        // the opponent is chalking — run the chalk-cube animation on their cue + the sound
        this.cueStick.startChalk();
        this.sound.chalk();
        break;
      case 'pointer':
        // another player is pointing at the table (or cleared their pointer)
        if (m.on) this.tablePointers.set(m.from, m.x, m.y);
        else this.tablePointers.clear(m.from);
        break;
      case 'newgame':
        this.adoptGameSeq(m.seq);
        this.startOnlineMatch(m.variant);
        break;
      case 'reviewShot':
        this.applyReviewShot(m);
        break;
      case 'gameHistory':
        this.onGameHistory(m.games);
        break;
      case 'gameDetail':
        this.onGameDetail(m.game);
        break;
      case 'store':
        this.onStoreState(m.coins, m.owned);
        break;
      case 'purchase':
        this.onPurchaseResult(m);
        break;
      case 'equip':
        this.onEquipResult(m);
        break;
      case 'rename':
        this.onRenameResult(m);
        break;
      case 'feedbackCreated':
        this.feedbackPanel.onSent();
        break;
      case 'feedbackError':
        this.feedbackPanel.onError();
        break;
      case 'music':
        this.music.applyRemote(m);
        break;
      case 'config':
        this.applyRoomConfig(m.config);
        break;
      case 'voice':
        void this.voice.onSignal(m);
        break;
      case 'request-state':
        if (this.gameActive && !this.spectator) this.net?.send({ type: 'state', state: this.serializeGame() });
        break;
      case 'state':
        // Paired with 'gameStart' by sendSpectatorStart's rematch — hold it alongside
        // the buffered gameStart (see the 'gameStart' case) so the finished-match
        // prompt isn't silently stomped by the paired state snapshot arriving right
        // behind it. `pendingGameStart` is only non-null while we're doing exactly that.
        if (this.pendingGameStart) {
          this.pendingState = m.state;
          break;
        }
        this.loadGame(m.state);
        break;
      case 'sync':
        // a fresh layout (e.g. the breaker's post-swing opening) — adopt it
        this.adoptGameSeq(m.seq);
        this.loadGame(m.state);
        this.message = '';
        this.refreshHud();
        break;
      case 'breakstream':
        // the breaker's live swing — rendered each frame by the 'watchbreak' branch
        if (this.phase === 'watchbreak') this.remoteBreak = { balls: m.balls, lever: m.lever };
        break;
      case 'peer-left':
        this.opponentPresent = false;
        this.tablePointers.clear(m.seat); // drop the departed player's ping arrow
        this.voice.peerGone(); // drop the dead audio link, keep our mic ready
        this.message = t('msg.oppLeft');
        this.refreshHud();
        // mid-game: the remaining player wins — offer to keep playing solo or exit
        if (this.gameActive && !this.spectator) {
          this.showPrompt(
            t('prompt.oppLeftWin'),
            t('prompt.keepPlaying'),
            () => this.continueSolo(),
            t('prompt.toMenu'),
            () => this.openMainMenu()
          );
        }
        break;
      case 'peer-rejoined':
        this.hidePrompt(); // opponent came back during the grace window
        this.opponentPresent = true;
        this.message = t('msg.oppBack');
        this.music.broadcastSync(); // catch the returning player up on the queue
        if (this.seat === 0) this.broadcastConfig(); // re-share table/physics too
        this.voice.peerBack(); // re-establish voice with the returning player
        this.refreshHud();
        break;
      case 'opponent-left':
      case 'room-closed':
        // For a spectator this always means the match ended — but the server's
        // notice arrives as soon as it resolves the shot, which is well before the
        // client finishes locally replaying that final shot's ball-roll animation
        // (finalizeShot, which is what flips match.phase to 'finished' and triggers
        // announceSpecMatchEnd — can take several seconds for a multi-cushion shot).
        // Always defer to that local detection, however long it takes, so the winner
        // dialog appears before the room list is yanked open underneath it; its own
        // buttons (watch next / exit spectate) do the actual exit.
        if (this.spectator) {
          this.specRoomAlreadyClosed = true;
          break;
        }
        // a spectator has no "opponent" — the match simply ended, so word it that way
        this.handleRoomClosed(this.spectator ? t('msg.roomClosedSpectator') : t('msg.roomClosed'));
        break;
      case 'kicked':
        this.handleRoomClosed(t('msg.kicked'));
        break;
      case 'error':
        // a coded error gets the localized wording; anything else is the
        // server's own English text, which is all we have for it
        this.lobby.setStatus(
          m.code === 'stakeShort'
            ? t('lobby.joinStakeShort', { n: String(m.stake ?? 0), have: String(this.coins) })
            : m.code === 'rankGap'
              ? t('lobby.joinRankGap', { n: String(m.maxGap ?? 3), tier: tierLabel(m.hostTier ?? 0) })
              : m.message
        );
        break;
    }
  }

  /** Apply a waiting-room snapshot: render it and (re)build the shared practice table. */
  private handleLobby(state: LobbyState): void {
    this.roomCode = state.code;
    this.format = state.format;
    this.reconnectAttempts = 0;
    // Once the game has started, the server still broadcasts a final 'lobby'
    // (status 'playing') — don't reopen the waiting window over the table.
    if (state.status === 'playing' || this.gameActive) {
      this.lobby.hide();
      return;
    }
    this.spectator = false;
    this.waiting = true;
    // adopt the host's shared table colour + physics so everyone plays the same
    // table (the host's edits are relayed inside each lobby snapshot).
    if (state.config && state.hostId !== state.youId) this.applyRoomConfig(state.config);
    this.lobby.setLobby(state);
    this.lobby.setTheme(this.theme); // reflect the local table colour in the swatches
    this.chat.setRoomChat(true);
    // build the practice table once (and again only if the host changes the variant)
    if (this.waitingVariant !== state.variant) this.buildPracticeTable(state.variant);
    this.refreshHud();
  }

  /** The ready-gate fired: every seat filled and ready — begin the actual match. */
  private handleGameStart(m: { variant: VariantId; format: GameFormat; seat: number; names: string[]; levels: number[]; ids?: string[]; cueColors?: string[]; breakStyle: BreakStyle }): void {
    this.seat = m.seat < 0 ? 0 : m.seat;
    this.spectator = m.seat < 0;
    this.format = m.format;
    this.playerNames = m.names;
    this.playerLevels = m.levels ?? [];
    this.playerIds = m.ids ?? [];
    this.playerCueColors = m.cueColors ?? [];
    this.seatStats = [];
    if (this.playerIds.some(Boolean)) this.net?.send({ type: 'playersStats', ids: this.playerIds });
    this.breakStyle = m.breakStyle ?? 'preset';
    this.waiting = false;
    this.gameActive = true;
    this.opponentPresent = true;
    this.waitingVariant = null;
    if (!this.spectator) this.saveRoomResume(this.roomCode, this.seat); // survive a page refresh
    this.lobby.hide();
    this.chat.setRoomChat(true);
    this.chat.system(t('chat.gameStarted'));
    this.startOnlineMatch(m.variant);
    // voice chat disabled for now (panel stays hidden)
    if (this.seat === 0 && !this.spectator) {
      this.music.broadcastSync(); // host shares the music queue
      this.broadcastConfig(); // ...and the table colour + physics settings
    }
    this.refreshPresence(); // now playing / spectating online
  }

  private startOnlineMatch(variant: VariantId): void {
    this.persistGameLog(); // flush a prior game (e.g. a rematch in the same room)
    this.reviewPanel?.dismiss(); // a new match → clear the previous game's review panel
    this.practiceCtx = null;
    this.variant = variant;
    this.base = createCaromWorld(variant);
    // One engine team per format team (2..4). A team's members are its seats
    // (seat % teams === team); it strikes a cue ball by parity (white / yellow,
    // shared by turn for 3-4 teams). Partners alternate innings.
    const teams = FORMAT_INFO[this.format].teams;
    const ballFor = (ti: number): BallId => (ti % 2 === 0 ? 'white' : 'yellow');
    // Purchased cue-ball colours only apply where a team is a single player (no
    // shared/ambiguous ownership) — i.e. every format except 2v2.
    const colorOverride: Partial<Record<BallId, number>> = {};
    if (FORMAT_INFO[this.format].perTeam === 1) {
      for (let ti = 0; ti < teams; ti++) {
        const hex = this.playerCueColors[ti];
        if (hex) colorOverride[ballFor(ti)] = parseInt(hex, 16);
      }
    }
    this.rebuildBalls(variant, colorOverride);
    this.rebuildScenery();
    this.cameraRig.setTable(tableFor(variant));
    const players = Array.from({ length: teams }, (_, ti) => {
      const members = this.playerNames.filter((_, s) => s % teams === ti);
      const memberLevels = this.playerLevels.filter((_, s) => s % teams === ti);
      const fallback = ti % 2 === 0 ? t('msg.white') : t('msg.yellow');
      // A team's handicap to clear is the SUM of its members' 점 tiers (2v2: both
      // partners added; 1v1: just the one).
      const teamTarget = memberLevels.length
        ? memberLevels.reduce((sum, lv) => sum + (lv || 0), 0)
        : this.playerLevels[ti] ?? this.handicaps[0];
      return {
        id: `t${ti}`,
        name: members.join(' & ') || fallback,
        cueBall: ballFor(ti),
        isAI: false,
        startScore: teamTarget,
        members: members.length ? members : [fallback],
      };
    });
    this.match = createMatch({
      rule: this.ruleFor(variant),
      mode: 'handicap',
      equalizingInning: false,
      players,
      startingPlayerIndex: 0,
    });
    this.announceFinishRule(variant);
    this.announcedPlaces.clear();
    this.beginGameLog('online', variant, this.format); // record every shot of this match
    this.phase = 'aiming';
    this.currentShot = null;
    this.oppStrike = null;
    this.lastShot = null;
    this.replaying = false;
    this.shotPractice = false;
    this.pendingRemote = null;
    this.remoteQueue = [];
    this.message = t(this.seat % teams === 0 ? 'msg.youBreak' : 'msg.oppBreaks');
    this.resetTouchCameraForNewGame();
    this.syncBalls();
    // swing-break room: the breaker (seat 0) does the interactive corner break and
    // streams it live; everyone else watches the streamed swing.
    if (this.breakStyle === 'swing' && variant === 'straight-rail') {
      if (!this.spectator && this.seat === 0) this.startCornerBreak();
      else this.watchCornerBreak();
      this.refreshHud();
      return;
    }
    this.faceObjectBall();
    this.applyTurnCamera();
    this.refreshHud();
  }

  /** Build the shared, synced practice table shown in the waiting room. */
  private buildPracticeTable(variant: VariantId): void {
    this.waitingVariant = variant;
    this.variant = variant;
    this.base = createCaromWorld(variant);
    this.match = createMatch({ ...this.matchConfig(variant), startingPlayerIndex: 0 });
    this.rebuildBalls(variant);
    this.rebuildScenery();
    this.cameraRig.setTable(tableFor(variant));
    this.phase = 'aiming';
    this.currentShot = null;
    this.lastShot = null;
    this.replaying = false;
    this.pendingRemote = null;
    this.message = t('msg.practice');
    this.syncBalls();
    this.faceObjectBall();
    this.applyTurnCamera();
    this.refreshHud();
  }

  /** Animate a peer's waiting-room practice shot (no scoring; snaps to their final state). */
  private applyRemotePractice(m: { cue: CueParams; finalState: Record<BallId, BallSnapshot> }): void {
    this.azimuth = m.cue.azimuth;
    this.spin = { ...m.cue.tipOffset };
    this.power01 = this.speedToPower(m.cue.speed);
    const shot = simulate(this.base, m.cue);
    this.lastShot = shot;
    this.pendingRemote = { finalState: m.finalState, result: evaluateShot(shot.events, this.match) };
    this.startPlayback(shot, false);
  }

  /** Fire an emote from the wheel: show + sound it locally, and broadcast to the room. */
  private fireEmote(id: string): void {
    const mySeat = this.spectator ? -1 : this.online ? this.seat : 0;
    this.playEmote(id, mySeat, this.account ?? this.nickname);
    if (this.online) this.net?.send({ type: 'emote', emote: id });
  }

  /** Play an emote (sound + floating bubble) for a given room seat. */
  private playEmote(id: string, seat: number, name: string): void {
    const def = EMOTES_BY_ID.get(id);
    if (!def) return;
    if (def.sound) {
      this.sound.resume();
      this.sound.emote(def.sound);
    }
    const teams = FORMAT_INFO[this.format].teams;
    const card = seat >= 0 ? seat % teams : -1; // room seat → player-card (team) index
    this.hud.showEmote(card, def.glyph, name);
  }

  /** A full snapshot (sync/newgame) legitimately jumps the seq counter forward —
   *  just adopt it as the new baseline, no gap check. */
  private adoptGameSeq(seq: number | undefined): void {
    if (typeof seq === 'number') this.lastGameSeq = seq;
  }

  /** shot/practice broadcasts should arrive back-to-back; a gap means the room's
   *  broadcast dropped one (e.g. our socket was mid-reconnect when it went out) and
   *  we're now silently holding stale ball positions with no way to notice. Ask the
   *  peer for a fresh full snapshot instead of drifting until someone happens to
   *  leave and rejoin. */
  private checkGameSeq(seq: number | undefined): void {
    if (typeof seq !== 'number') return;
    if (this.lastGameSeq !== 0 && seq > this.lastGameSeq + 1) this.net?.send({ type: 'resync' });
    if (seq > this.lastGameSeq) this.lastGameSeq = seq;
  }

  private applyRemoteShot(m: { cue: CueParams; finalState: Record<BallId, BallSnapshot>; result: ShotResult; match?: MatchState; from?: number }): void {
    // Don't interrupt a shot that's still animating — queue it. A bot on a run (or
    // a fast opponent) can send the next shot before this one finishes; interrupting
    // would skip its finalize, dropping its result/finalState and desyncing the match
    // (the "balls jump back + AI shoots repeatedly" bug). Drained in finalizeShot.
    if (this.currentShot || this.oppStrike || this.drainPending) {
      this.remoteQueue.push({ kind: 'shot', ...m });
      return;
    }
    this.playRemoteShot(m);
  }

  /** Play/apply one queued remote shot or timeout, at most one per frame so each
   *  lands in its own paint. Armed by afterMatchAdvance once the previous one has
   *  been applied. */
  private drainRemoteQueue(): void {
    if (!this.drainPending) return;
    if (this.currentShot || this.oppStrike) return; // something is animating — retry next frame
    this.drainPending = false;
    if (!this.remoteQueue.length || this.match.phase === 'finished') return;
    const next = this.remoteQueue.shift()!;
    if (next.kind === 'timeout') this.applyTimeoutNow();
    else this.playRemoteShot(next, this.remoteQueue.length > 0);
  }

  /** The opponent's shot clock ran out (relayed 'timeout'). Same turn-authority
   *  check as a shot, and the same animation-in-flight queueing. */
  private applyRemoteTimeout(m: { from?: number }): void {
    if (!this.acceptsRemoteShot(m.from)) return;
    if (this.currentShot || this.oppStrike || this.drainPending) {
      this.remoteQueue.push({ kind: 'timeout', from: m.from });
      return;
    }
    this.applyTimeoutNow();
  }

  /** Per-frame shot-clock upkeep for online PvP: starts a 45s timer the moment
   *  it's my turn to aim, ticks the HUD/sound in the last 10s, and self-reports
   *  a timeout when it hits zero. Recomputed fresh every frame from `myTurn`/
   *  `phase`, so it self-arms and self-clears across every phase transition
   *  without needing a reset hook at each of the many places that flips back to
   *  'aiming' — vs-AI/managed-bot and solo/hotseat play never arm it. */
  private updateTurnTimer(nowMs: number): void {
    // gameActive excludes the waiting room's free shared-table practice (myTurn is
    // true there for anyone so people can warm up before the match starts) — the
    // shot clock must only run once the real match is underway.
    const active = this.online && this.gameActive && !this.spectator && this.phase === 'aiming' && this.myTurn && !this.match.players.some((p) => p.isAI);
    if (!active) {
      if (this.turnDeadlineMs !== 0) {
        this.turnDeadlineMs = 0;
        this.turnTickSecond = -1;
        this.hud.setTurnTimer(this.seat, null);
      }
      return;
    }
    if (this.turnDeadlineMs === 0) {
      this.turnDeadlineMs = nowMs + TURN_TIME_MS;
      this.turnTickSecond = -1;
    }
    const remaining = this.turnDeadlineMs - nowMs;
    if (remaining <= 0) {
      this.turnDeadlineMs = 0;
      this.turnTickSecond = -1;
      this.hud.setTurnTimer(this.seat, null);
      this.fireTurnTimeout();
      return;
    }
    this.hud.setTurnTimer(this.seat, remaining, TURN_TICK_START_MS);
    if (remaining <= TURN_TICK_START_MS) {
      const sec = Math.ceil(remaining / 1000);
      if (sec !== this.turnTickSecond) {
        this.turnTickSecond = sec;
        this.sound.turnTick();
      }
    }
  }

  /** My own shot clock hit zero: self-report it, same trust model as an actual
   *  shot (the acting player's own client is authoritative for their turn). */
  private fireTurnTimeout(): void {
    if (!this.myTurn || this.phase !== 'aiming') return; // stale guard
    this.net?.send({ type: 'timeout' });
    this.applyTimeoutNow();
  }

  /** Apply a shot-clock timeout to the match — the reducer is symmetric, so
   *  whether it's mine (just self-reported above) or a relayed one from the
   *  opponent, both clients land on the identical resulting MatchState. */
  private applyTimeoutNow(): void {
    const timedOutIndex = this.match.activePlayerIndex;
    const timedOutName = this.match.players[timedOutIndex]?.name ?? '';
    const { state, forfeited } = applyTimeout(this.match);
    this.match = state;
    this.sound.miscue();
    this.message = forfeited ? t('msg.timeoutForfeit', { name: timedOutName }) : t('msg.timeout', { name: timedOutName });
    this.afterMatchAdvance();
  }

  /**
   * Should a relayed shot be played? The server stamps `from` with the sender's
   * seat, so this rejects a shot that didn't come from whoever's turn it is —
   * the client half of "nobody may shoot for another player". Server-authored
   * shots (bot games) carry no `from` and are trusted.
   *
   * Spectators skip the turn test on purpose: they're passive, their match state
   * can legitimately lag behind a burst of queued shots, and rejecting there
   * would strand them mid-backlog instead of catching up.
   */
  private acceptsRemoteShot(from: number | undefined): boolean {
    if (from == null) return true; // server-authored
    if (!this.gameActive) return false; // no live match this could belong to
    if (this.spectator) return true;
    if (from === this.seat) return false; // never re-play my own shot
    const teams = this.match.players.length;
    if (teams > 0 && from % teams !== this.match.activePlayerIndex) return false;
    return true;
  }

  private playRemoteShot(m: { cue: CueParams; finalState: Record<BallId, BallSnapshot>; result: ShotResult; match?: MatchState; from?: number }, instant = false): void {
    if (!this.acceptsRemoteShot(m.from)) return; // not this player's turn to shoot
    this.azimuth = m.cue.azimuth;
    this.spin = { ...m.cue.tipOffset };
    this.power01 = this.speedToPower(m.cue.speed);
    this.lastCue = m.cue; // so this opponent/bot shot is recorded with the right cue
    const shot = simulate(this.base, m.cue);
    this.lastShot = shot;
    this.pendingRemote = { finalState: m.finalState, result: m.result, match: m.match };
    if (instant) {
      // spectator has fallen behind (more shots piled up in remoteQueue behind this
      // one than real-time animation can keep up with): skip the strike telegraph +
      // real-time roll and jump straight to the result, so the backlog drains instead
      // of growing forever. Only the newest queued shot gets full real-time playback.
      this.currentShot = shot;
      this.finalizeShot();
      return;
    }
    // telegraph the opponent's whole stroke (aim + draw + strike) before the roll
    this.beginStrike(m.cue, shot);
  }

  /**
   * Telegraph an opponent's stroke before the balls roll: pose the cue aimed
   * (direction / tilt / spin dot), draw it back proportional to power, then thrust
   * it into the ball at a speed set by how hard they hit. Bots (and even humans,
   * post-release) send no live aim frames for the strike, so this is the "tell" of
   * their shot. When the thrust lands it hands off to the normal shot playback.
   */
  private beginStrike(cue: CueParams, shot: SimulationResult, replay = false): void {
    const power = Math.max(0, Math.min(1, this.power01));
    // pose at the SHOT's own cue ball (a replayed shot may not be the active one)
    const cbp = this.base.get(cue.ball).pos;
    this.oppStrike = {
      shot,
      cuePos: this.scratch.set(cbp.x, cbp.y, cbp.z).clone(),
      az: cue.azimuth,
      el: cue.elevation,
      spin: { a: cue.tipOffset.a, b: cue.tipOffset.b },
      maxPull: 0.25 + 0.75 * power, // draw back further for a harder hit
      t0: performance.now(),
      backDur: replay ? 360 : 240, // a slightly more deliberate wind-up when reviewing
      fwdDur: 220 - 150 * power, // harder = faster thrust (~70..220ms)
      replay,
    };
    this.phase = 'strike';
    this.setAimActive(false);
    this.remoteAim = null; // the live aim cue gives way to the scripted strike
    this.cueStick.setVisible(true);
    this.hud.setSpin(cue.tipOffset.a, cue.tipOffset.b); // mirror their English on the pad
    this.message = '';
    this.refreshHud();
  }

  /** Auto-lag on a single-ball table: each cue ball goes up and back; nearer the head rail breaks. */
  private runLag(): 0 | 1 {
    const lag = (ball: BallId): number => {
      const ref = this.base.get(ball);
      const lagWorld = new World({
        playWidth: this.base.halfW * 2,
        playLength: this.base.halfL * 2,
        constants: this.base.constants,
        balls: [{ id: ball, radius: ref.radius, mass: ref.mass, pos: { x: ref.pos.x, y: ref.pos.y } }],
      });
      const cue: CueParams = {
        ball,
        azimuth: Math.PI / 2 + (Math.random() * 2 - 1) * 0.05,
        elevation: 0,
        speed: 2.6 + (Math.random() * 2 - 1) * 0.3,
        tipOffset: { a: 0, b: 0 },
      };
      const res = simulate(lagWorld, cue);
      return Math.abs(res.finalState[ball].pos.y + this.base.halfL); // distance to head rail (y = -halfL)
    };
    return resolveLag(lag('white'), lag('yellow')).firstPlayerIndex;
  }

  // ---- helpers ----

  private get activeCueId(): BallId {
    return this.match.players[this.match.activePlayerIndex].cueBall;
  }

  private get activeIsAI(): boolean {
    return this.match.players[this.match.activePlayerIndex].isAI;
  }

  /**
   * The active AI's Korean handicap (점) — also its skill rank, since the player
   * picks a level and the computer plays at it. Falls back to the stored level.
   */
  private aiLevel(): number {
    return this.match.players[this.match.activePlayerIndex].target || this.handicaps[1] || 100;
  }

  private cueBallPos(): THREE.Vector3 {
    const p = this.base.get(this.activeCueId).pos;
    return this.scratch.set(p.x, p.y, p.z).clone();
  }

  private objectBalls(): { pos: THREE.Vector3; radius: number; color: number }[] {
    return ballsFor(this.variant).filter((id) => id !== this.activeCueId).map((id) => {
      const b = this.base.get(id);
      return { pos: new THREE.Vector3(b.pos.x, b.pos.y, b.pos.z), radius: b.radius, color: BALL_COLOR[id] };
    });
  }

  /**
   * How much to slow the mouse-driven aim swing right now, as a fraction of
   * normal speed (1 = full speed, smaller = finer). Slows automatically as
   * the aim line sweeps across a ball — that's exactly where a tiny angle
   * change shifts the contact point the most, so the same physical mouse
   * motion should buy finer control there than it does swinging through
   * open table.
   */
  private autoFineFactor(): number {
    const AUTO_FINE = 0.25; // slowest point, right at a ball's center line
    const WINDOW_BALLS = 6; // start slowing within this many ball-angular-widths
    const cue = this.cueBallPos();
    let factor = 1;
    for (const b of this.objectBalls()) {
      const dx = b.pos.x - cue.x;
      const dy = b.pos.y - cue.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 1e-6) continue;
      const halfWidth = Math.atan2(b.radius, dist); // angular half-width of the ball
      const window = halfWidth * WINDOW_BALLS;
      let diff = this.azimuth - Math.atan2(dy, dx);
      diff = Math.atan2(Math.sin(diff), Math.cos(diff)); // wrap to [-pi, pi]
      diff = Math.abs(diff);
      if (diff >= window) continue;
      const t = diff / window; // 0 = dead-center, 1 = window edge
      const local = AUTO_FINE + (1 - AUTO_FINE) * t;
      if (local < factor) factor = local;
    }
    return factor;
  }

  /** Default aim: toward the nearest object ball. */
  /** Default aim at the start of a turn: toward the nearest RED ball (the scoring
   *  target), falling back to any object ball if there are no reds. */
  /**
   * Set the cue's starting aim direction: an admin-authored override when one
   * is given (daily/tutorial layouts), else the usual auto-face-nearest-ball.
   */
  private applyStartAngle(angle: number | null | undefined): void {
    if (angle == null) {
      this.faceObjectBall();
      return;
    }
    this.azimuth = angle;
    this.elevation = 0;
    this.hud.setTilt(0);
  }

  private faceObjectBall(): void {
    const cue = this.base.get(this.activeCueId).pos;
    const others = ballsFor(this.variant).filter((id) => id !== this.activeCueId);
    const reds = others.filter((id) => id.startsWith('red'));
    const targets = reds.length ? reds : others;
    let best = this.azimuth;
    let bestD = Infinity;
    for (const id of targets) {
      const p = this.base.get(id).pos;
      const d = Math.hypot(p.x - cue.x, p.y - cue.y);
      if (d < bestD) {
        bestD = d;
        best = Math.atan2(p.y - cue.y, p.x - cue.x);
      }
    }
    this.azimuth = best;
    // start every turn at the lowest cue tilt — level. The aim loop re-floors it to
    // the minimum the rail behind the ball needs, so you never inherit a previous
    // shot's massé elevation.
    this.elevation = 0;
    this.hud.setTilt(0);
  }

  // Three-cushion shots need to carry three rails, so even the lightest stroke
  // should leave with more pace than a four-ball tap.
  private minShotSpeed(): number {
    return this.variant === 'three-cushion' ? MIN_SHOT_SPEED * 1.5 : MIN_SHOT_SPEED;
  }

  private powerToSpeed(p: number): number {
    const min = this.minShotSpeed();
    return min + p * (this.tune.maxShotSpeed - min);
  }

  /** Inverse of powerToSpeed: recover the 0..1 power that produced a cue speed. */
  private speedToPower(speed: number): number {
    const min = this.minShotSpeed();
    return Math.max(0, Math.min(1, (speed - min) / (this.tune.maxShotSpeed - min)));
  }

  /**
   * Map an actual collision impact speed (m/s, the closing velocity reported by
   * the physics) to a 0..1 sound intensity. This is what makes a click's loudness
   * track how hard the balls *actually* met — a soft cue shot that produces a fast
   * carom still cracks, and each contact in a break scales independently — instead
   * of every click using the cue's power slider. Referenced to the max shot speed
   * so a full-pace direct hit saturates at 1.
   */
  private impactIntensity(speed: number): number {
    return Math.max(0, Math.min(1, speed / this.tune.maxShotSpeed));
  }

  private setCamera(mode: CameraMode): void {
    const wasTop = this.cameraMode === 'top';
    this.cameraMode = mode;
    this.cameraRig.resetView(); // picking a preset clears any right-drag view orbit
    // Every other preset switch mid-shot is deferred until the balls settle
    // (the chase cam owns the view while a shot is playing out; applyTurnCamera
    // picks the queued mode back up once it ends) — but the top-down view can
    // be toggled on or off at any time, including mid-flight, since watching
    // (or leaving) it isn't something worth waiting for the shot to finish for.
    if (this.phase !== 'simulating' || mode === 'top' || wasTop) this.cameraRig.setMode(mode);
    this.refreshHud();
  }

  /**
   * Touch only: a new game opens in the aim view. The camera toggle's choice
   * otherwise carries over from the previous game, so a phone player who ended
   * one in the top-down view would start the next there too. Desktop keeps its
   * last pick (1/2/Tab are a keystroke away).
   */
  private resetTouchCameraForNewGame(): void {
    if (!this.touch || this.cameraMode === 'aim') return;
    this.cameraMode = 'aim';
    this.cameraRig.resetView();
  }

  /** Show/hide the aim line + ghost ball (the HUD button and the G key share this). */
  private toggleGuide(): void {
    this.guideOn = !this.guideOn;
    this.aimVisuals.setVisible(this.guideOn);
    this.noteTutorialAction('guide');
    this.refreshHud();
  }

  /**
   * Leaving aim mode: hold the current angle and switch to free-orbit controls.
   * A bare right-click alone does not satisfy the tutorial's view:orbit action —
   * see the pointermove handler in bindDesktopInput, which requires an actual
   * drag once OrbitControls takes over.
   */
  private enterFreeOrbit(): void {
    this.cameraMode = 'orbit';
    this.cameraRig.enterOrbitFromCurrent();
    this.refreshHud();
  }

  /**
   * Adjust the framed-camera zoom AND remember it. The player's pull-back is a
   * standing preference, not a per-shot fiddle: it has to come back on the next
   * shot, after watching the opponent (which parks the camera at SPECTATE_ZOOM),
   * and on the next visit. Every zoom gesture goes through here.
   */
  private adjustZoom(delta: number): void {
    this.aimZoom = this.cameraRig.adjustZoom(delta);
    localStorage.setItem('carom.zoom', String(this.aimZoom));
  }

  /** Widen (delta>0) or tighten the framed cameras; orbit zooms with the wheel. */
  private zoomView(delta: number): void {
    this.adjustZoom(delta);
    this.message =
      this.cameraMode === 'orbit'
        ? t('msg.zoomOrbit')
        : t('msg.zoom', { z: this.cameraRig.displayZoom.toFixed(1) });
    this.refreshHud();
  }

  // ---- shooting ----

  /** Start pointing at the table (hold F). Seated online players only. */
  private startPointing(): void {
    if (!this.online || this.spectator || this.seat < 0) return;
    this.pointing = true;
  }

  /** Stop pointing (release F): clear my arrow locally and tell the room. */
  private stopPointing(): void {
    if (!this.pointing) return;
    this.pointing = false;
    this.tablePointers.clear(this.seat);
    if (this.online && this.net) this.net.send({ type: 'pointer', on: false, x: 0, y: 0 });
  }

  /** Raycast the cursor (or an explicit screen point, for touch) onto the table
   *  plane (z=0), clamped to the table + a rail margin so you can point at the
   *  edge. Returns null if the ray misses. */
  private raycastTable(clientX = this.pointerX, clientY = this.pointerY): { x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    const nx = Math.max(-1, Math.min(1, ((clientX - rect.left) / rect.width) * 2 - 1));
    const ny = Math.max(-1, Math.min(1, -((clientY - rect.top) / rect.height) * 2 + 1));
    this.ptrRaycaster.setFromCamera(new THREE.Vector2(nx, ny), this.cameraRig.renderCamera);
    const hit = new THREE.Vector3();
    if (!this.ptrRaycaster.ray.intersectPlane(this.ptrPlane, hit)) return null;
    const m = 0.08; // let the pointer reach the rail / edge just outside the cushion
    return {
      x: Math.max(-(this.base.halfW + m), Math.min(this.base.halfW + m, hit.x)),
      y: Math.max(-(this.base.halfL + m), Math.min(this.base.halfL + m, hit.y)),
    };
  }

  /** Top-view touch aim: point at wherever the finger is touching, relative to the
   *  cue ball's own position on the table — not a left/right drag delta. The top
   *  view looks straight down, so a screen point maps onto the table plane (z=0)
   *  and the aim direction is just the angle from the ball to that point. */
  private aimAtTouchPoint(clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.ptrRaycaster.setFromCamera(new THREE.Vector2(nx, ny), this.cameraRig.renderCamera);
    const hit = new THREE.Vector3();
    if (!this.ptrRaycaster.ray.intersectPlane(this.ptrPlane, hit)) return;
    const cue = this.cueBallPos();
    const dx = hit.x - cue.x;
    const dy = hit.y - cue.y;
    if (Math.hypot(dx, dy) < 0.02) return; // touch landed on the ball itself — keep the current aim
    this.azimuth = Math.atan2(dy, dx);
  }

  /** Click while holding F: dab a chalk-colored mark at the pointed spot, local
   *  only (unlike the ping arrow, marks aren't broadcast — just your own aiming
   *  aid). Cleared automatically the moment the next shot fires. */
  private dropChalkMark(): void {
    const p = this.raycastTable();
    if (!p) return;
    this.chalkMarks.add(p.x, p.y);
    this.needsRender = true;
  }

  /** Per-frame: while holding F, show my own arrow and broadcast it (throttled). */
  private updatePointing(nowMs: number): void {
    if (!this.pointing) return;
    const p = this.raycastTable();
    if (!p) return;
    this.tablePointers.set(this.seat, p.x, p.y); // local feedback of my own pointer
    if (this.online && this.net && nowMs - this.lastPointerSentMs > 50) {
      this.lastPointerSentMs = nowMs;
      this.net.send({ type: 'pointer', on: true, x: p.x, y: p.y });
    }
  }

  /** An online room's waiting-lobby shared practice table: seated, not spectating,
   *  game not yet started. Shares several offline-solo-practice affordances (ball
   *  dragging, re-practice, F-to-mark falling back to pointing) since a shot taken
   *  here is local/non-scoring either way — see canDragBalls, fireCue, resetToLastShot. */
  private isLobbyPractice(): boolean {
    return this.online && this.waiting && !this.spectator;
  }

  /** May the balls be picked up and moved by hand right now? Offline solo/vs-AI
   *  practice, or an online room's waiting-lobby shared practice table (local
   *  only — not broadcast — but harmless: the next real shot still snaps
   *  everyone to its authoritative finalState, same as an unsynced chalk mark).
   *  Either way, only while the table is at rest between shots. Never during a
   *  이번주의 매치 attempt: it's a scored run from a fixed shared layout. */
  private canDragBalls(): boolean {
    if (this.dailyRun) return false;
    const soloOffline = !this.online && !!this.match.config.solo;
    return (soloOffline || this.isLobbyPractice()) && this.phase === 'aiming' && !this.activeIsAI && !this.stroking;
  }

  /** Which ball (if any) sits under a screen point, for picking one up. A
   *  generous hitbox — well bigger than the ball itself — makes it easy to
   *  grab without pixel-perfect aim; a click/tap that merely lands near a
   *  ball, not exactly on it, should still pick it up. */
  private ballAtPoint(clientX = this.pointerX, clientY = this.pointerY): BallId | null {
    const p = this.raycastTable(clientX, clientY);
    if (!p) return null;
    let best: BallId | null = null;
    let bestD = Infinity;
    for (const b of this.base.balls) {
      const d = Math.hypot(b.pos.x - p.x, b.pos.y - p.y);
      if (d < b.radius * 4.5 && d < bestD) {
        bestD = d;
        best = b.id;
      }
    }
    return best;
  }

  /** Pick up the ball under the cursor (hold F on desktop — a left-click directly
   *  on a ball does the same thing, see the pointerdown handler). No-op if none
   *  is there. */
  private startBallDrag(): void {
    if (!this.canDragBalls() || this.draggingBallId) return;
    const id = this.ballAtPoint();
    if (!id) return;
    this.draggingBallId = id;
    this.canvas.style.cursor = 'grabbing';
  }

  /** Drop the ball being carried, wherever it currently sits. */
  private stopBallDrag(): void {
    if (!this.draggingBallId) return;
    this.draggingBallId = null;
    if (this.aimActive) this.canvas.style.cursor = 'none';
    else if (!this.touch) this.canvas.style.cursor = '';
    this.needsRender = true;
  }

  /** Per-frame (or per-touch-move): move the carried ball to a screen point,
   *  clamped inside the cushions and refusing a spot that would overlap another
   *  ball (it just stays at its last valid spot rather than snapping through). */
  private updateBallDrag(clientX = this.pointerX, clientY = this.pointerY): void {
    const id = this.draggingBallId;
    if (!id) return;
    const p = this.raycastTable(clientX, clientY);
    if (!p) return;
    const b = this.base.get(id);
    const limX = this.base.halfW - b.radius;
    const limY = this.base.halfL - b.radius;
    const nx = Math.max(-limX, Math.min(limX, p.x));
    const ny = Math.max(-limY, Math.min(limY, p.y));
    for (const other of this.base.balls) {
      if (other.id === id) continue;
      if (Math.hypot(other.pos.x - nx, other.pos.y - ny) < other.radius + b.radius) return;
    }
    b.pos.x = nx;
    b.pos.y = ny;
    b.pos.z = b.radius;
    b.vel = { x: 0, y: 0, z: 0 };
    b.angVel = { x: 0, y: 0, z: 0 };
    this.syncBalls();
    this.needsRender = true;
  }

  /** May the player re-chalk right now? On your turn the cue must be up (aiming)
   *  or the balls still rolling from a shot (simulating); while waiting for your
   *  turn you can chalk anytime to prep a fresh tip. Never while spectating,
   *  mid-stroke, in a menu, or after the game is over. */
  private canChalk(): boolean {
    if (this.menuOpen || this.spectator || this.stroking) return false;
    if (this.phase === 'finished' || this.phase === 'assessed') return false;
    return this.myTurn ? this.phase === 'aiming' || this.phase === 'simulating' : true;
  }

  /** Re-chalk the tip: restores grip, runs the upright chalking animation + sound. */
  private chalkTip(): void {
    this.noteTutorialAction('chalk');
    this.sound.resume();
    this.chalkLevel = 1;
    this.sound.chalk();
    this.hud.setChalk(this.chalkLevel);
    this.message = t('msg.chalked');
    this.refreshHud();
    // Only animate the on-screen cue + telegraph when MY cue is the one shown (my
    // aiming turn). While waiting, the shown cue is the opponent's, so a chalk
    // there is a silent tip prep — sound + gauge only, no cue animation/broadcast.
    const myCueShown = this.phase === 'aiming' && this.myTurn && !this.spectator;
    if (myCueShown) {
      this.cueStick.startChalk();
      if (this.online && this.net && this.opponentPresent) this.net.send({ type: 'chalk' });
    }
  }

  private shootHuman(): void {
    if (this.phase !== 'aiming' || !this.myTurn || this.aiThinking) return;
    this.sound.resume();

    const a = this.spin.a;
    const b = this.spin.b;
    const offMag = Math.hypot(a, b); // how far off-centre we're hitting (0..~1)

    // Real-world inconsistency: a hair of random error in aim, power, spin and the
    // tip's bite each shot, scaled by the "shot variation" tuning. rnd() ≈ N(0,1)
    // in [-1.5, 1.5] (sum of three uniforms) so most shots wobble only slightly.
    const j = this.tune.shotJitter;
    const rnd = () => Math.random() + Math.random() + Math.random() - 1.5;

    // The tip's grip on the ball comes from the chalk. With a well-chalked tip you
    // can pile on english safely; as it wears, the safe-spin window shrinks and
    // the odds of the tip skidding off (a miscue) climb — and grip itself varies a
    // touch shot to shot (chalk is never applied perfectly evenly).
    const grip = Math.max(0.3, 0.6 + 0.4 * this.chalkLevel + rnd() * j * 0.08); // 0.6..1.0 ± jitter
    const safeOff = 0.85 * grip; // tip offset you can apply with no risk: ~0.51 .. 0.85
    // Beyond the safe window the risk ramps slowly (slope ~1/0.7) and is capped at
    // 0.45, so a miscue is an occasional consequence of heavy english on a worn tip
    // rather than something that happens on normal spin.
    const miscueProb = offMag <= safeOff ? 0 : Math.min(0.45, (offMag - safeOff) / 0.7);

    if (Math.random() < miscueProb) {
      this.miscue();
      return;
    }

    // Clean hit: a worn tip still bites less, so the ball takes less of the spin
    // you dialled in (chalk affects how much english actually transfers).
    const spinEff = 0.6 + 0.4 * this.chalkLevel; // 0.6 (bare) .. 1.0 (fresh)
    // Each shot scrubs a little chalk off the tip — more when you brush on english.
    this.chalkLevel = Math.max(0, this.chalkLevel - (CHALK_WEAR_BASE + CHALK_WEAR_SPIN * offMag));
    this.hud.setChalk(this.chalkLevel);

    this.fireCue({
      ball: this.activeCueId,
      azimuth: this.azimuth + rnd() * j * 0.01, // aim wobble (~±0.5° at full jitter)
      elevation: this.elevation,
      speed: this.powerToSpeed(this.power01) * (1 + rnd() * j * 0.12), // power wobble (~±12%)
      // spin wobble only nudges English you actually dialled in — a centred hit
      // (a = b = 0) gets none, so it never picks up surprise side/top/back spin.
      // (Aim + power still wobble, which is where a real miss mostly shows up.)
      tipOffset: {
        a: a === 0 ? 0 : a * spinEff + rnd() * j * 0.03,
        b: b === 0 ? 0 : b * spinEff + rnd() * j * 0.02,
      },
    });
  }

  /** The tip skidded off the ball: a weak, errant hit with the english lost. */
  private miscue(): void {
    this.sound.miscue();
    this.chalkLevel = Math.max(0, this.chalkLevel - 0.4); // a miscue strips the tip
    this.hud.setChalk(this.chalkLevel);
    // tip slides off: little power, an unpredictable squirt, spin lost (slips low)
    const squirt = (Math.random() - 0.5) * 0.18; // up to ~±5° of errant deflection
    this.fireCue({
      ball: this.activeCueId,
      azimuth: this.azimuth + squirt,
      elevation: this.elevation,
      speed: this.powerToSpeed(this.power01) * (0.22 + 0.16 * Math.random()),
      tipOffset: { a: (Math.random() - 0.5) * 0.2, b: -(0.1 + 0.2 * Math.random()) },
    });
    // set after fireCue → startPlayback (which clears the message) so it sticks
    this.message = t('msg.miscue');
    this.refreshHud();
  }

  private fireCue(cue: CueParams): void {
    // reflect the shot in the aim state so visuals/camera match (esp. for AI shots)
    this.azimuth = cue.azimuth;
    this.spin = { ...cue.tipOffset };
    this.power01 = this.speedToPower(cue.speed);
    this.lastCue = cue;
    // Solo practice (or an online room's waiting-lobby shared practice table):
    // remember the table + score + aim from just before this shot so 'r'/다시치기
    // can rewind and let you take the same shot again.
    if ((!this.online && this.match.config.solo) || this.isLobbyPractice()) {
      const balls = {} as NonNullable<typeof this.preShot>['balls'];
      for (const id of ballsFor(this.variant)) {
        const b = this.base.get(id);
        balls[id] = { pos: { ...b.pos }, orientation: { ...b.orientation } };
      }
      this.preShot = { match: this.match, cue, balls };
    } else {
      this.preShot = null;
    }
    const shot = simulate(this.base, cue);
    this.lastShot = shot;
    // online: broadcast the shot the instant it's fired so others animate in real
    // time. In the waiting room it's a (non-scoring) synced practice shot; in a
    // live match it carries the authoritative result.
    if (this.online && this.net && !this.spectator) {
      if (this.waiting || this.shotPractice) {
        this.net.send({ type: 'practice', cue, finalState: shot.finalState });
      } else if (this.gameActive && this.myTurn) {
        // …and never send a scoring shot when it isn't our turn: with stale seat
        // state that's exactly "shooting for the opponent". The server rejects it
        // too, but a client should not be trying in the first place.
        const result = evaluateShot(shot.events, this.match);
        this.net.send({ type: 'shot', cue, finalState: shot.finalState, result });
      }
    }
    // solo vs-AI: telegraph the computer opponent's stroke too (online opponents go
    // through playRemoteShot). Your own shots play immediately, as before.
    if (!this.online && this.activeIsAI) this.beginStrike(cue, shot);
    else this.startPlayback(shot, false);
  }

  /** Re-play the previous shot (human turns only); does not affect the score. */
  private replayLast(): void {
    if (!this.lastShot || this.phase !== 'aiming' || this.activeIsAI || this.stroking) return;
    this.sound.resume();
    this.startPlayback(this.lastShot, true);
  }

  /**
   * Solo practice: rewind the table, score, and aim to just before the last shot
   * so you can take that exact shot again ('r' key). Offline single-play only;
   * one rewind per shot (the snapshot is consumed).
   */
  private resetToLastShot(): void {
    if (this.dailyRun) return; // 이번주의 매치: every stroke counts — no retries
    if (!this.isLobbyPractice() && (this.online || !this.match.config.solo)) return;
    if (this.activeIsAI) return;
    const snap = this.preShot;
    if (!snap) return;
    this.stroking = false; // cut a held stroke-draw or running shot short to retry
    this.currentShot = null;
    for (const id of ballsFor(this.variant)) {
      const b = this.base.get(id);
      const s = snap.balls[id];
      if (!s) continue;
      b.pos = { ...s.pos };
      b.vel = { x: 0, y: 0, z: 0 };
      b.angVel = { x: 0, y: 0, z: 0 };
      b.orientation = { ...s.orientation };
      b.state = 'stationary';
    }
    this.match = snap.match; // rewind the score too, so it's a true redo
    this.preShot = null;
    // restore the aim/power/spin from that shot as the starting point to retry
    const cue = snap.cue;
    this.azimuth = cue.azimuth;
    this.spin = { ...cue.tipOffset };
    this.power01 = this.speedToPower(cue.speed);
    this.elevation = 0;
    this.hud.setPower(this.power01);
    this.hud.setSpin(this.spin.a, this.spin.b);
    this.hud.setTilt(0);
    this.lastShot = null; // the recorded animation no longer matches the table
    this.phase = 'aiming';
    this.syncBalls();
    this.applyTurnCamera();
    this.message = t('msg.resetShot');
    this.refreshHud();
  }

  /** Stream the local cue pose to the room (throttled) so others see it move. */
  private broadcastAim(nowMs: number): void {
    if (!this.online || !this.net || !this.opponentPresent || this.spectator) return;
    if (nowMs - this.lastAimSentMs < 45) return; // ~22 Hz is plenty for a smooth cue
    this.lastAimSentMs = nowMs;
    this.net.send({
      type: 'aim',
      azimuth: this.azimuth,
      elevation: this.elevation,
      a: this.spin.a,
      b: this.spin.b,
      pullback: this.stroking ? this.strokeDraw : 0,
    });
  }

  /**
   * Build the cushion-count pops for a shot — but only in 마지막 3쿠션, where the
   * rail count is the whole point of the shot. In normal play a running tally
   * over every rail would be noise.
   *
   * The window counted here is exactly the rule's: the striker's cushion contacts
   * strictly BEFORE it reaches the second object ball (match.ts scores the shot on
   * `cushionsBeforeSecond`). Counting rails the rule ignores would be worse than
   * showing nothing — it would teach the player the wrong thing.
   */
  private buildPopCues(shot: SimulationResult): void {
    this.popCues = [];
    this.popIdx = 0;
    this.jackpotFiredThisShot = false;
    const rule = this.match.config.rule;
    const target = rule.finalShotMinCushions;
    const player = this.match.players[this.match.activePlayerIndex];
    if (target == null || !player || !inFinalShotStage(this.match.config, rule, player)) return;

    const striker = player.cueBall;
    const seq = deriveSequence(shot.events, striker);
    const cutoff = seq.tSecond; // null → the cue never reached a second ball; count them all
    let n = 0;
    for (const e of shot.events) {
      if (e.kind !== 'ball-cushion' || e.ball !== striker) continue;
      if (cutoff !== null && e.t >= cutoff) continue; // outside the scoring window
      n++;
      this.popCues.push({ t: e.t, n, x: e.pos.x, y: e.pos.y, win: n === target });
    }
    this.popCues.sort((a, b) => a.t - b.t);
  }

  private startPlayback(shot: SimulationResult, replay: boolean): void {
    this.remoteAim = null; // a shot is starting — stop showing the aiming cue
    this.setAimActive(false); // release pointer-lock for the playback / next turn
    this.currentShot = shot;
    this.replaying = replay;
    this.soundCues = shot.events
      .filter((e) => e.kind === 'ball-ball' || e.kind === 'ball-cushion')
      .map((e) => ({ t: e.t, kind: e.kind === 'ball-ball' ? 'ball' : 'cushion', intensity: this.impactIntensity(e.speed) }));
    this.soundIdx = 0;
    this.cushionPops.clear(); // never carry a previous shot's tally into this one
    this.buildPopCues(shot);
    this.sound.strike(this.power01);
    this.shotStartMs = performance.now();
    this.phase = 'simulating';
    this.cueStick.setVisible(false);
    this.aimVisuals.setVisible(false); // drop the aim line the instant the shot starts
    // chase the cue ball for the shot's flight — unless watching from the
    // top-down view, which stays put rather than jumping to the chase cam
    if (this.cameraMode !== 'top') this.cameraRig.setMode('action');
    this.message = replay ? 'Replaying…' : '';
    this.refreshHud();
  }

  private endReplay(): void {
    this.replaying = false;
    this.currentShot = null;
    // a review replay of a finished game returns to the end state, not the aim loop
    this.phase = this.match.phase === 'finished' ? 'finished' : 'aiming';
    this.syncBalls();
    this.applyTurnCamera();
    this.message = '';
    this.refreshHud();
    // one-shot: fires after the balls have settled (the permalink viewer's
    // "what next?" prompt), cleared first so a re-entrant replay can re-arm it
    const after = this.afterReplay;
    this.afterReplay = null;
    after?.();
  }

  /** Scrub slider: freeze on a frame of the last shot (resume by aiming on the table). */
  private onScrub(frac: number): void {
    if (!this.lastShot || (this.phase !== 'aiming' && this.phase !== 'review')) return;
    this.reviewFrac = frac;
    if (this.phase !== 'review') {
      this.phase = 'review';
      this.message = t('msg.reviewing');
      this.refreshHud();
    }
  }

  private exitReview(): void {
    this.phase = 'aiming';
    this.aimVisuals.setVisible(this.guideOn);
    this.syncBalls();
    this.message = '';
    this.refreshHud();
  }

  private finalizeShot(): void {
    const shot = this.currentShot;
    if (!shot) return;
    const remote = this.pendingRemote;
    // online: the shooter's reported final state + result are authoritative
    const authoritativeFinal = remote ? remote.finalState : shot.finalState;
    const lastFrame = shot.frames[shot.frames.length - 1];

    // shot recorder: snapshot the PRE-shot layout now, before `base` is advanced to
    // the final rest state below (the record is pushed once the result is known).
    const shotStartPositions = this.captureBallPositions();

    // bring the base world to rest at the final positions (carry orientation for continuity)
    for (const id of ballsFor(this.variant)) {
      const b = this.base.get(id);
      const fs = authoritativeFinal[id];
      b.pos = { ...fs.pos };
      b.vel = { x: 0, y: 0, z: 0 };
      b.angVel = { x: 0, y: 0, z: 0 };
      b.orientation = { ...lastFrame.balls[id].orientation };
      b.state = 'stationary';
    }

    const result = remote ? remote.result : evaluateShot(shot.events, this.match);
    // record this shot (shooter is still the active player until applyShotResult)
    this.recordShot(shotStartPositions, result);
    // Bot exhibition / managed games echo their authoritative post-shot match back
    // with the shot — adopt it directly rather than re-deriving, so a spectator can
    // never silently drift from the server's truth (one missed/mis-timed message
    // used to leave it permanently out of sync with no way to self-correct).
    this.match = remote?.match ?? applyShotResult(this.match, result);
    // (the shot is broadcast at fire time in fireCue, not here, for real-time sync)
    this.pendingRemote = null;
    const ev = result.evaluation;
    // A scored carom that touched a cushion anywhere (bank/뱅크, or a rail between
    // the two object balls) gets a congratulatory chime on top of the per-contact
    // cushion thuds already played during the replay — see bankScore(). This is
    // broader than a true bank shot (below) — any cushion contact earns the chime.
    const cushionTouched = ev.cushionsBeforeFirst > 0 || ev.cushionsBetween > 0 || ev.cushionsBeforeSecond > 0;
    if (ev.scored && cushionTouched && !this.jackpotFiredThisShot) this.sound.bankScore();
    // A true bank shot: the cue ball hit a cushion before touching any ball at
    // all — a cushion contact later in the shot (between object balls, say)
    // doesn't count, no matter how the carom otherwise scored.
    const banked = ev.cushionsBeforeFirst > 0;
    const reason = this.shotMessage(ev, this.match.config.rule);
    const base = ev.scored ? `${reason}  (+${result.appliedPoints})` : reason;
    this.message = `${base} · ${t('msg.power', { p: Math.round(this.power01 * 100) })}`;

    // reset the tip contact (spin) back to centre for the next shot
    this.spin = { a: 0, b: 0 };
    this.hud.setSpin(0, 0);
    // reset the cue tilt to level too — the aim loop re-floors it as the rail needs
    this.elevation = 0;
    this.hud.setTilt(0);

    this.currentShot = null;
    this.syncBalls();
    this.chalkMarks.clear(); // aiming aid for the shot that just finished, not the next one

    if (this.assessing) {
      // assessment session: this shot is one "turn"; count it and a carom toward the level
      this.tallyAssessment(ev.scored);
      this.refreshHud();
      return;
    }

    if (this.dailyRun) {
      // 오늘의 배치: every stroke counts whether or not it scored — a miss just
      // leaves you shooting from where the balls stopped.
      this.tallyDaily(ev.scored, banked);
      this.refreshHud();
      return;
    }

    if (this.tutorial && this.lastCue) {
      // 당구 교실: the mission decides what this shot meant. Nothing about the
      // match score matters here, so we stop before the placement/finish logic.
      this.tallyTutorial({ cue: this.lastCue, evaluation: ev, events: shot.events, variant: this.variant });
      this.refreshHud();
      return;
    }

    this.afterMatchAdvance();
  }

  /**
   * Shared tail for anything that just advanced `this.match` outside of a fresh
   * shot's own animation — a real shot's finalizeShot(), and a shot-clock
   * timeout (applyTimeoutNow()), both end here: announce newly-finished 3+
   * player placements, flip to the finished banner (and report/persist the
   * game) or back to aiming for whoever's up next, then drain anything that
   * queued up (shot or timeout) while this one was being applied.
   */
  private afterMatchAdvance(): void {
    // 3+ player "play until last": announce each newly-finished player as they place
    // (the final standings are shown on the end banner). Placed players stop getting
    // turns automatically (activePlayerIndex skips them) and just watch the rest.
    if (this.match.players.length >= 3) {
      this.match.players.forEach((pl, i) => {
        if (pl.place == null || this.announcedPlaces.has(i)) return;
        this.announcedPlaces.add(i);
        if (this.match.phase !== 'finished') {
          const ord = t(`ord.${pl.place}`);
          const mine = this.playerIds[i] === this.accountId;
          this.message = mine ? t('placement.you', { place: ord }) : t('placement.other', { name: pl.name, place: ord });
        }
      });
    }

    if (this.match.phase === 'finished') {
      this.phase = 'finished';
      this.cameraRig.setMode('orbit');
      // Victory/defeat cue for the local player only — spectators get their own
      // (silent) announceSpecMatchEnd prompt below, not this.
      if (!this.spectator) {
        const w = this.match.winnerIndex;
        if (w != null && this.match.players.length >= 2) {
          const myIndex = this.online
            ? this.playerIds.indexOf(this.accountId ?? '')
            : this.match.players.findIndex((p) => !p.isAI);
          if (myIndex >= 0) {
            if (myIndex === w) this.sound.victory();
            else this.sound.defeat();
          }
        }
      }
      // a recorded game just ended: save it (per account). The end banner shows a
      // "Review shots" button that opens the panel on demand (openEndReview) — we
      // don't auto-open it so it never covers the result. (post-game "try this
      // layout" practice has no gameLogMeta, so it's skipped.)
      if (this.gameLogMeta) this.persistGameLog();
      // single play that's done → next launch asks the mode instead of auto-resuming
      if (!this.online) localStorage.setItem('carom.finished', '1');
      else if (!this.shotPractice) {
        this.reportMatchResult();
        this.requestMyProfile(); // pull the updated rank/elo/win-rate for the card
      }
      if (this.spectator) this.announceSpecMatchEnd();
    } else {
      this.phase = 'aiming';
      this.faceObjectBall();
      this.applyTurnCamera();
      this.scheduleAIIfNeeded();
    }
    this.refreshHud();
    // Arm the drain for the next queued remote shot or timeout (a bot/opponent run),
    // now that this one is done. drainRemoteQueue() takes one per frame: a
    // fast-forwarded shot applies with no animation, so draining them synchronously
    // settled several inside this one task and the browser never painted the values
    // in between — a 2-shot drain showed up as a single 20-point jump on the
    // countdown. Driven from frame() rather than a bare rAF so the hidden-tab worker
    // ticker keeps it draining too.
    if (this.remoteQueue.length && this.match.phase !== 'finished') this.drainPending = true;
  }

  // ---- shot recorder / game history ----

  /** The current base ball layout as flat table-plane coordinates. */
  private captureBallPositions(): Record<BallId, { x: number; y: number }> {
    const out = {} as Record<BallId, { x: number; y: number }>;
    for (const id of ballsFor(this.variant)) {
      const p = this.base.get(id).pos;
      out[id] = { x: p.x, y: p.y };
    }
    return out;
  }

  /**
   * Begin a fresh recorded game/session. Flushing the previous one is the caller's
   * job (before it swaps `this.match`), so `beginGameLog` only snapshots the header.
   */
  private beginGameLog(mode: GameRecord['mode'], variant: VariantId, format: GameRecord['format']): void {
    this.gameLog = [];
    this.gameLogMeta = {
      id: crypto.randomUUID(),
      variant,
      mode,
      format,
      physics: JSON.parse(JSON.stringify(this.base.constants)) as PhysicsConstants,
      ts: Date.now(),
    };
  }

  /** Append one shot to the in-progress game log (no-op outside a recorded game). */
  private recordShot(positions: Record<BallId, { x: number; y: number }>, result: ShotResult): void {
    if (!this.gameLogMeta || !this.lastCue) return;
    const idx = this.match.activePlayerIndex;
    const player = this.match.players[idx];
    const ev = result.evaluation;
    this.gameLog.push({
      seq: this.gameLog.length,
      shooterIndex: idx,
      shooterName: player?.name ?? '',
      inning: this.match.inningNumber,
      cue: { ...this.lastCue, tipOffset: { ...this.lastCue.tipOffset } },
      positions,
      result: { scored: ev.scored, points: result.appliedPoints, foul: ev.foul },
    });
  }

  /** Assemble the current game's record (or null if nothing was recorded). */
  private buildGameRecord(): GameRecord | null {
    const meta = this.gameLogMeta;
    if (!meta || this.gameLog.length === 0) return null;
    const w = this.match.winnerIndex;
    return {
      id: meta.id,
      ts: meta.ts,
      variant: meta.variant,
      mode: meta.mode,
      format: meta.format,
      physics: meta.physics,
      players: this.match.players.map((p, i) => ({ name: p.name, index: i })),
      winner: w != null ? this.match.players[w]?.name ?? null : null,
      shots: this.gameLog,
    };
  }

  /**
   * Flush the in-progress game log. Only games that actually reached their END are
   * saved to history (and kept as `lastGameRecord` for the end-of-game panel);
   * abandoned solo / vs-AI / online sessions are discarded, not recorded. Clears the
   * log either way; safe to call repeatedly.
   */
  private persistGameLog(): void {
    // nothing recorded yet (also true during the first construct-time buildForVariant,
    // before `this.match` exists) → nothing to flush
    if (!this.gameLogMeta) {
      this.gameLog = [];
      return;
    }
    const rec = this.match?.phase === 'finished' ? this.buildGameRecord() : null;
    this.gameLog = [];
    this.gameLogMeta = null;
    if (!rec) return;
    this.lastGameRecord = rec;
    (this.net ?? this.presence)?.send({ type: 'saveGame', game: rec });
  }

  // ---- shot review / replay / practice ----

  /** Rebuild `base` for a variant + physics and seat the balls at `positions`. */
  private loadPositionsIntoBase(
    positions: Record<BallId, { x: number; y: number }>,
    variant: VariantId = this.variant,
    physics: PhysicsConstants = this.base.constants
  ): void {
    if (variant !== this.variant) {
      this.variant = variant;
      this.rebuildBalls(variant);
      this.rebuildScenery();
      this.cameraRig.setTable(tableFor(variant));
    }
    this.base = createCaromWorld(variant, physics);
    for (const id of ballsFor(variant)) {
      const b = this.base.get(id);
      const p = positions[id];
      if (p) b.pos = { x: p.x, y: p.y, z: b.pos.z };
      b.vel = { x: 0, y: 0, z: 0 };
      b.angVel = { x: 0, y: 0, z: 0 };
      b.state = 'stationary';
    }
    this.syncBalls();
  }

  /** Open the end-of-game review panel for the game that just finished. */
  private openEndReview(): void {
    const game = this.lastGameRecord;
    if (!game || !game.shots.length) return;
    this.reviewPanel.open(game, {
      title: t('review.title'),
      subtitle: t('review.subtitleEnd'),
      actions: [
        { key: 'replay', label: t('review.replay'), primary: true },
        { key: 'practice', label: t('review.tryLayout') },
        // Always offered: in a room this posts the shot to chat, and either way it
        // hands back a link anyone can open — the only organic reach the game has.
        { key: 'share', label: t('share.shot') },
      ],
      onAction: (key, rec) => {
        if (key === 'replay') this.replayRecordedShot(game, rec, true);
        else if (key === 'share') this.shareShot(game, rec);
        else this.tryRecordedLayout(game, rec);
      },
    });
  }

  // ---- shot sharing ----

  /**
   * Share one of my shots. Only the pre-shot layout and the cue travel — the
   * engine is deterministic, so that's all anyone needs to see exactly what
   * happened.
   *
   * Two audiences, one payload: in a room it posts to chat (instant, in context),
   * and it ALWAYS produces a permalink for everyone else — the whole shot is
   * packed into the URL fragment, so someone who has never opened the game can
   * click it and watch. That link is the only way a shot leaves the building.
   */
  private shareShot(game: GameRecord, rec: ShotRecord): void {
    const shot: SharedShot = {
      variant: game.variant,
      positions: rec.positions,
      cue: rec.cue,
      label: rec.result.scored ? t('review.scored', { p: rec.result.points }) : t('review.miss'),
    };
    if (this.online && this.net) {
      this.net.send({ type: 'chat', text: '', shot });
      this.chat.system(t('share.shared'));
    }
    this.offerShotLink(shot);
  }

  /**
   * Hand the player the external link. Copying is attempted eagerly (we're inside
   * the share click, so the clipboard is permitted), but the URL is also shown
   * selectable: clipboard writes fail silently on insecure origins and in some
   * embedded browsers, and a share button that quietly did nothing would be worse
   * than one that just shows you the link.
   */
  private offerShotLink(shot: SharedShot): void {
    const url = shotLinkUrl(shot);
    const copy = (): void => {
      void navigator.clipboard?.writeText(url).catch(() => {});
    };
    copy();
    const box = document.createElement('div');
    box.className = 'share-link';
    box.textContent = url;
    const msg = document.createElement('div');
    msg.append(document.createTextNode(t('share.linkReady')), box);
    this.showPrompt(
      msg.innerHTML,
      t('share.copyLink'),
      () => {
        // Native sheet where there is one (phones): it reaches KakaoTalk etc.
        // directly. Elsewhere the clipboard is the share sheet.
        if (navigator.share) void navigator.share({ url, text: shot.label }).catch(() => {});
        else copy();
        this.message = t('share.linkCopied');
        this.refreshHud();
      },
      t('share.close'),
      () => {}
    );
  }

  /**
   * Someone opened a shot permalink. They may never have played: give them the
   * shot first (it plays itself), then the two things they might want next —
   * take the shot on, or go find the game.
   */
  private openLinkedShot(shot: SharedShot): void {
    const positions = shot.positions as Record<BallId, { x: number; y: number }>;
    // Set the table up exactly as "practise this shot" does, so taking it over is
    // a no-op afterwards — but don't grab the cue (or pointer lock: a page that
    // just loaded has no user gesture to spend on it) while the shot plays.
    this.practiceLayout(positions, shot.cue, shot.variant, PHYSICS, false);
    this.playLinkedShot(shot);
  }

  /** Play the linked shot, then offer the visitor somewhere to go. */
  private playLinkedShot(shot: SharedShot): void {
    this.loadPositionsIntoBase(shot.positions as Record<BallId, { x: number; y: number }>, shot.variant, PHYSICS);
    this.azimuth = shot.cue.azimuth;
    this.spin = { ...shot.cue.tipOffset };
    this.power01 = this.speedToPower(shot.cue.speed);
    this.pendingRemote = null;
    const sim = simulate(this.base, shot.cue);
    this.lastShot = sim;
    this.message = shot.label ? t('share.viewing', { label: shot.label }) : t('share.viewingPlain');
    this.afterReplay = () => {
      this.showPrompt(
        t('share.visitorPrompt'),
        t('share.tryIt'),
        () => this.rePractice(), // seats the layout again and drops into aim mode
        t('share.watchAgain'),
        () => this.playLinkedShot(shot)
      );
    };
    this.beginStrike(shot.cue, sim, true);
    this.refreshHud();
  }

  /**
   * Play a shot someone shared. Only ever local — a shared shot is a clip, so it
   * must never disturb a live game (or an ongoing daily attempt).
   */
  private replaySharedShot(shot: SharedShot): void {
    if (this.phase === 'simulating' || this.stroking || this.dailyRun) return;
    // Never take over a table someone else is also using: a live match, or the
    // waiting room's SHARED practice table (moving those balls locally would
    // silently desync everyone else in the room).
    if ((this.gameActive && !this.spectator) || this.waiting) {
      this.chat.system(t('share.busy'));
      return;
    }
    this.reviewPanel.hide();
    this.loadPositionsIntoBase(shot.positions as Record<BallId, { x: number; y: number }>, shot.variant, PHYSICS);
    this.azimuth = shot.cue.azimuth;
    this.spin = { ...shot.cue.tipOffset };
    this.power01 = this.speedToPower(shot.cue.speed);
    this.pendingRemote = null;
    const sim = simulate(this.base, shot.cue);
    this.lastShot = sim;
    // same non-scoring playback the review panel uses (stroke telegraph, then roll)
    this.beginStrike(shot.cue, sim, true);
  }

  /** Replay a recorded shot locally (and, in a room, synced to everyone). */
  private replayRecordedShot(game: GameRecord, rec: ShotRecord, allowSync: boolean): void {
    this.loadPositionsIntoBase(rec.positions, game.variant, game.physics);
    this.azimuth = rec.cue.azimuth;
    this.spin = { ...rec.cue.tipOffset };
    this.power01 = this.speedToPower(rec.cue.speed);
    this.pendingRemote = null;
    const shot = simulate(this.base, rec.cue);
    this.lastShot = shot;
    // telegraph the stroke (pause + draw the cue back) before the balls roll, then
    // hand off to a non-scoring replay playback
    this.beginStrike(rec.cue, shot, true);
    if (allowSync && this.online && this.net && !this.spectator) {
      this.net.send({ type: 'reviewShot', mode: 'replay', positions: rec.positions, cue: rec.cue });
    }
  }

  /** "Try this layout": in a room, reset everyone's table to it for shared practice;
   *  offline, drop into solo practice at that layout. */
  private tryRecordedLayout(game: GameRecord, rec: ShotRecord): void {
    if (this.online && this.opponentPresent && !this.spectator) {
      this.enterShotPractice(rec.positions, game.variant, game.physics, rec.cue, true);
      this.net?.send({ type: 'reviewShot', mode: 'practice', positions: rec.positions });
    } else {
      this.practiceFromShot(game, rec);
    }
    // keep the review panel open so the player can pick another shot to practise
  }

  /** Enter shared, non-scoring practice with the balls seated at `positions`.
   *  `enterAim` drops the local initiator straight into aim mode (remote receivers
   *  don't — there's no user gesture to grab pointer-lock). */
  private enterShotPractice(
    positions: Record<BallId, { x: number; y: number }>,
    variant: VariantId = this.variant,
    physics?: PhysicsConstants,
    cue?: CueParams,
    enterAim = false
  ): void {
    const phys = physics ?? this.base.constants;
    this.loadPositionsIntoBase(positions, variant, phys);
    this.match = createMatch({ ...this.matchConfig(this.variant), startingPlayerIndex: 0 });
    this.shotPractice = true;
    this.practiceCtx = { positions, cue, variant: this.variant, physics: phys };
    this.phase = 'aiming';
    this.currentShot = null;
    this.lastShot = null;
    this.replaying = false;
    this.pendingRemote = null;
    this.message = t('review.practiceMode');
    this.faceObjectBall();
    this.applyTurnCamera();
    if (enterAim) this.setAimActive(true); // straight into aiming
    this.refreshHud();
  }

  /** Offline: load a recorded shot's layout into a fresh solo practice game. */
  private practiceFromShot(game: GameRecord, rec: ShotRecord): void {
    this.practiceLayout(rec.positions, rec.cue, game.variant, game.physics);
  }

  /**
   * Seat a layout + cue in a fresh solo practice game. Shared by "practise this
   * shot" and by the shot-permalink viewer; `enterAim` is what separates them —
   * a visitor watches the shot first, so the cue isn't handed over yet.
   */
  private practiceLayout(
    positions: Record<BallId, { x: number; y: number }>,
    cue: CueParams,
    variant: VariantId,
    physics: PhysicsConstants,
    enterAim = true
  ): void {
    this.mainMenu?.hide();
    this.menuOpen = false;
    this.assessing = false;
    this.variant = variant; // setMode builds the table for the shot's own variant
    this.setMode('solo'); // leave any online room + build a solo baseline table
    this.loadPositionsIntoBase(positions, variant, physics);
    this.match = createMatch({ ...this.matchConfig(variant), solo: true, startingPlayerIndex: 0 });
    this.shotPractice = false;
    this.practiceCtx = { positions, cue, variant, physics };
    this.phase = 'aiming';
    this.currentShot = null;
    this.lastShot = null;
    this.replaying = false;
    this.pendingRemote = null;
    this.azimuth = cue.azimuth;
    this.spin = { ...cue.tipOffset };
    this.power01 = this.speedToPower(cue.speed);
    this.hud.setSpin(this.spin.a, this.spin.b);
    this.hud.setPower(this.power01);
    this.beginGameLog('solo', variant, 'solo'); // record the practice attempts too
    this.syncBalls();
    this.faceObjectBall();
    this.applyTurnCamera();
    this.refreshPresence();
    if (enterAim) this.setAimActive(true); // drop the player straight into aim mode
    this.message = t('review.practiceLoaded');
    this.refreshHud();
  }

  /** Reload the practised shot's starting layout to try it again (the "re-practice"
   *  button, and the old 'r' key). Falls back to the generic solo rewind. */
  private rePractice(): void {
    if (this.dailyRun) return; // 이번주의 매치: no 다시 연습 mid-attempt
    const ctx = this.practiceCtx;
    if (!ctx) {
      this.resetToLastShot(); // no recorded-shot context → the plain solo rewind
      return;
    }
    this.stroking = false; // cut a held stroke-draw short, same as firing/cancelling it
    this.loadPositionsIntoBase(ctx.positions, ctx.variant, ctx.physics);
    this.match = createMatch({ ...this.matchConfig(ctx.variant), solo: !this.shotPractice, startingPlayerIndex: 0 });
    if (ctx.cue) {
      this.azimuth = ctx.cue.azimuth;
      this.spin = { ...ctx.cue.tipOffset };
      this.power01 = this.speedToPower(ctx.cue.speed);
    }
    this.elevation = 0;
    this.hud.setSpin(this.spin.a, this.spin.b);
    this.hud.setPower(this.power01);
    this.hud.setTilt(0);
    this.phase = 'aiming';
    this.currentShot = null;
    this.lastShot = null;
    this.replaying = false;
    this.pendingRemote = null;
    this.preShot = null;
    this.syncBalls();
    this.faceObjectBall();
    this.applyTurnCamera();
    this.setAimActive(true); // straight back into aiming
    this.message = t('review.practiceLoaded');
    // shared room practice: reset everyone to the layout too
    if (this.online && this.shotPractice && this.net) {
      this.net.send({ type: 'reviewShot', mode: 'practice', positions: ctx.positions });
    }
    this.refreshHud();
  }

  /** A peer triggered a synced replay or a shared "try this layout" from the review panel. */
  private applyReviewShot(m: { mode: 'replay' | 'practice'; positions: Record<BallId, { x: number; y: number }>; cue?: CueParams }): void {
    if (m.mode === 'replay' && m.cue) {
      this.loadPositionsIntoBase(m.positions);
      this.azimuth = m.cue.azimuth;
      this.spin = { ...m.cue.tipOffset };
      this.power01 = this.speedToPower(m.cue.speed);
      this.pendingRemote = null;
      const shot = simulate(this.base, m.cue);
      this.lastShot = shot;
      this.beginStrike(m.cue, shot, true); // telegraph the stroke before the roll
    } else if (m.mode === 'practice') {
      this.enterShotPractice(m.positions);
    }
  }

  // ---- personal game history ----

  /** Ask the server for my recent recorded games (reply opens the history list). */
  private requestGameHistory(): void {
    (this.presence ?? this.net)?.send({ type: 'gameHistory' });
  }

  private onGameHistory(games: GameSummary[]): void {
    this.mainMenu.hide(); // the full-screen menu would otherwise cover the list
    this.historyPanel.open(games);
  }

  /** History list → open one game's full shot log (fetch, then show the review panel). */
  private openHistoryGame(id: string): void {
    (this.presence ?? this.net)?.send({ type: 'gameDetail', id });
  }

  private onGameDetail(game: GameRecord | null): void {
    if (!game || !game.shots.length) return;
    this.historyPanel.hide();
    this.reviewPanel.open(game, {
      title: t('review.historyTitle'),
      subtitle: t('review.subtitleHistory', { n: game.shots.length }),
      actions: [
        { key: 'practice', label: t('review.practiceShot'), primary: true },
        { key: 'replay', label: t('review.replay') },
        { key: 'share', label: t('share.shot') },
      ],
      onAction: (key, rec) => {
        if (key === 'practice') this.practiceFromShot(game, rec);
        else if (key === 'share') this.shareShot(game, rec);
        else this.replayRecordedShot(game, rec, false);
      },
      onBack: () => {
        this.reviewPanel.hide();
        this.requestGameHistory(); // back to the game list
      },
      // closing the review → back to the menu, unless we've dropped into practice
      onClose: () => this.menuOpen && this.mainMenu.show(),
    });
  }

  // ---- item store ----

  /** Open the store: shows the cached balance/ownership immediately, then refreshes. */
  private openStore(): void {
    if (!this.features.has('itemStore')) return; // store flagged off (server/flags.mjs)
    this.mainMenu.hide(); // the full-screen menu would otherwise cover the panel
    this.storePanel.open(this.coins, [...this.owned], this.myCueColor);
    (this.presence ?? this.net)?.send({ type: 'store' });
  }

  private onStoreState(coins: number, owned: string[]): void {
    this.coins = coins;
    this.owned = new Set(owned);
    this.emoteWheel?.setOwned(this.owned);
    if (this.storePanel.isOpen) this.storePanel.setState(coins, owned, this.myCueColor);
    this.syncCoins();
  }

  private onPurchaseResult(m: { ok: boolean; coins?: number; owned?: string[]; error?: string }): void {
    if (m.ok && m.coins !== undefined && m.owned) this.onStoreState(m.coins, m.owned);
    // errors (insufficient coins / already owned) are rare given the UI already
    // disables those buttons — no dedicated toast, just leave the balance as-is.
  }

  private onEquipResult(m: { ok: boolean; cueColor?: string | null; error?: string }): void {
    if (!m.ok) return;
    this.myCueColor = m.cueColor ?? null;
    if (this.storePanel.isOpen) this.storePanel.setState(this.coins, [...this.owned], this.myCueColor);
  }

  private onRenameResult(m: { ok: boolean; name?: string; coins?: number; error?: string }): void {
    if (!m.ok || !m.name) return;
    if (this.guest) this.nickname = m.name;
    else this.account = m.name;
    if (typeof m.coins === 'number') this.coins = m.coins;
    this.presencePanel?.setMyName(this.myDisplayName());
    this.mainMenu?.setProfile(this.account ?? this.nickname, this.myProfile, !this.account);
    if (this.storePanel.isOpen) this.storePanel.setState(this.coins, [...this.owned], this.myCueColor);
    this.syncCoins();
  }

  // ---- 건의하기 (one-way bug report / feedback) ----

  /** Open the feedback modal, ready to compose a fresh message. */
  private openFeedback(): void {
    this.mainMenu.hide();
    this.feedbackPanel.open();
  }

  // ---- ranking card / 코인 rewards ----

  /** Adopt a fresh ranking card: tier (drives ranked-by-default), ladder progress, balance. */
  private onMyProfile(profile: MyProfile | null): void {
    this.myProfile = profile;
    this.myTier = profile ? profile.tier : null;
    const coins = profile && typeof profile.coins === 'number' ? profile.coins : null;
    // a mid-shot profile refresh must not reveal the payout before the balls stop
    if (coins != null && !this.shotInFlight()) this.coins = coins;
    else if (coins != null) this.heldCoinUpdates.push(() => { this.coins = coins; this.syncCoins(); });
    this.mainMenu?.setProfile(this.account ?? this.nickname, profile, !this.account);
    this.lobby?.setMyTier(this.myTier);
    this.syncCoins();
  }

  /** A shot is still on screen (or about to be) — payouts wait for it to finish. */
  private shotInFlight(): boolean {
    return !!(this.currentShot || this.oppStrike || this.drainPending || this.remoteQueue.length ||
      this.phase === 'strike' || this.phase === 'simulating');
  }

  /** Reveal held payout toasts / balance changes once the shot animation is over. */
  private flushHeldCoinUpdates(): void {
    if (!this.heldCoinUpdates.length || this.shotInFlight()) return;
    const held = this.heldCoinUpdates;
    this.heldCoinUpdates = [];
    for (const fn of held) fn();
  }

  /** A finished game paid out — show what it earned and keep the balance current. */
  private onEarned(events: CoinEvent[], coins: number): void {
    // the server pays out the instant the final shot is submitted, but the balls
    // are still rolling — hold the toast and the new balance until they stop
    if (this.shotInFlight()) {
      this.heldCoinUpdates.push(() => this.onEarned(events, coins));
      return;
    }
    this.coins = coins;
    if (this.myProfile) this.myProfile = { ...this.myProfile, coins };
    if (this.storePanel.isOpen) this.storePanel.setState(coins, [...this.owned]);
    this.rewardToast.show(events, coins);
    this.syncCoins();
  }

  /** Push the 코인 balance to everything that gates on it: the room browsers
   *  (you can't take a seat in a staked room you can't cover), the create
   *  form's stake picker, and the in-match player card (its coin line is a
   *  point-in-time snapshot from 'playersStats' otherwise, so a mid-match
   *  payout — ranked reward or stake settlement — would sit stale until the
   *  next seat change). Call after anything that moves the balance. */
  private syncCoins(): void {
    this.mainMenu?.setCoins(this.coins);
    this.lobby?.setCoins(this.coins);
    const mySeat = this.seatStats[this.seat];
    if (mySeat && mySeat.coins !== this.coins) {
      this.seatStats = this.seatStats.map((st, i) => (i === this.seat ? { ...st!, coins: this.coins } : st));
      this.refreshHud();
    }
  }

  // ---- 당구 교실 (tutorial) ----

  /**
   * Recompute tutorial progress and hand it to the main menu: the overall
   * device curriculum (drives the 튜토리얼 button's own sub-label) and each of
   * the three courses (drives the course-picker cards). Call after any change
   * to `tutorialDone`.
   */
  private refreshTutorialProgress(): void {
    const device = missionsFor(this.touch);
    this.mainMenu.setTutorialProgress(
      tutorialStatus(this.tutorialDone, device),
      clearedCount(this.tutorialDone, device),
      device.length
    );
    this.mainMenu.setCourseProgress(
      COURSES.map((c) => {
        const list = missionsForCourse(c.id, this.touch);
        return {
          id: c.id,
          status: tutorialStatus(this.tutorialDone, list),
          done: clearedCount(this.tutorialDone, list),
          total: list.length,
        };
      })
    );
  }

  /**
   * Enter one of the three tutorial courses, resuming at the first mission in
   * it the player hasn't cleared.
   *
   * A lesson is a solo session with a handicap it can never run out (the mission,
   * not the scoreboard, decides when you're done), the aim guide forced on, and
   * the offline 4-ball corner break suppressed so the mission's layout is the
   * layout you actually shoot.
   */
  private startTutorial(course: CourseId): void {
    this.abandonDailyRun();
    this.dailyPanel.hide();
    this.assessing = false;
    this.assessResult = '';
    this.menuOpen = false;
    this.mainMenu.hide();
    this.reviewPanel.dismiss();
    this.historyPanel.hide();
    this.handicaps = [ASSESS_HANDICAP, ASSESS_HANDICAP];
    if (!this.guideOn) this.toggleGuide(); // a beginner starts with the line drawn
    this.tutorialCourse = course;
    this.tutorialMissions = missionsForCourse(course, this.touch);
    const first = nextMission(this.tutorialDone, this.tutorialMissions) ?? this.tutorialMissions[0];
    this.enterMission(first, true);
    this.tutorialPanel.show();
    this.message = t('tut.msg.start');
    this.refreshHud();
  }

  /**
   * Make `mission` the current one and set its table up. `rebuild` is for the
   * first mission of a session (it may have to leave an online room); afterwards
   * the table is only rebuilt when the mission changes variant, since rebuilding
   * throws away the scene for no visible gain.
   */
  private enterMission(mission: Mission, rebuild: boolean): void {
    const variantChanged = this.variant !== mission.variant;
    // Set BEFORE any rebuild: it suppresses the corner break that would otherwise
    // scatter the layout we are about to place.
    this.tutorial = { mission, state: 'doing', actions: new Set() };
    this.variant = mission.variant;
    if (rebuild) this.setMode('solo');
    else if (variantChanged) this.buildForVariant(mission.variant);
    this.placeMissionLayout(mission);
    this.renderTutorial();
  }

  /** Put the mission's balls on the table and hand the player a clean cue. */
  private placeMissionLayout(mission: Mission): void {
    const layout = resolveLayout(mission);
    this.tutorialHint.setPath(resolveHint(mission), ballRadius(mission.variant));
    for (const id of ballsFor(mission.variant)) {
      const p = layout[id];
      if (!p) continue;
      const b = this.base.get(id);
      b.pos = { x: p.x, y: p.y, z: b.radius };
      b.vel = { x: 0, y: 0, z: 0 };
      b.angVel = { x: 0, y: 0, z: 0 };
      b.state = 'stationary';
    }
    // Reset the cue itself too: a mission that asks for a specific tip contact or
    // elevation has to start from centre-ball and level, or the player inherits
    // the previous lesson's answer and can pass without touching the control.
    this.spin = { a: 0, b: 0 };
    this.hud.setSpin(0, 0);
    this.elevation = 0;
    this.hud.setTilt(0);
    this.phase = 'aiming';
    this.syncBalls();
    this.applyStartAngle(mission.cueAngle);
    this.applyTurnCamera();
  }

  /**
   * Judge the shot that just finished against the current mission.
   *
   * Every path through here must end with the player able to shoot again:
   * `finalizeShot` returns early for us, so the phase reset at the bottom of it
   * never runs. Shooting on during an action mission, or after already clearing
   * one, is allowed and simply doesn't re-judge anything.
   */
  private tallyTutorial(ctx: ShotContext): void {
    const tut = this.tutorial;
    if (!tut?.mission) return;
    // Action missions are cleared by controls, and a cleared mission stays
    // cleared — in both cases the shot was just practice.
    if (tut.mission.actions || tut.state === 'passed') {
      this.resumeAiming();
      return;
    }
    if (shotSatisfies(tut.mission, ctx)) {
      this.completeMission();
      return;
    }
    // A miss is never a failure: say what was missing and (for missions whose
    // layout IS the lesson) hand back the same picture to try again.
    tut.state = 'retry';
    if (tut.mission.retryLayout) this.placeMissionLayout(tut.mission);
    else this.resumeAiming();
    this.renderTutorial();
  }

  /**
   * Hand the table back to the player after a tutorial shot, without moving a
   * ball. `placeMissionLayout` does this too — this is for the paths that leave
   * the balls where they came to rest.
   */
  private resumeAiming(): void {
    this.phase = 'aiming';
    this.faceObjectBall();
    this.applyTurnCamera();
  }

  /**
   * Record that the player used a control. Action missions complete here; for
   * every other mission this is a no-op, so callers can report freely.
   */
  private noteTutorialAction(action: TutorialAction): void {
    const tut = this.tutorial;
    if (!tut?.mission?.actions || tut.state === 'passed') return;
    if (tut.actions.has(action)) return;
    tut.actions.add(action);
    if (actionsSatisfied(tut.mission, tut.actions)) this.completeMission();
    else this.renderTutorial();
  }

  /**
   * Mark the current mission cleared: chime, show the "cleared" banner, and
   * wait a beat before moving on by itself — no button to press.
   */
  private completeMission(): void {
    const tut = this.tutorial;
    if (!tut?.mission) return;
    this.tutorialDone.add(tut.mission.id);
    saveProgress(this.tutorialDone);
    this.refreshTutorialProgress();
    tut.state = 'passed';
    this.sound.tutorialPass();
    this.message = t('tut.msg.passed');
    // Keep the table live while the player reads the result: the wait is not a
    // modal, and knocking the balls about in between is fine. Only re-arm when
    // the phase actually needs it — an action mission completes mid-aim, and
    // resumeAiming would snap the cue round the moment a checkbox ticked.
    if (this.phase !== 'aiming') this.resumeAiming();
    this.renderTutorial();
    this.refreshHud();
    // …then move on by itself once the chime and banner have had a moment to land.
    this.clearTutorialTimer();
    this.tutorialNextTimer = setTimeout(() => {
      this.tutorialNextTimer = null;
      if (this.tutorial?.state === 'passed') this.advanceMission();
    }, TUTORIAL_ADVANCE_MS);
  }

  private clearTutorialTimer(): void {
    if (!this.tutorialNextTimer) return;
    clearTimeout(this.tutorialNextTimer);
    this.tutorialNextTimer = null;
  }

  /** Next button: move on to the next unfinished mission, or close the course. */
  private advanceMission(): void {
    this.clearTutorialTimer();
    const cur = this.tutorial?.mission;
    if (!cur) return;
    const next = nextMissionAfter(cur, this.tutorialDone, this.tutorialMissions);
    if (!next) {
      this.tutorial = { mission: null, state: 'doing', actions: new Set() };
      this.message = t('tut.msg.done');
      this.resumeAiming(); // the closing card still leaves a playable table behind it
      this.renderTutorial();
      this.refreshHud();
      return;
    }
    this.enterMission(next, false);
  }

  /**
   * Skip: leave the mission unfinished and move on. Skipped missions come back
   * around at the end (see nextMissionAfter) rather than being lost — the course
   * is only "complete" when every step really is.
   */
  private skipMission(): void {
    this.clearTutorialTimer();
    const cur = this.tutorial?.mission;
    if (!cur) return;
    const next = nextMissionAfter(cur, this.tutorialDone, this.tutorialMissions);
    if (!next || next.id === cur.id) {
      this.exitTutorial();
      return;
    }
    this.enterMission(next, false);
  }

  /** Put the balls back where this mission started them. */
  private resetMission(): void {
    const tut = this.tutorial;
    if (!tut?.mission || this.phase === 'simulating' || this.stroking) return;
    this.clearTutorialTimer(); // reset means "stay here", so cancel the auto-advance
    tut.state = 'doing';
    this.placeMissionLayout(tut.mission);
    this.renderTutorial();
    this.refreshHud();
  }

  /** Leave the tutorial the way the player asked to: back to the start menu. */
  private exitTutorial(): void {
    this.endTutorialSession();
    this.openMainMenu();
  }

  /**
   * Tear the session down without deciding where to go next. Safe to call when no
   * tutorial is running, so every "we're leaving this screen" path can just call
   * it (openMainMenu, startSingle).
   */
  private endTutorialSession(): void {
    this.clearTutorialTimer(); // never let a timer fire into a torn-down session
    if (!this.tutorial) return;
    this.tutorial = null;
    this.tutorialCourse = null;
    this.tutorialPanel.hide();
    this.message = '';
    this.refreshPresence(); // no longer 'tutorial' — falls back to solo/standby
  }

  private renderTutorial(): void {
    const tut = this.tutorial;
    if (!tut || !this.tutorialCourse) return;
    this.tutorialPanel.render({
      course: this.tutorialCourse,
      missions: this.tutorialMissions,
      mission: tut.mission,
      index: tut.mission ? this.tutorialMissions.indexOf(tut.mission) : this.tutorialMissions.length - 1,
      done: this.tutorialDone,
      state: tut.state,
      actionsDone: tut.actions,
      touch: this.touch,
    });
  }

  // ---- 오늘의 배치 (daily challenge) ----

  /** Open the daily panel and ask the server for the current layout + board. */
  private openDaily(): void {
    const sock = this.presence ?? this.net;
    if (!sock) {
      this.mainMenu.show(); // no socket to ask — stay put rather than blanking the menu
      return;
    }
    this.mainMenu.hide(); // the full-screen menu would otherwise cover the panel
    // We may have nothing cached yet (first open). Remember that the panel is
    // WANTED so the reply opens it — without this the menu is hidden and no
    // panel ever appears, which looks exactly like a hang.
    this.dailyWanted = true;
    if (this.daily) this.dailyPanel.open(this.daily);
    sock.send({ type: 'daily' });
    // …and if the server never answers, put the menu back rather than stranding
    // the player on a table with no UI.
    if (this.dailyTimer) clearTimeout(this.dailyTimer);
    this.dailyTimer = setTimeout(() => {
      this.dailyTimer = null;
      if (!this.dailyWanted || this.dailyPanel.isOpen) return;
      this.dailyWanted = false;
      if (this.menuOpen) this.mainMenu.show();
    }, 6000);
  }

  /** Adopt a daily snapshot (plain, or the one bundled with an attempt's result). */
  private onDaily(m: Extract<ServerMessage, { type: 'daily' | 'dailyResult' }>): void {
    if (this.dailyTimer) {
      clearTimeout(this.dailyTimer);
      this.dailyTimer = null;
    }
    this.daily = {
      day: m.day,
      endsAt: m.endsAt,
      variant: m.variant,
      target: m.target,
      maxAttempts: m.maxAttempts,
      maxStrokes: m.maxStrokes,
      positions: m.positions,
      ruleType: m.ruleType,
      minBanks: m.minBanks,
      cueAngle: m.cueAngle,
      title: m.title,
      timeLimitSec: m.timeLimitSec,
      mine: m.mine,
      entries: m.entries,
    };
    this.mainMenu.setDaily(this.daily); // keep the row-3 mission card's detail line current
    if (m.type === 'dailyResult') {
      if (m.ok && m.events?.length && m.coins !== undefined) this.onEarned(m.events, m.coins);
      const run = this.lastDailyRun ?? { strokes: 0, caroms: 0, banks: 0, elapsedMs: null };
      this.dailyWanted = false;
      this.dailyPanel.showResult(this.daily, !!m.cleared, run.strokes, run.caroms, run.elapsedMs ?? undefined);
      this.lastDailyRun = null;
      return;
    }
    // Open on the FIRST snapshot when the panel was requested — otherwise the
    // menu is already hidden and nothing would ever appear.
    if (this.dailyPanel.isOpen || this.dailyWanted) {
      this.dailyWanted = false;
      this.dailyPanel.open(this.daily);
    }
  }

  /**
   * Merge admin-authored 당구 교실 content (custom lessons + overrides on the
   * built-in curriculum) into the runtime curriculum, then refresh anything
   * already showing counts/labels derived from it.
   */
  private onTutorialContent(m: Extract<ServerMessage, { type: 'tutorialContent' }>): void {
    applyTutorialContent({ lessons: m.lessons, overrides: m.overrides });
    this.refreshTutorialProgress();
  }

  /**
   * Begin an attempt: a solo table built for the day's variant, with the balls
   * placed at the exact shared layout. Misses don't end the run — every stroke
   * just counts — so the score is purely "how few strokes did that take".
   */
  private startDailyRun(): void {
    const d = this.daily;
    if (!d) return;
    this.dailyPanel.hide();
    this.dailyWanted = false;
    if (this.dailyTimer) {
      clearTimeout(this.dailyTimer);
      this.dailyTimer = null;
    }
    if (this.dailyCountdown) {
      clearTimeout(this.dailyCountdown);
      this.dailyCountdown = null;
    }
    if (this.dailyTick) {
      clearInterval(this.dailyTick);
      this.dailyTick = null;
    }
    // Set BEFORE the rebuild: it also suppresses the offline 4-ball corner break,
    // which would otherwise scatter the very layout we're about to place.
    this.dailyRun = { strokes: 0, caroms: 0, banks: 0, startTs: Date.now() };
    this.assessing = false;
    this.menuOpen = false;
    this.mainMenu.hide();
    this.reviewPanel.dismiss();
    // A handicap high enough that the match can never "clear" on its own — the
    // attempt ends on the day's target or the stroke cap, which we own here.
    this.handicaps = [ASSESS_HANDICAP, ASSESS_HANDICAP];
    this.variant = d.variant;
    this.setMode('solo'); // leaves online if needed, then builds the day's variant
    this.placeDailyLayout(d);
    this.message =
      d.ruleType === 'banks' ? t('daily.goalBanks', { t: d.target, b: d.minBanks })
      : d.ruleType === 'timedScore' ? t('daily.goalTimed', { s: d.timeLimitSec ?? 60 })
      : d.ruleType === 'timedSpeed' ? t('daily.goalSpeed', { t: d.target })
      : t('daily.goal', { t: d.target });
    // Time Attack (score): the clock itself ends the run — the target/stroke
    // cap don't apply, so nothing else in tallyDaily ever finishes this rule.
    if (d.ruleType === 'timedScore' && d.timeLimitSec) {
      this.dailyCountdown = setTimeout(() => this.finishDailyRun(false), d.timeLimitSec * 1000);
    }
    // Both Time Attack rules show a live-updating timer chip in the HUD.
    if (d.ruleType === 'timedScore' || d.ruleType === 'timedSpeed') {
      this.dailyTick = setInterval(() => this.refreshHud(), 250);
    }
    this.refreshHud();
  }

  /** Put the day's shared layout on the table (identical for every player). */
  private placeDailyLayout(d: DailyState): void {
    for (const id of ballsFor(d.variant)) {
      const p = d.positions?.[id];
      const b = this.base.get(id);
      if (!p) continue;
      b.pos = { x: p.x, y: p.y, z: b.radius };
      b.vel = { x: 0, y: 0, z: 0 };
      b.angVel = { x: 0, y: 0, z: 0 };
      b.state = 'stationary';
    }
    this.phase = 'aiming';
    this.syncBalls();
    this.applyStartAngle(d.cueAngle);
    this.applyTurnCamera();
  }

  /**
   * Count one attempt stroke; finish when the target is run (and, for a
   * 'banks' mission, enough of those caroms were banks) or the cap is hit.
   */
  private tallyDaily(scored: boolean, banked: boolean): void {
    const d = this.daily;
    const run = this.dailyRun;
    if (!d || !run) return;
    run.strokes += 1;
    if (scored) run.caroms += 1;
    if (scored && banked) run.banks += 1;

    if (d.ruleType === 'timedScore') {
      // Only the countdown (see startDailyRun) ends this rule — every stroke
      // just keeps adding to the score, there's no target or stroke cap.
      this.phase = 'aiming';
      this.faceObjectBall();
      this.applyTurnCamera();
      this.message = t('daily.hudTimed', { a: run.caroms });
      return;
    }

    const bankOk = d.ruleType === 'banks' ? run.banks >= d.minBanks : true;
    const strokeCapHit = d.maxStrokes > 0 && run.strokes >= d.maxStrokes;
    if ((run.caroms >= d.target && bankOk) || strokeCapHit) {
      this.finishDailyRun(run.caroms >= d.target && bankOk);
      return;
    }
    this.phase = 'aiming';
    this.faceObjectBall();
    this.applyTurnCamera();
    this.message =
      d.ruleType === 'banks' ? t('daily.hudBanks', { a: run.caroms, t: d.target, b: run.banks, m: d.minBanks, n: run.strokes })
      : d.ruleType === 'timedSpeed' ? t('daily.hudSpeed', { a: run.caroms, t: d.target, e: Math.floor((Date.now() - run.startTs) / 1000) })
      : t('daily.hud', { a: run.caroms, t: d.target, n: run.strokes });
  }

  /** Report the finished attempt; the server scores it and pays any 코인. */
  private finishDailyRun(cleared: boolean): void {
    const run = this.dailyRun;
    const d = this.daily;
    if (!run || !d) return;
    if (this.dailyCountdown) {
      clearTimeout(this.dailyCountdown);
      this.dailyCountdown = null;
    }
    if (this.dailyTick) {
      clearInterval(this.dailyTick);
      this.dailyTick = null;
    }
    const elapsedMs = d.ruleType === 'timedSpeed' && cleared ? Date.now() - run.startTs : null;
    this.lastDailyRun = { strokes: run.strokes, caroms: run.caroms, banks: run.banks, elapsedMs };
    this.dailyRun = null;
    this.phase = 'finished';
    this.cameraRig.setMode('orbit');
    this.message =
      d.ruleType === 'timedScore' ? t('daily.timeUp', { n: run.caroms })
      : d.ruleType === 'timedSpeed' && cleared && elapsedMs != null ? t('daily.clearedTime', { s: (elapsedMs / 1000).toFixed(1) })
      : cleared ? t('daily.cleared', { n: run.strokes })
      : t('daily.failed', { n: run.caroms });
    (this.presence ?? this.net)?.send({
      type: 'dailySubmit', strokes: run.strokes, caroms: run.caroms, banks: run.banks,
      elapsedMs: elapsedMs ?? undefined,
    });
    this.refreshHud();
  }

  // ---- head-to-head / 재대결 ----

  /** Ask for my record against everyone currently seated (excluding myself). */
  private requestHeadToHead(): void {
    if (!this.accountId) return;
    const ids = this.playerIds.filter((id) => id && id !== this.accountId);
    if (ids.length) (this.net ?? this.presence)?.send({ type: 'headToHead', ids });
  }

  /**
   * Invite a player back for another game; the server opens a room if needed.
   *
   * This MUST go over the game socket, never the always-on presence one: the
   * server seats the sender in the room it opens, and seating the presence
   * connection would leave a phantom player in a room the game socket knows
   * nothing about. If we're not online yet, connect first and send on open.
   */
  private sendRematch(name: string): void {
    this.chat.system(t('h2h.invited', { name }));
    if (this.online && this.net?.connected) {
      this.net.send({ type: 'rematch', to: name, variant: this.variant, identity: this.identity() });
      return;
    }
    this.pendingRematch = name;
    this.menuOpen = false;
    this.mainMenu.hide();
    this.setMode('online');
  }

  /** Someone wants a rematch — offer to join the room they're waiting in. */
  private onRematchInvite(from: string, code: string): void {
    this.showPrompt(
      t('h2h.invite', { name: from }),
      t('h2h.accept'),
      () => this.joinRoomByCode(code),
      t('h2h.decline'),
      () => {}
    );
  }

  /** Join a specific room as a PLAYER, connecting first if we're offline. */
  private joinRoomByCode(code: string): void {
    if (!code) return;
    if (this.online && this.net?.connected) {
      this.net.send({ type: 'joinRoom', code, identity: this.identity() });
      return;
    }
    this.pendingWatch = code; // the online connect handler joins this room once up
    this.menuOpen = false;
    this.mainMenu.hide();
    this.setMode('online');
  }

  // ---- skill assessment ----

  /** Begin a fixed-length solo calibration session. */
  private startAssessment(): void {
    this.assessing = true;
    this.assessTurns = 0;
    this.assessScores = 0;
    this.assessResult = '';
    this.setMode('solo'); // leave online if needed + build a solo table (break skipped while assessing)
    this.message = t('assess.start', { total: ASSESS_TURNS });
    this.refreshHud();
  }

  /** Record one assessment shot; finish the session after the last turn. */
  private tallyAssessment(scored: boolean): void {
    this.assessTurns += 1;
    if (scored) this.assessScores += 1;
    if (this.assessTurns >= ASSESS_TURNS) {
      this.finishAssessment();
      return;
    }
    this.phase = 'aiming';
    this.faceObjectBall();
    this.applyTurnCamera();
    this.message = t('assess.progress', {
      turn: this.assessTurns + 1,
      total: ASSESS_TURNS,
      scored: this.assessScores,
      level: assessLevel(this.assessScores * 10),
    });
  }

  /** Compute the level + rank and freeze the table on the result banner. */
  private finishAssessment(): void {
    const level = assessLevel(this.assessScores * 10);
    const rank = t(rankKeyForLevel(level));
    this.assessResult = t('assess.result', { level, rank, scored: this.assessScores, total: ASSESS_TURNS });
    this.assessing = false;
    this.phase = 'assessed';
    this.message = '';
    this.cameraRig.setMode('orbit');
  }

  /** Localized result line built from the evaluation fields (engine stays pure). */
  private shotMessage(ev: ShotEvaluation, rule: CaromRuleConfig): string {
    if (ev.foul) {
      if (ev.reason.includes('left the table')) return t('shot.foulOff');
      if (ev.reason.includes('on the break')) return t('shot.foulBreak', { ball: t('ball.' + (rule.breakRequiresFirstHit ?? 'red')) });
      return t('shot.foulNothing');
    }
    const cushions = rule.cushionWindow === 'between' ? ev.cushionsBetween : ev.cushionsBeforeSecond;
    if (ev.scored) return rule.minCushions > 0 ? t('shot.caromCushions', { n: cushions }) : t('shot.carom');
    if (ev.firstObjectBall === null) return t('shot.missNothing');
    if (!ev.bothContacted) return t('shot.missSecond');
    return t('shot.missCushions', { n: cushions, m: rule.minCushions });
  }

  private scheduleAIIfNeeded(): void {
    if (this.online || this.phase !== 'aiming' || !this.activeIsAI) {
      this.aiThinking = false;
      return;
    }
    this.aiThinking = true;
    this.refreshHud();
    const token = ++this.aiToken;
    // defer so the "thinking" state paints and the search doesn't block the first frame
    setTimeout(() => {
      if (token !== this.aiToken || this.phase !== 'aiming') return;
      // Play to the rule the AI is actually up against: in the 마지막 3쿠션 stage a
      // plain carom scores nothing and passes the table, so it must not be treated
      // as a make (shotGoalRule raises minCushions for exactly that case).
      const goalRule = shotGoalRule(this.match.config, this.match.config.rule, this.match.players[this.match.activePlayerIndex]);
      const cue = findBestShot(this.base, this.activeCueId, goalRule, this.match.isBreakShot, this.aiLevel());
      this.aiThinking = false;
      this.fireCue(cue);
    }, 450);
  }

  // ---- input ----

  private bindInput(): void {
    if (this.touch) {
      this.bindTouchInput();
      preventEdgeSwipeNavigation(); // an edge-drag to aim shouldn't trigger "go back"
      return; // phones/tablets use finger gestures, not pointer-lock + keyboard
    }
    // Aim mode: LEFT-click enters it; then moving the mouse aims the cue
    // (left/right) and tilts it (up/down). Holding the LEFT button and dragging
    // sets the spin (the red dot). RIGHT-click or any non-modifier key exits aim
    // mode; a RIGHT-drag orbits the camera around the cue ball.

    // Right-click never opens the browser context menu over the game view.
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    this.canvas.addEventListener('pointerdown', (e) => {
      if (this.menuOpen) return; // start menu is a modal — no aiming behind it
      if (this.draggingBallId) return; // carrying a ball — clicks don't drive aim/camera
      if (e.button === 0 && this.canDragBalls()) {
        // practice mode: clicking directly on a ball picks it up, same as
        // holding F, but without needing the key — drag with the button held,
        // release to drop it.
        const id = this.ballAtPoint();
        if (id) {
          this.draggingBallId = id;
          this.canvas.style.cursor = 'grabbing';
          this.canvas.setPointerCapture(e.pointerId); // keep tracking past the canvas edge
          return;
        }
      }
      if (this.pointing) {
        // holding F: click dabs a chalk mark on the cloth instead of the usual
        // aim/orbit click behavior (cleared automatically on the next shot)
        if (e.button === 0) this.dropChalkMark();
        return;
      }
      if (this.phase === 'breaking') {
        this.armBreak(); // any click enables swing mode; no need to hold the button
        return;
      }
      if (e.button === 2) {
        // right-click leaves aim mode, freezing the camera at its current angle
        // and handing control to free-orbit (right-drag rotates, middle pans) —
        // unless watching from the top-down view, which stays fixed rather than
        // dropping into a free-look camera.
        if (this.aimActive) {
          this.setAimActive(false);
          if (this.cameraMode !== 'top') this.enterFreeOrbit();
        }
      } else if (e.button === 0) {
        // left-click enters (or returns to) aim mode on your turn
        if (!this.aimActive && this.myTurn) {
          // come back to the shot framing from free-orbit — unless the player is
          // deliberately aiming from the top-down view, which stays put so a shot
          // can be taken from up there
          if (this.cameraMode !== 'top') this.setCamera('aim');
          this.setAimActive(true);
          this.noteTutorialAction('aim:enter');
          this.enteringAim = true; // ...this same press shouldn't drag spin
        } else if (this.aimActive && document.pointerLockElement !== this.canvas) {
          // already aiming but the lock didn't take (e.g. the browser's post-Esc
          // cooldown blocked the initial request, so the cursor was drifting
          // off-window) — re-acquire on this fresh user gesture.
          this.requestAimLock();
        }
      }
    });
    this.canvas.addEventListener('pointerup', (e) => {
      if (e.button === 0 && this.draggingBallId) {
        this.stopBallDrag();
        return;
      }
      if (this.phase === 'breaking') return; // swing mode stays on once armed
      if (e.button === 0) this.enteringAim = false;
    });
    // if the browser drops pointer-lock (e.g. Esc), leave aim/break mode to match
    document.addEventListener('pointerlockchange', () => {
      const locked = document.pointerLockElement === this.canvas;
      if (this.aimActive && !locked) this.setAimActive(false);
      else if (this.aimActive && locked) this.canvas.style.cursor = 'none'; // re-lock acquired: hide the cursor again
      // break: Esc releases the lock — disable swing mode (once it was truly held)
      if (this.phase === 'breaking' && this.breakArmed) {
        if (locked) this.breakLocked = true;
        else if (this.breakLocked) this.disarmBreak();
      }
    });
    // pointer-lock can be refused (notably the browser's post-Esc cooldown). When
    // it is, the cursor isn't pinned and would drift off-window, so reveal it and
    // prompt a click — the next click re-requests the lock past the cooldown.
    document.addEventListener('pointerlockerror', () => {
      if (!this.aimActive) return;
      this.canvas.style.cursor = '';
      this.message = t('msg.aimRelock');
      this.refreshHud();
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (this.phase === 'breaking') {
        // The cue starts at its resting heading and rotates by horizontal mouse
        // MOVEMENT (deltas, so it works under pointer lock and never jumps to the
        // cursor). Moving the mouse left/right swings the shaft across the balls.
        if (!this.breakArmed) return;
        this.leverAngle -= e.movementX * 0.0025;
        this.leverHasAngle = true;
        return;
      }
      // While pointer-locked (aim mode) the cursor is fixed, so read the relative
      // movement and keep a virtual pointer position; otherwise track the cursor.
      const locked = document.pointerLockElement === this.canvas;
      let dx: number;
      let dy: number;
      if (locked) {
        dx = e.movementX;
        dy = e.movementY;
        this.pointerX += dx;
        this.pointerY += dy;
      } else {
        if (!this.pointerInit) {
          this.pointerX = e.clientX;
          this.pointerY = e.clientY;
          this.pointerInit = true;
        }
        dx = e.clientX - this.pointerX;
        dy = e.clientY - this.pointerY;
        this.pointerX = e.clientX;
        this.pointerY = e.clientY;
      }
      if (this.phase === 'review') this.exitReview(); // moving over the table resumes play
      if (this.draggingBallId) {
        this.updateBallDrag();
        return; // carrying a ball — don't also let the move steer the aim/camera
      }
      const canAdjust = this.phase === 'aiming' && this.myTurn && this.cameraMode !== 'orbit';
      const FINE = 0.08; // fine-aim sensitivity (fraction of normal)
      const aimSens = this.tune.aimSensitivity;
      const leftHeld = (e.buttons & 1) !== 0;
      if (this.cameraRig.getMode() === 'orbit') {
        // viewing / free-orbit mode — OrbitControls drives the camera itself, but
        // a deliberate right-drag here is still "looked around" for the tutorial
        // (a bare right-click to leave aim mode, with no drag, should not be).
        if ((e.buttons & 2) !== 0 && Math.abs(dx) + Math.abs(dy) > 2) this.noteTutorialAction('view:orbit');
        return;
      }
      if (this.stroking) {
        if (leftHeld && canAdjust) {
          // hold LEFT while stroking to drag the spin dot; keep the loaded power
          // steady by sliding the stroke anchor with the cursor (freezes the draw)
          this.strokeAnchorY += dy;
          this.adjustSpin(dx, dy);
        } else {
          this.updateStroke(); // Ctrl held: drive the cue back/forth from the anchor
        }
      } else if (this.cameraMode !== 'orbit' && (e.buttons & 2) !== 0) {
        // right-button drag: orbit the camera around the (fixed) cue ball
        this.cameraRig.orbitView(dx, dy);
        // Only a deliberate drag teaches anything — a one-pixel twitch while
        // right-clicking to leave aim mode shouldn't tick the lesson off.
        if (Math.abs(dx) + Math.abs(dy) > 2) this.noteTutorialAction('view:orbit');
      } else if (this.cameraMode !== 'orbit' && leftHeld && (this.spectator || this.viewing)) {
        // watching — the opponent's turn, or spectating: there's no aiming to
        // protect here, so left-drag orbits too, same as right-drag.
        this.cameraRig.orbitView(dx, dy);
      } else if (this.aimActive && canAdjust && leftHeld && !this.enteringAim) {
        // hold the left button and drag to set the spin (red dot)
        this.adjustSpin(dx, dy);
      } else if (this.aimActive && canAdjust && !leftHeld) {
        // aim mode, no button: move to aim (left/right) + tilt (up/down). Shift
        // forces a fine adjustment; the aim line also self-slows automatically
        // as it swings across a ball, since that's where a small angle change
        // matters most for the contact point. Aiming returns the view to the
        // shot angle.
        const fine = Math.min(e.shiftKey ? FINE : 1, this.autoFineFactor());
        if (e.shiftKey && Math.abs(dx) + Math.abs(dy) > 2) this.noteTutorialAction('aim:fine');
        this.cameraRig.resetView();
        this.azimuth -= dx * aimSens * fine;
        this.adjustTilt(dy * fine);
      }
    });

    // Mouse wheel zooms the view. In orbit mode OrbitControls handles the wheel.
    this.canvas.addEventListener(
      'wheel',
      (e) => {
        if (this.cameraRig.getMode() === 'orbit') return; // OrbitControls handles zoom
        e.preventDefault();
        this.adjustZoom(Math.sign(e.deltaY) * 0.15);
        this.noteTutorialAction('view:zoom');
        this.message = t('msg.zoom', { z: this.cameraRig.displayZoom.toFixed(1) });
        this.refreshHud();
      },
      { passive: false }
    );

    window.addEventListener('keydown', (e) => {
      // Chat comes first, and deliberately BEFORE the menu gate: whispers are
      // available everywhere, including the start menu and the room browser.
      // While the input is open it owns every key (it stops propagation itself);
      // this guard only covers keys that never reach it, e.g. an auto-repeat.
      if (this.chat.isActive()) return;
      if (e.key === 'Enter' && !e.ctrlKey && !e.altKey && !e.metaKey && !this.emoteWheel.isOpen() && !isTypingTarget(e.target)) {
        e.preventDefault();
        this.setAimActive(false); // release pointer-lock so the caret is usable
        this.chat.openInput();
        return;
      }
      if (this.menuOpen) return; // start menu is a modal — ignore gameplay keys
      if (e.key === 'e' || e.key === 'E') {
        if (e.repeat) return; // ignore auto-repeat while E is held down
        if (this.emoteWheel.isOpen()) this.emoteWheel.close(); // press again to close
        else {
          this.emoteWheel.openWheel(); // hold E: point at a slot, release to fire
          this.setAimActive(false); // release pointer-lock so the cursor can aim the wheel
        }
        return;
      }
      if (this.emoteWheel.isOpen()) {
        if (e.key === 'Escape') this.emoteWheel.close();
        return; // wheel is open: swallow other gameplay keys
      }
      if (this.phase === 'breaking') {
        // first key enables swing mode; any key after that disables it (like aim mode)
        if (!this.breakArmed) this.armBreak();
        else this.disarmBreak();
        return;
      }
      if (this.isStrokeModKey(e.key)) {
        this.beginStroke();
        return;
      }
      if (e.key === 'Shift') {
        if (this.phase === 'aiming' && this.myTurn) this.cameraRig.setFineAim(true);
        return;
      }
      if (e.key === 'c' || e.key === 'C') {
        // chalk the tip — on your shot (cue up), or while waiting for your turn
        if (this.canChalk()) this.chalkTip();
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        // practice mode (offline solo/vs-AI, or an online room's waiting lobby):
        // hold F over a ball to pick it up and drag it; release to drop it.
        // Otherwise (including practice mode away from any ball) online: hold F
        // to point at the table — others see an arrow.
        if (!e.repeat) {
          if (this.canDragBalls() && this.ballAtPoint()) this.startBallDrag();
          else if (this.online) this.startPointing();
        }
        return;
      }
      // any other (non-modifier) key leaves aim mode — except Tab, which only
      // swaps the camera view and shouldn't interrupt an aim already in progress
      if (e.key !== 'Alt' && e.key !== 'Meta' && e.key !== 'Tab') {
        // Note it BEFORE the call: setAimActive is a no-op when already off, and
        // the lesson is specifically "a key got you out of aim mode".
        if (this.aimActive) this.noteTutorialAction('aim:exitkey');
        this.setAimActive(false);
      }
      switch (e.key) {
        case 'ArrowLeft':
          this.azimuth += 0.012;
          break;
        case 'ArrowRight':
          this.azimuth -= 0.012;
          break;
        case 'ArrowUp':
          this.power01 = Math.min(1, this.power01 + 0.03);
          this.hud.setPower(this.power01);
          break;
        case 'ArrowDown':
          this.power01 = Math.max(0, this.power01 - 0.03);
          this.hud.setPower(this.power01);
          break;
        case '1':
          this.setCamera('aim');
          break;
        case '2':
          this.setCamera('top');
          break;
        case '3':
          this.setCamera('orbit');
          break;
        case '4':
          this.setCamera('action');
          break;
        case 'Tab':
          e.preventDefault(); // don't let focus leave the canvas
          this.setCamera(this.cameraMode === 'top' ? 'aim' : 'top');
          break;
        case '-':
        case '_':
          this.zoomView(0.2); // wider / further out
          break;
        case '=':
        case '+':
          this.zoomView(-0.2); // closer in
          break;
        case 'g':
        case 'G':
          this.toggleGuide();
          break;
        case 'r':
        case 'R':
          // Solo practice "replay last shot" button — watch the last shot's roll
          // again without touching the score/position. replayLast() itself already
          // refuses outside solo-aiming (see its guard), so this is a no-op
          // elsewhere rather than a trap.
          this.replayLast();
          break;
        case 'z':
        case 'Z':
          // 'r' used to be this — reload the practised shot's layout, or (with no
          // practice context) plain solo rewind-and-retry of the last shot. Moved
          // off 'r' so that key could become "replay" (see above); this is now the
          // only way to reach the plain-solo rewind, which has no HUD button.
          this.rePractice();
          break;
      }
    });

    window.addEventListener('keyup', (e) => {
      if (this.isStrokeModKey(e.key)) this.cancelStroke();
      if (e.key === 'Shift') this.cameraRig.setFineAim(false);
      if (e.key === 'e' || e.key === 'E') this.emoteWheel.onKeyRelease(); // release = fire the pointed slot
      if (e.key === 'f' || e.key === 'F') {
        this.stopPointing();
        this.stopBallDrag();
      }
    });
  }

  /**
   * Touch control scheme (phones/tablets), replacing the pointer-lock desktop path:
   *   • one finger on the table → aim (horizontal drag rotates the cue)
   *   • two fingers             → orbit the framed view (drag) + pinch to zoom
   *   • free-orbit camera       → three's OrbitControls owns touch natively
   *   • break shot              → tap to arm, then swing by dragging sideways
   * The shot itself is taken from the HUD "pull back & release" pad (see the
   * onStrokeDraw/onStrokeRelease callbacks), so aiming and striking never collide.
   */
  private bindTouchInput(): void {
    const canvas = this.canvas;
    canvas.style.touchAction = 'none'; // we own every gesture — no page scroll/zoom
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    const pts = new Map<number, { x: number; y: number }>();
    let lastX = 0; // last 1-finger x / 2-finger midpoint x
    let lastY = 0;
    let pinch = 0; // last 2-finger separation (px)
    const LEVER = 0.006; // break-swing rotation (rad per px)
    const mid = () => {
      const a = [...pts.values()];
      return {
        x: (a[0].x + a[1].x) / 2,
        y: (a[0].y + a[1].y) / 2,
        d: Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y),
      };
    };

    canvas.addEventListener('pointerdown', (e) => {
      if (this.menuOpen) return;
      canvas.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.phase === 'review') this.exitReview();
      if (this.phase === 'breaking') {
        this.armBreak();
        this.leverPrevAngle = this.leverAngle;
      }
      if (pts.size === 1) {
        lastX = e.clientX;
        lastY = e.clientY;
        // practice mode: a touch landing on a ball picks it up instead of aiming
        if (this.canDragBalls()) {
          const id = this.ballAtPoint(e.clientX, e.clientY);
          if (id) {
            this.draggingBallId = id;
            this.touchDragPointerId = e.pointerId;
            this.needsRender = true;
            return;
          }
        }
        if (this.phase === 'aiming' && this.myTurn && this.cameraMode === 'top') {
          this.aimAtTouchPoint(e.clientX, e.clientY);
          this.needsRender = true;
        }
      } else if (pts.size === 2) {
        const m = mid();
        lastX = m.x;
        lastY = m.y;
        pinch = m.d;
      }
    });

    canvas.addEventListener('pointermove', (e) => {
      const p = pts.get(e.pointerId);
      if (!p) return;
      p.x = e.clientX;
      p.y = e.clientY;
      if (this.touchDragPointerId === e.pointerId && this.draggingBallId) {
        this.updateBallDrag(e.clientX, e.clientY);
        return; // carrying a ball — this finger doesn't also aim/orbit
      }
      if (this.cameraRig.getMode() === 'orbit') return; // OrbitControls drives touch

      if (pts.size >= 2) {
        // two fingers: pinch-zoom + orbit-drag the framed view around the cue ball
        const m = mid();
        if (pinch > 0) {
          this.adjustZoom((pinch - m.d) * 0.01);
          if (Math.abs(pinch - m.d) > 2) this.noteTutorialAction('touch:zoom');
        }
        const odx = m.x - lastX;
        const ody = m.y - lastY;
        this.cameraRig.orbitView(odx, ody);
        if (Math.abs(odx) + Math.abs(ody) > 2) this.noteTutorialAction('touch:orbit');
        lastX = m.x;
        lastY = m.y;
        pinch = m.d;
        this.needsRender = true;
        return;
      }

      const dx = e.clientX - lastX;
      lastX = e.clientX;
      lastY = e.clientY;
      if (this.phase === 'breaking') {
        if (this.breakArmed) {
          this.leverAngle -= dx * LEVER;
          this.leverHasAngle = true;
          this.needsRender = true;
        }
        return;
      }
      // Dragging the table itself only aims in the top-down view (tap/drag at the
      // touch point, relative to the cue ball) — everywhere else, aiming is the
      // dedicated aim pad's job (see HUD.bindAimPad) so a stray finger on the
      // table while orienting the view doesn't also swing the shot.
      if (this.phase === 'aiming' && this.myTurn && this.cameraMode === 'top') {
        this.cameraRig.resetView(); // aiming snaps the view back to the shot framing
        this.aimAtTouchPoint(e.clientX, e.clientY);
        if (Math.abs(dx) > 2) this.noteTutorialAction('touch:aim');
        this.needsRender = true;
      }
    });

    const release = (e: PointerEvent) => {
      if (!pts.delete(e.pointerId)) return;
      if (this.touchDragPointerId === e.pointerId) {
        this.touchDragPointerId = null;
        this.stopBallDrag();
      }
      if (pts.size === 1) {
        const a = [...pts.values()][0];
        lastX = a.x;
        lastY = a.y;
      } else if (pts.size === 2) {
        const m = mid();
        lastX = m.x;
        lastY = m.y;
        pinch = m.d;
      }
    };
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);
  }

  /** Enter/leave aim mode (left-click on, right-click or any key off). */
  private setAimActive(active: boolean): void {
    if (this.aimActive === active) return;
    this.aimActive = active;
    if (!active) this.enteringAim = false;
    this.canvas.style.cursor = active ? 'none' : '';
    // pointer-lock so the cursor stays put and aiming reads relative motion (no
    // drift to the screen edge, cursor doesn't obscure the shot)
    if (active) {
      this.requestAimLock();
    } else if (document.pointerLockElement === this.canvas) {
      document.exitPointerLock();
    }
    this.message = t(active ? 'msg.aimMode' : 'msg.aimExit');
    this.refreshHud();
  }

  /**
   * Request pointer-lock for aim mode. Fire-and-forget: if it's refused (e.g. the
   * browser's post-Esc cooldown) a `pointerlockerror` fires and the next click
   * retries — see bindInput. Without the lock the cursor drifts off-window.
   */
  private requestAimLock(): void {
    if (this.touch) return; // no pointer-lock on touch — gestures aim directly
    if (!this.canvas.requestPointerLock) return;
    void Promise.resolve(this.canvas.requestPointerLock()).catch(() => {});
  }

  /** Hold-left-drag in aim mode nudges the tip contact (English / follow / draw). */
  private adjustSpin(dx: number, dy: number): void {
    let a = this.spin.a + dx * 0.006;
    let b = this.spin.b - dy * 0.006;
    const m = Math.hypot(a, b);
    if (m > 1) {
      a /= m;
      b /= m;
    }
    // The bed blocks the tip: drawing so low that the contact drops below the
    // cloth is physically impossible, so clamp how far down the tip can go.
    const R = this.ballView.radius;
    const minB = (TIP_CLOTH_GAP - R) / (R * 0.9); // contact z = R + b*0.9R must stay above the cloth
    if (b < minB) b = minB;
    this.spin = { a, b };
    this.hud.setSpin(a, b);
    this.noteTutorialAction('spin:drag');
  }

  /** Set the cue elevation (clamped); the readout lives in the controls pane.
   *  Allowed past vertical (~100°) so a full massé can stand the cue up over 90°;
   *  the strike model itself caps the physical effect at 90°. */
  private setElevation(rad: number): void {
    this.elevation = Math.max(0, Math.min(MAX_ELEVATION, rad));
    this.hud.setTilt((this.elevation * 180) / Math.PI);
  }

  /** Vertical mouse movement tilts the cue up (dy<0) or back down toward level. */
  private adjustTilt(dy: number): void {
    if (dy === 0) return; // purely-horizontal aiming shouldn't touch the tilt
    this.setElevation(this.elevation - dy * this.tune.tiltSensitivity);
  }

  /** Ctrl (or, on macOS, ⌘) pressed/released: the stroke modifier key. */
  private isStrokeModKey(key: string): boolean {
    return key === 'Control' || (this.macMod && key === 'Meta');
  }

  /** Ctrl pressed: enter the stroke and anchor the release line at the cursor. */
  private beginStroke(): void {
    if (this.phase !== 'aiming' || !this.myTurn || this.aiThinking || this.stroking) return;
    this.sound.resume();
    // move to the shot-taking angle (clears any view orbit) — unless already
    // watching from the top-down view, which a shot can be taken from as-is
    if (this.cameraMode !== 'top') this.setCamera('aim');
    this.stroking = true;
    this.strokeDraw = 0;
    this.maxDraw = 0;
    this.peakDrawPx = 0;
    this.tPeak = performance.now();
    this.strokeAnchorY = this.pointerY; // the line: cross back past it to release
    this.message = t('msg.strokeLoad');
    this.refreshHud();
  }

  /**
   * While stroking, the cue follows the mouse back and forth relative to the anchor
   * line set when Ctrl was pressed. Pulling back (down) winds up + draws the cue;
   * swinging FORWARD across the line strikes. Power is the AVERAGE speed of the
   * forward swing — the wind-up distance covered divided by the time to cover it
   * (px/ms) — so a brisk flick hits hard and a slow push taps, across a smooth
   * range (peak per-frame speed saturated too easily).
   */
  private updateStroke(): void {
    const now = performance.now();
    const drawPx = this.pointerY - this.strokeAnchorY; // >0 = pulled back (toward you)
    this.strokeDraw = Math.max(0, Math.min(1, drawPx / this.tune.strokeDrawRange));
    this.maxDraw = Math.max(this.maxDraw, this.strokeDraw); // arms the shot
    // track the deepest wind-up point (the turnaround) + when it happened
    if (drawPx >= this.peakDrawPx) {
      this.peakDrawPx = drawPx;
      this.tPeak = now;
    }
    // average forward speed since the turnaround: distance swung forward / elapsed
    const fwdDist = this.peakDrawPx - drawPx; // px travelled forward from the deepest point
    const avgSpeed = fwdDist / Math.max(1, now - this.tPeak); // px per ms
    // S-curve: both the weakest and strongest shots need an extreme swing; ordinary
    // swings land across the middle. u in [-1,1] about the mid speed, raised to the
    // sharpness power with sign preserved.
    const u = 2 * Math.min(1, avgSpeed / this.tune.strokeSpeedFull) - 1;
    this.power01 = 0.5 + 0.5 * Math.sign(u) * Math.pow(Math.abs(u), this.tune.strokeCurve);
    this.hud.setPower(this.power01);
    // swung forward across the start line after a real wind-up → strike
    if (this.pointerY <= this.strokeAnchorY && this.maxDraw >= STROKE_MIN_DRAW) {
      this.stroking = false;
      this.strokeDraw = 0;
      this.maxDraw = 0;
      this.shootHuman(); // fires at the average-swing-speed power
    }
  }

  /**
   * Ctrl released. This only ABORTS the stroke — it never fires, no matter how far
   * the cue was drawn. The shot goes off solely by pushing the mouse forward across
   * the start line (see updateStroke); letting go of Ctrl backs out cleanly.
   */
  private cancelStroke(): void {
    if (!this.stroking) return;
    this.stroking = false;
    this.strokeDraw = 0;
    this.maxDraw = 0;
    this.message = t('msg.strokeCancel');
    this.refreshHud();
  }

  private resize(): void {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.renderer.resize(w, h);
    this.cameraRig.resize(w / Math.max(1, h));
    this.aimVisuals.setResolution(w, h);
  }

  // ---- main loop ----

  /**
   * Swap between the live canvas and the static `menu-bg.jpg` stand-in.
   * While parked at the main menu/lobby (menuOpen), there is nothing on the
   * table worth animating, so frame() skips the scene update + render
   * entirely (see the early-return below) and this static image — already
   * showing the same idle table the canvas would have drawn — sits in its
   * place instead. Saves the on-demand renderer's ~20fps idle heartbeat
   * (draw calls + shadow-map work) for as long as the menu is up.
   */
  private applyMenuBackdrop(open: boolean): void {
    if (!this.menuBgStatic || this.menuBgShown === open) return;
    this.menuBgShown = open;
    this.menuBgStatic.style.display = open ? 'block' : 'none';
    this.canvas.style.visibility = open ? 'hidden' : 'visible';
  }

  private frame(nowMs: number): void {
    if (this.menuOpen) {
      this.applyMenuBackdrop(true);
      this.lastNow = nowMs;
      return; // nothing animates behind the menu/lobby — see applyMenuBackdrop
    }
    this.applyMenuBackdrop(false);
    this.stats.begin();
    // raw interval for telemetry — `dt` below is clamped to 0.05 for physics
    // stability, which would floor exactly the long frames p95 exists to catch
    const rawFrameMs = nowMs - this.lastNow;
    const dt = Math.min(0.05, (nowMs - this.lastNow) / 1000);
    this.lastNow = nowMs;
    if (this.cueStick) this.cueStick.updateChalk(dt);
    this.cameraRig.setChalking(this.cueStick ? this.cueStick.isChalking() : false);
    this.tutorialHint.setVisible(false); // only the aiming/myTurn branch below turns it back on
    this.updateTurnTimer(nowMs);
    this.drainRemoteQueue();
    this.flushHeldCoinUpdates();

    if (this.phase === 'simulating' && this.currentShot) {
      this.hud.setAimOverlap(null); // no aim overlay while the shot plays out
      const elapsed = ((nowMs - this.shotStartMs) / 1000) * this.tune.playbackSpeed;
      // fire sounds as playback passes their event times
      while (this.soundIdx < this.soundCues.length && elapsed >= this.soundCues[this.soundIdx].t) {
        const c = this.soundCues[this.soundIdx++];
        if (c.kind === 'ball') this.sound.ballClick(c.intensity);
        else this.sound.cushion(c.intensity * 0.8);
      }
      // 마지막 3쿠션: pop the running rail count at the rail it happened on, with
      // a climbing casino ding layered over the normal cushion thud above.
      while (this.popIdx < this.popCues.length && elapsed >= this.popCues[this.popIdx].t) {
        const p = this.popCues[this.popIdx++];
        this.cushionPops.show(p.x, p.y, String(p.n), p.win);
        this.sound.cushionCount(p.n, this.match.config.rule.finalShotMinCushions ?? p.n);
        if (p.win) {
          this.sound.jackpot();
          this.jackpotFiredThisShot = true;
        }
      }
      if (elapsed >= this.currentShot.duration) {
        if (this.replaying) this.endReplay();
        else this.finalizeShot();
      } else {
        const tr = sampleAt(this.currentShot, elapsed);
        this.ballView.apply(tr);
        const cp = tr[this.activeCueId].pos;
        this.cameraRig.setActionTarget(this.scratch.set(cp.x, cp.y, cp.z));
      }
    } else if (this.phase === 'aiming') {
      const cuePos = this.cueBallPos();
      if (this.myTurn) {
        // The suggested-shot arrow only makes sense while the player is still
        // working the mission — hide it the instant it's cleared or retrying
        // isn't the point (a passed mission just gets the "well done" pause).
        const showHint = !!this.tutorial?.mission?.hint && this.tutorial.state !== 'passed';
        this.tutorialHint.setVisible(showHint);
        if (showHint) {
          this.tutorialHint.tick(dt);
          this.needsRender = true; // the dash animation is scene content — see render-performance notes
        }
        // The rail behind the ball physically blocks the shaft: don't let the cue
        // tilt below the angle needed to clear it (you can't aim through the table).
        // Bottom spin lowers the tip, so the floor rises with the vertical offset.
        const floorE = this.cueStick.requiredElevation(cuePos, this.azimuth, this.spin.b);
        if (this.elevation < floorE) this.elevation = floorE;
        this.hud.setTilt((this.elevation * 180) / Math.PI);
        this.cameraRig.setAim(cuePos, this.azimuth, this.elevation);
        const showStick = !this.aiThinking;
        this.aimVisuals.setVisible(this.guideOn && showStick);
        this.aimVisuals.update(cuePos, this.azimuth, this.objectBalls(), this.ballView.radius, this.base.halfW, this.base.halfL);
        this.hud.setAimOverlap(this.aimVisuals.overlap);
        this.cueStick.setVisible(showStick);
        if (showStick) {
          // chalking lifts the cue into first-person view (no table clipping);
          // otherwise pose it against the ball with any pull-back for power
          if (this.cueStick.isChalking()) this.cueStick.poseChalkInView(this.cameraRig.renderCamera);
          else this.cueStick.pose(cuePos, this.azimuth, this.elevation, this.spin, this.stroking ? this.strokeDraw : 0);
        }
        this.broadcastAim(nowMs);
      } else if (this.spectator) {
        // spectating: keep the aim camera behind the active player's cue ball,
        // looking down their shot line. Use their live aim if we have it (human
        // matches telegraph it); bots don't, so face the nearest object ball.
        const r = this.remoteAim;
        this.cameraRig.setAim(cuePos, r ? r.azimuth : this.azimuth, r ? r.elevation : this.elevation);
        this.aimVisuals.setVisible(false);
        this.hud.setAimOverlap(null);
        if (r) {
          this.cueStick.setVisible(true);
          this.cueStick.pose(cuePos, r.azimuth, r.elevation, { a: r.a, b: r.b }, r.pullback);
        } else {
          this.cueStick.setVisible(false);
        }
      } else if (this.online && this.opponentPresent && this.remoteAim) {
        // watch the opponent's cue move in real time, framed behind their ball
        const r = this.remoteAim;
        this.cameraRig.setAim(cuePos, r.azimuth, r.elevation); // stay on their ball
        this.aimVisuals.setVisible(false);
        this.hud.setAimOverlap(null);
        this.hud.setSpin(r.a, r.b); // mirror their English on the spin pad
        // chalking lifts the cue to first-person on the shooter's side; here we keep
        // it on the ball so the rubbing chalk cube reads from the watcher's view
        this.cueStick.setVisible(true);
        this.cueStick.pose(cuePos, r.azimuth, r.elevation, { a: r.a, b: r.b }, r.pullback);
      } else {
        this.cameraRig.setAim(cuePos, this.azimuth, this.elevation);
        this.aimVisuals.setVisible(false);
        this.hud.setAimOverlap(null);
        this.cueStick.setVisible(false);
      }
    } else if (this.phase === 'breaking') {
      this.stepCornerBreak(dt);
    } else if (this.phase === 'watchbreak') {
      // render the breaker's streamed swing: move the balls + pose the cue
      this.aimVisuals.setVisible(false);
      if (this.remoteBreak) {
        const ids = ballsFor(this.variant);
        const frame = {} as Record<BallId, { pos: { x: number; y: number; z: number }; orientation: { x: number; y: number; z: number; w: number } }>;
        for (const id of ids) {
          const rb = this.remoteBreak.balls[id];
          if (rb) frame[id] = { pos: { x: rb.x, y: rb.y, z: rb.z }, orientation: { x: 0, y: 0, z: 0, w: 1 } };
        }
        this.ballView.apply(frame);
        this.cueStick.setVisible(true);
        this.cueStick.poseLever(this.breakPivot, this.scratch.set(Math.cos(this.remoteBreak.lever), Math.sin(this.remoteBreak.lever), 0));
      } else {
        this.cueStick.setVisible(false);
      }
    } else if (this.phase === 'strike' && this.oppStrike) {
      // opponent's scripted stroke: draw the cue back, then thrust it into the ball
      const s = this.oppStrike;
      const tBack = s.t0 + s.backDur;
      if (nowMs >= tBack && nowMs - tBack >= s.fwdDur) {
        // thrust complete → impact: hand off to playback (replay = non-scoring)
        const shot = s.shot;
        const replay = !!s.replay;
        this.oppStrike = null;
        this.startPlayback(shot, replay);
      } else {
        const pull =
          nowMs < tBack
            ? s.maxPull * ((nowMs - s.t0) / s.backDur) // draw back 0 → max
            : s.maxPull * (1 - Math.min(1, (nowMs - tBack) / Math.max(1, s.fwdDur))); // thrust max → 0
        this.cameraRig.setAim(s.cuePos, s.az, s.el); // frame behind their cue ball
        this.aimVisuals.setVisible(false);
        this.hud.setAimOverlap(null);
        this.cueStick.setVisible(true);
        this.cueStick.pose(s.cuePos, s.az, s.el, s.spin, pull);
      }
    } else if (this.phase === 'review' && this.lastShot) {
      this.ballView.apply(sampleAt(this.lastShot, this.reviewFrac * this.lastShot.duration));
      this.cueStick.setVisible(false);
      this.aimVisuals.setVisible(false);
    } else {
      this.cueStick.setVisible(false);
      this.aimVisuals.setVisible(false);
    }

    this.updateFigures();
    this.updatePointing(nowMs); // hold-F: place/broadcast my pointer
    if (this.draggingBallId && !this.canDragBalls()) this.stopBallDrag(); // e.g. turn ended mid-carry
    this.tablePointers.update(nowMs); // animate everyone's ping arrows
    this.cushionPops.update(dt); // animate the 마지막 3쿠션 rail-count pops
    const cameraMoved = this.cameraRig.update(dt);

    // --- on-demand rendering: skip the (expensive) draw when nothing is moving ---
    // "Active" frames have geometry/view in motion and must draw every frame. When
    // idle we draw only a slow heartbeat (~20fps) so a static scene stays live but
    // the GPU/CPU drop instead of running flat-out at the display's refresh rate.
    const IDLE_RENDER_MS = 50;
    const animating = this.phase === 'simulating' || this.phase === 'breaking' || this.phase === 'watchbreak' || this.phase === 'strike';
    const geometryMoving =
      animating ||
      (this.phase === 'aiming' && this.myTurn && !this.menuOpen) ||
      this.remoteAim != null ||
      this.pointing || // my own pointer follows the mouse — needs full rate
      this.cushionPops.count > 0; // …and while a cushion-count pop is still on screen
      // (an OPPONENT's ping arrow is NOT in this list: its only motion is a slow
      // 0.02m bob, which the idle heartbeat below already redraws smoothly enough —
      // forcing full-rate rendering for it tanked FPS whenever anyone points.)
    const active = geometryMoving || cameraMoved || this.needsRender;
    // hidden tab: game logic above still ran, but the canvas isn't visible, so
    // skip the GPU draw (needsRender stays set and redraws on the next visible frame)
    const didDraw = !document.hidden && (active || nowMs - this.lastRenderMs >= IDLE_RENDER_MS);
    if (didDraw) {
      // recompute shadows only when something that casts them actually moved.
      // While aiming, the only caster in motion is the cue stick, and its shadow
      // is soft and low-contrast (PCFSoftShadowMap's fixed 17-tap kernel — NOT
      // spot.shadow.radius, which that path ignores) — so refresh the map at ~30Hz
      // instead of the display's refresh rate. Ball motion still updates every
      // frame, where a lagging contact shadow would read as the ball sliding.
      const SHADOW_AIM_MS = 33;
      const aimingOnly = !animating && this.phase === 'aiming';
      if (this.needsRender || (geometryMoving && (!aimingOnly || nowMs - this.lastShadowMs >= SHADOW_AIM_MS))) {
        this.renderer.three.shadowMap.needsUpdate = true;
        this.lastShadowMs = nowMs;
      }
      this.renderer.render(this.scene, this.cameraRig.renderCamera);
      this.needsRender = false;
      this.lastRenderMs = nowMs;
    }
    this.perf?.sample(this.renderer.three, this.scene, didDraw); // ?perf=1 only
    this.perfSampler.sample(this.renderer.three, rawFrameMs, didDraw);
    this.flushPerf(nowMs, false);
    this.stats.end();
  }

  /**
   * Ship the current telemetry window if it's due. `final` forces a flush when
   * the page is going away, so a player who closes the tab mid-session still
   * reports (that's often exactly the session that was performing badly).
   *
   * Silently does nothing without a socket — telemetry never blocks or retries,
   * and a dropped window is not worth a queue.
   */
  private flushPerf(nowMs: number, final: boolean): void {
    if (!final && nowMs - this.lastPerfFlushMs < PERF_FLUSH_MS) return;
    if (!final && !this.perfSampler.ready) return;
    this.lastPerfFlushMs = nowMs;
    const sample = this.perfSampler.flush(this.renderer.three, this.scene, this.env);
    if (sample && this.net) this.net.send({ type: 'clientPerf', sample });
  }

  private refreshHud(): void {
    this.hud.update(this.match, {
      message: this.message,
      power: this.power01,
      aiThinking: this.aiThinking,
      canShoot: this.phase === 'aiming' && this.myTurn && !this.aiThinking,
      canReplay: this.phase === 'aiming' && this.myTurn && !this.stroking && this.lastShot !== null,
      canScrub: (this.phase === 'aiming' || this.phase === 'review') && this.lastShot !== null,
      mode: this.uiMode,
      guideOn: this.guideOn,
      cameraMode: this.cameraMode,
      variant: this.variant,
      banner: this.phase === 'breaking' && !this.breakArmed ? t('msg.breakArm') : null,
      // in active play (solo/ai always, online only once a match is running) → hide ☰
      inGame: !this.menuOpen && (!this.online || this.gameActive),
      spectating: this.spectator && !this.menuOpen,
      stats: this.seatStats,
      h2h: Object.fromEntries(this.h2h),
      assessing: this.assessing || this.phase === 'assessed',
      tutorial: !!this.tutorial,
      assessResult: this.phase === 'assessed' ? this.assessResult : null,
      // finished game with recorded shots → offer "Review shots" on the banner
      canReview: this.phase === 'finished' && this.lastGameRecord !== null && this.lastGameRecord.shots.length > 0,
      // practising a recorded shot → show the re-practice (reload layout) button.
      // Stays visible through the shot too (rePractice guards against firing mid-shot).
      // Plain solo/vs-AI play (no recorded layout) still gets the button once a shot
      // has been taken, so "replay the previous ball setup" also has a button, not
      // just the 'z' key.
      // Never during a 이번주의 매치 attempt (see rePractice / resetToLastShot).
      showRePractice:
        !this.dailyRun &&
        (this.practiceCtx !== null ||
          ((!this.online ? this.match.config.solo : this.isLobbyPractice()) && this.preShot !== null)),
      daily: this.dailyRun && this.daily
        ? {
            caroms: this.dailyRun.caroms,
            target: this.daily.target,
            strokes: this.dailyRun.strokes,
            ruleType: this.daily.ruleType,
            banks: this.dailyRun.banks,
            minBanks: this.daily.minBanks,
            remainingSec: this.daily.ruleType === 'timedScore' && this.daily.timeLimitSec != null
              ? Math.max(0, Math.ceil(this.daily.timeLimitSec - (Date.now() - this.dailyRun.startTs) / 1000))
              : undefined,
            elapsedSec: this.daily.ruleType === 'timedSpeed'
              ? Math.floor((Date.now() - this.dailyRun.startTs) / 1000)
              : undefined,
          }
        : null,
    });
    this.refreshPresence(); // keep the online-players status in step with the game state
  }
}

/** Distance from interior point (ox,oy) along unit (dx,dy) to the box |x|=hw,|y|=hl. */
function edgeDistance(ox: number, oy: number, dx: number, dy: number, hw: number, hl: number): number {
  let s = Infinity;
  if (dx > 1e-6) s = Math.min(s, (hw - ox) / dx);
  else if (dx < -1e-6) s = Math.min(s, (-hw - ox) / dx);
  if (dy > 1e-6) s = Math.min(s, (hl - oy) / dy);
  else if (dy < -1e-6) s = Math.min(s, (-hl - oy) / dy);
  return Number.isFinite(s) ? Math.max(0, s) : 0;
}
